import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { SendSmsSettings } from '../../components/SendSmsSettings';
import { OutboundCallSettings } from '../../components/agent/OutboundCallSettings';
import { PostCallPayloadTable } from '../../components/agent/PostCallPayloadTable';
import { CallDirectionPicker } from '../../components/agent/CallDirectionPicker';
import { CallRoutingToolPreview } from '../../components/agent/CallRoutingToolPreview';
import { IVR_PROMPT_TEMPLATE } from '../../components/agent/ivrPromptTemplate';
import { SummaryNoteTest } from '../../components/agent/SummaryNoteTest';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import AdvancedVoicePanel from '../../components/voice/AdvancedVoicePanel';
import VoiceLibraryModal from '../../components/voice/VoiceLibraryModal';
import { useVoiceSample } from '../../components/voice/useVoiceSample';
import { SelectedVoiceCard } from '../../components/voice/SelectedVoiceCard';
import { V4VoiceControls, isV4 } from '../../components/voice/V4VoiceControls';
import { useV4Capabilities } from '../../components/voice/useV4Capabilities';
import { VoiceModelSelect } from '../../components/voice/VoiceModelSelect';
import { LanguagePicker } from '../../components/voice/LanguagePicker';
import { Switch } from '../../components/ui/switch';
import IPhoneChatMockup from '../../components/voice/IPhoneChatMockup';
import LiveTimezoneClock from '../../components/agent/LiveTimezoneClock';
import PromptEditor from '../../components/agent/PromptEditor';
import { ConfigDisclosure } from '../../components/agent/ConfigDisclosure';
import { KnowledgePicker } from '../../components/agent/KnowledgePicker';
import '../../components/agent/agent-configuration.css';
import { VersionDiff, computeSnapshotChanges } from '../../components/agent/VersionDiff';
import {
  ArrowLeft,
  Settings,
  Mic,
  Rocket,
  History,
  Phone,
  Brain,
  Wrench,
  Webhook,
  FileText,
  Save,
  Play,
  Pause,
  MessageSquare,
  Volume2,
  Loader2,
  Check,
  X,
  ChevronRight,
  Tag,
  Zap,
  Send,
  AlertCircle,
  Info,
  Edit3,
  ToggleLeft,
  ToggleRight,
  PhoneOutgoing,
  Calendar,
  User,
  Mail,
  Clock,
  Workflow,
  Plus,
  Trash2,
  Copy,
  Globe,
  Download,
  Upload,
  Thermometer,
  Timer,
  Search,
  Users,
  ExternalLink,
  Maximize2,
  Minimize2,
  Camera,
  MessageCircle,
  CheckCircle,
  Bot,
  Target,
  Minus,
  XCircle,
  ChevronDown,
  Link2,
  AlertTriangle,
  RefreshCw,
  Database
} from 'lucide-react';

// Voice options - Premium ultra-realistic voices curated for quality
const PREMIUM_VOICES = [
  // Female voices - Various styles and accents
  { id: 'rachel', name: 'Rachel', gender: 'Female', accent: 'American', age: 'Young', style: 'Calm, warm', description: 'Soothing and professional' },
  { id: 'domi', name: 'Domi', gender: 'Female', accent: 'American', age: 'Young', style: 'Strong, confident', description: 'Bold and expressive' },
  { id: 'bella', name: 'Bella', gender: 'Female', accent: 'American', age: 'Young', style: 'Soft, warm', description: 'Gentle and friendly' },
  { id: 'elli', name: 'Elli', gender: 'Female', accent: 'American', age: 'Young', style: 'Emotional, expressive', description: 'Dynamic range' },
  { id: 'sarah', name: 'Sarah', gender: 'Female', accent: 'American', age: 'Young', style: 'Friendly, natural', description: 'Conversational warmth' },
  { id: 'charlotte', name: 'Charlotte', gender: 'Female', accent: 'British', age: 'Middle-aged', style: 'Elegant, refined', description: 'Sophisticated charm' },
  { id: 'lily', name: 'Lily', gender: 'Female', accent: 'British', age: 'Young', style: 'Gentle, soothing', description: 'Calming presence' },
  { id: 'jessica', name: 'Jessica', gender: 'Female', accent: 'American', age: 'Young', style: 'Playful, bright', description: 'Energetic and fun' },
  { id: 'matilda', name: 'Matilda', gender: 'Female', accent: 'Australian', age: 'Middle-aged', style: 'Professional, warm', description: 'Business friendly' },
  { id: 'gigi', name: 'Gigi', gender: 'Female', accent: 'American', age: 'Young', style: 'Youthful, animated', description: 'Fresh and lively' },
  { id: 'grace', name: 'Grace', gender: 'Female', accent: 'American', age: 'Young', style: 'Sweet, gentle', description: 'Warm and caring' },
  { id: 'alice', name: 'Alice', gender: 'Female', accent: 'British', age: 'Middle-aged', style: 'Confident, clear', description: 'Articulate speaker' },
  // Male voices - Various styles and accents
  { id: 'adam', name: 'Adam', gender: 'Male', accent: 'American', age: 'Middle-aged', style: 'Deep, resonant', description: 'Authoritative presence' },
  { id: 'antoni', name: 'Antoni', gender: 'Male', accent: 'American', age: 'Young', style: 'Well-rounded, smooth', description: 'Versatile and natural' },
  { id: 'josh', name: 'Josh', gender: 'Male', accent: 'American', age: 'Young', style: 'Engaging, dynamic', description: 'Youthful energy' },
  { id: 'arnold', name: 'Arnold', gender: 'Male', accent: 'American', age: 'Middle-aged', style: 'Crisp, articulate', description: 'Clear narrator' },
  { id: 'sam', name: 'Sam', gender: 'Male', accent: 'American', age: 'Young', style: 'Raspy, unique', description: 'Distinctive character' },
  { id: 'eric', name: 'Eric', gender: 'Male', accent: 'American', age: 'Middle-aged', style: 'Friendly, approachable', description: 'Trustworthy tone' },
  { id: 'george', name: 'George', gender: 'Male', accent: 'British', age: 'Middle-aged', style: 'Warm, narrative', description: 'Storyteller voice' },
  { id: 'callum', name: 'Callum', gender: 'Male', accent: 'Scottish', age: 'Young', style: 'Smooth, relaxed', description: 'Easy-going charm' },
  { id: 'charlie', name: 'Charlie', gender: 'Male', accent: 'Australian', age: 'Young', style: 'Natural, casual', description: 'Laid-back friendly' },
  { id: 'daniel', name: 'Daniel', gender: 'Male', accent: 'British', age: 'Middle-aged', style: 'Deep, authoritative', description: 'Professional narrator' },
  { id: 'bill', name: 'Bill', gender: 'Male', accent: 'American', age: 'Old', style: 'Wise, grandfatherly', description: 'Trusted advisor' },
  { id: 'brian', name: 'Brian', gender: 'Male', accent: 'American', age: 'Middle-aged', style: 'Conversational', description: 'Relatable speaker' },
];

// LeadConnector Functions - Prebuilt functions for LeadConnector integration
const GHL_FUNCTIONS = [
  { id: 'end_call', name: 'end_call', description: 'Gracefully end the current call', category: 'call', defaultEnabled: true },
  { id: 'schedule_callback', name: 'schedule_callback', description: 'Schedule a callback at a specific time', category: 'call', defaultEnabled: true },
  { id: 'schedule_textback', name: 'schedule_textback', description: 'Schedule a follow-up text message at a specific time', category: 'call', defaultEnabled: false },
  { id: 'check_availability', name: 'check_availability', description: 'Check calendar availability for appointments', category: 'calendar', defaultEnabled: true },
  { id: 'book_appointment', name: 'book_appointment', description: 'Book an appointment on the calendar', category: 'calendar', defaultEnabled: true },
  { id: 'reschedule_appointment', name: 'reschedule_appointment', description: 'Reschedule an existing appointment', category: 'calendar', defaultEnabled: true },
  { id: 'cancel_appointment', name: 'cancel_appointment', description: 'Cancel an existing appointment', category: 'calendar', defaultEnabled: true },
  { id: 'get_contact_appointments', name: 'get_contact_appointments', description: "Look up the caller's existing appointments so the AI never double-books", category: 'calendar', defaultEnabled: true },
  { id: 'transfer_call', name: 'transfer_call', description: 'Transfer the call to another number or agent', category: 'call', defaultEnabled: false },
  { id: 'route_call', name: 'call_routing', description: 'Follow your Call Routing menu and send callers to the right department, queue or agent', category: 'call', defaultEnabled: false },
  { id: 'add_tag', name: 'add_tag', description: 'Add a tag to the contact in LeadConnector', category: 'contact', defaultEnabled: false },
  { id: 'remove_tag', name: 'remove_tag', description: 'Remove a tag from the contact', category: 'contact', defaultEnabled: false },
  { id: 'update_custom_field', name: 'update_custom_field', description: 'Save answers from the conversation into LeadConnector custom fields', category: 'contact', defaultEnabled: false, chatOnly: true },
  { id: 'update_contact', name: 'update_contact', description: 'Update contact information in LeadConnector (name, email, address)', category: 'contact', defaultEnabled: false },
  { id: 'create_contact', name: 'create_contact', description: 'Create a new contact in LeadConnector for first-time callers', category: 'contact', defaultEnabled: false },
  { id: 'add_note', name: 'add_note', description: 'Add a note to the contact record', category: 'contact', defaultEnabled: true },
  { id: 'trigger_workflow', name: 'trigger_workflow', description: 'Trigger a LeadConnector workflow for the contact', category: 'automation', defaultEnabled: false },
  { id: 'send_sms', name: 'send_sms', description: 'Send an SMS message to the contact', category: 'communication', defaultEnabled: false },
  { id: 'update_opportunity_stage', name: 'update_opportunity_stage', description: 'Add contact to an opportunity pipeline stage', category: 'sales', defaultEnabled: false },
  { id: 'trigger_outbound_call', name: 'trigger_outbound_call', description: 'Trigger an outbound voice call (Chat Assistant Only)', category: 'call', defaultEnabled: false, chatOnly: true },
  { id: 'switch_to_sms', name: 'switch_to_sms', description: 'Continue the conversation by SMS.', category: 'communication', defaultEnabled: false, chatOnly: true, widgetOnly: true },
];

// Card-based tag manager shared by the add_tag / remove_tag config modals.
// Tags are stored as [{ tag_name, description }] — the AI picks ONE tag
// whose "when" condition matches and passes it as a parameter.
const TagCardsEditor = ({ mode, tags, onChange, ghlTags, isDarkMode, textPrimary, textSecondary, inputBg, cardBorder, createRequest = 0, hideInlineCreate = false }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const isAdd = mode === 'add';
  const verb = isAdd ? 'add' : 'remove';
  const accentText = isAdd ? 'text-teal-400' : 'text-rose-400';
  const accentChip = isAdd ? 'bg-teal-400/10 text-teal-400 border-teal-400/30' : 'bg-rose-400/10 text-rose-400 border-rose-400/30';
  const accentBtn = isAdd ? 'bg-teal-500 hover:bg-teal-400' : 'bg-rose-500 hover:bg-rose-400';
  const accentDashed = isAdd ? 'hover:border-teal-400/50 hover:text-teal-400' : 'hover:border-rose-400/50 hover:text-rose-400';
  const accentFocus = isAdd ? 'focus:border-teal-400/50' : 'focus:border-rose-400/50';

  const openCreate = () => { setEditIdx(null); setDraftName(''); setDraftDesc(''); setEditorOpen(true); };
  // Footer "Create Tag" button (50/50 split) signals via an incrementing counter.
  useEffect(() => { if (createRequest > 0) openCreate(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [createRequest]);
  const openEdit = (idx) => { setEditIdx(idx); setDraftName(tags[idx]?.tag_name || ''); setDraftDesc(tags[idx]?.description || ''); setEditorOpen(true); };
  const deleteTag = (idx) => {
    const next = [...tags];
    next.splice(idx, 1);
    onChange(next);
    if (editIdx === idx) setEditorOpen(false);
  };
  const saveDraft = () => {
    const name = draftName.trim();
    if (!name) { toast.error('Tag name is required'); return; }
    if (tags.some((t, i) => i !== editIdx && (t.tag_name || '').trim().toLowerCase() === name.toLowerCase())) {
      toast.error(`Tag "${name}" already exists`);
      return;
    }
    const next = [...tags];
    const entry = { tag_name: name, description: draftDesc.trim() };
    if (editIdx === null) next.push(entry); else next[editIdx] = entry;
    onChange(next);
    setEditorOpen(false);
  };

  return (
    <div className="space-y-4" data-testid={`${mode}-tag-cards-editor`}>
      {isAdd && (
        <div className={`flex items-start gap-2.5 p-3 rounded-xl border ${isDarkMode ? 'bg-sky-400/[0.06] border-sky-400/20' : 'bg-sky-50 border-sky-200'}`} data-testid="native-tags-note">
          <Info size={14} className="text-sky-400 shrink-0 mt-0.5" />
          <p className={`text-xs leading-relaxed ${textSecondary}`}>
            <span className={`font-semibold ${textPrimary}`}>Heads up:</span> <span className="font-medium">answered</span>, <span className="font-medium">not answered</span>, <span className="font-medium">ai voice booked appointment</span> and <span className="font-medium">ai chat booked appointment</span> are built-in tags applied automatically — no need to create them here.
          </p>
        </div>
      )}
      <p className={`text-xs ${textSecondary}`}>
        Create tags with a clear condition. During the conversation the AI picks the <span className={`font-semibold ${accentText}`}>one tag</span> whose condition matches and {verb}s only that tag.
      </p>

      {tags.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {tags.map((tag, idx) => (
            <div key={idx} className={`p-4 rounded-xl border ${cardBorder} ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'} flex flex-col gap-2.5`} data-testid={`tag-card-${idx}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold truncate ${accentChip}`}>
                  <Tag size={11} className="shrink-0" />
                  <span className="truncate">{tag.tag_name}</span>
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(idx)} className={`p-1.5 rounded-lg ${textSecondary} hover:${accentText} hover:bg-white/10 transition-colors`} data-testid={`tag-card-edit-${idx}`}>
                    <Edit3 size={13} />
                  </button>
                  <button onClick={() => deleteTag(idx)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`tag-card-delete-${idx}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <p className={`text-xs leading-relaxed ${tag.description ? textSecondary : `italic ${textSecondary} opacity-60`}`}>
                {tag.description || `No condition set — the AI won't know when to ${verb} this tag`}
              </p>
            </div>
          ))}
        </div>
      )}

      {editorOpen && (
        <div className={`p-4 rounded-xl border-2 ${isAdd ? 'border-teal-400/30' : 'border-rose-400/30'} ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'} space-y-3`} data-testid="tag-draft-editor">
          <p className={`text-xs font-semibold ${textPrimary}`}>{editIdx === null ? 'Create Tag' : 'Edit Tag'}</p>
          <div>
            <label className={`block text-xs font-medium ${textSecondary} mb-1.5`}>Tag Name *</label>
            <input
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="e.g., hot_lead"
              list={`ghl-tag-suggestions-${mode}`}
              autoFocus
              className={`w-full px-3 py-2.5 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none ${accentFocus}`}
              data-testid="tag-draft-name"
            />
            <datalist id={`ghl-tag-suggestions-${mode}`}>
              {(ghlTags || []).map((t) => <option key={t.id || t.name} value={t.name} />)}
            </datalist>
          </div>
          <div>
            <label className={`block text-xs font-medium ${textSecondary} mb-1.5`}>When should the AI {verb} this tag?</label>
            <textarea
              value={draftDesc}
              onChange={(e) => setDraftDesc(e.target.value)}
              placeholder={isAdd ? 'e.g., When the contact shows strong interest in purchasing' : 'e.g., When the contact books an appointment, remove this follow-up tag'}
              rows={2}
              className={`w-full px-3 py-2.5 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none ${accentFocus} resize-none`}
              data-testid="tag-draft-description"
            />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveDraft} className={`px-4 py-2 rounded-lg ${accentBtn} text-black text-xs font-bold transition-colors`} data-testid="tag-draft-save">
              {editIdx === null ? 'Create Tag' : 'Save Changes'}
            </button>
            <button onClick={() => setEditorOpen(false)} className={`px-4 py-2 rounded-lg border ${cardBorder} ${textSecondary} text-xs font-medium hover:bg-white/5 transition-colors`} data-testid="tag-draft-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {!editorOpen && !hideInlineCreate && (
        <button
          onClick={openCreate}
          className={`w-full py-3 rounded-xl border-2 border-dashed ${cardBorder} ${textSecondary} ${accentDashed} transition-colors text-sm font-medium flex items-center justify-center gap-2`}
          data-testid="add-new-tag-button"
        >
          <Plus size={16} />
          Create Tag
        </button>
      )}

      {tags.length === 0 && !editorOpen && (
        <p className={`text-xs ${textSecondary} text-center`}>No tags yet. Click "Create Tag" to get started.</p>
      )}
    </div>
  );
};


// Card-based custom field mapper for the update_custom_field config modal.
// Fields are stored as [{ id, key, name, dataType, options, description }] —
// the AI extracts the customer's answer and writes it into the GHL field.
const CustomFieldsEditor = ({ fields, onChange, ghlCustomFields, loadingFields, onSync, syncing, createRequest = 0, isDarkMode, textPrimary, textSecondary, inputBg, cardBorder }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [draftFieldId, setDraftFieldId] = useState('');
  const [draftDesc, setDraftDesc] = useState('');

  const openCreate = () => { setEditIdx(null); setDraftFieldId(''); setDraftDesc(''); setEditorOpen(true); };
  const openEdit = (idx) => { setEditIdx(idx); setDraftFieldId(fields[idx]?.id || ''); setDraftDesc(fields[idx]?.description || ''); setEditorOpen(true); };
  // Footer "Add Field" button (50/50 split) signals via an incrementing counter.
  useEffect(() => { if (createRequest > 0) openCreate(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [createRequest]);

  const usedIds = fields.map(f => f.id);
  const selectable = (ghlCustomFields || []).filter(f => !usedIds.includes(f.id) || (editIdx !== null && fields[editIdx]?.id === f.id));
  const draftField = (ghlCustomFields || []).find(f => f.id === draftFieldId);

  const DT_LABELS = { TEXT: 'Text', LARGE_TEXT: 'Long text', NUMERICAL: 'Number', MONETORY: 'Money', PHONE: 'Phone', DATE: 'Date', SINGLE_OPTIONS: 'Dropdown', MULTIPLE_OPTIONS: 'Multi-select', RADIO: 'Radio', CHECKBOX: 'Checkbox', TEXTBOX_LIST: 'Text list' };
  const dtLabel = (dt) => DT_LABELS[String(dt || 'TEXT').toUpperCase()] || dt || 'Text';
  const optNames = (f) => (f?.options || []).map(o => (typeof o === 'object' && o !== null ? (o.name || o.label || o.value || '') : String(o))).filter(Boolean);

  const deleteField = (idx) => {
    const next = [...fields];
    next.splice(idx, 1);
    onChange(next);
    if (editIdx === idx) setEditorOpen(false);
  };

  const saveDraft = () => {
    if (!draftField) { toast.error('Choose a custom field'); return; }
    const entry = {
      id: draftField.id,
      key: draftField.key || '',
      name: draftField.name || '',
      dataType: draftField.dataType || 'TEXT',
      options: draftField.options || [],
      description: draftDesc.trim(),
    };
    const next = [...fields];
    if (editIdx === null) next.push(entry); else next[editIdx] = entry;
    onChange(next);
    setEditorOpen(false);
  };

  return (
    <div className="space-y-4" data-testid="custom-fields-editor">
      <div className={`flex items-start gap-2.5 p-3 rounded-xl border ${isDarkMode ? 'bg-sky-400/[0.06] border-sky-400/20' : 'bg-sky-50 border-sky-200'}`} data-testid="custom-fields-note">
        <Info size={14} className="text-sky-400 shrink-0 mt-0.5" />
        <p className={`text-xs leading-relaxed ${textSecondary}`}>
          <span className={`font-semibold ${textPrimary}`}>How it works:</span> the moment a customer shares the info you describe below, the AI saves it straight into that LeadConnector custom field. Dropdown fields are locked to their exact options — the AI can never invent values.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className={`text-xs ${textSecondary}`}>
          {loadingFields
            ? 'Loading custom fields from LeadConnector...'
            : `${(ghlCustomFields || []).length} custom field${(ghlCustomFields || []).length === 1 ? '' : 's'} available from LeadConnector`}
        </p>
        <button
          onClick={onSync}
          disabled={syncing}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${cardBorder} ${textSecondary} text-xs font-medium hover:bg-white/5 transition-colors disabled:opacity-50`}
          data-testid="sync-ghl-custom-fields-btn"
        >
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing...' : 'Sync fields'}
        </button>
      </div>

      {fields.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.map((field, idx) => {
            const opts = optNames(field);
            return (
              <div key={field.id || idx} className={`p-4 rounded-xl border ${cardBorder} ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'} flex flex-col gap-2.5`} data-testid={`field-card-${idx}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold truncate bg-sky-400/10 text-sky-400 border-sky-400/30">
                    <Database size={11} className="shrink-0" />
                    <span className="truncate">{field.name || field.key}</span>
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-100 border-gray-200'} ${textSecondary}`}>
                      {dtLabel(field.dataType)}
                    </span>
                    <button onClick={() => openEdit(idx)} className={`p-1.5 rounded-lg ${textSecondary} hover:text-sky-400 hover:bg-white/10 transition-colors`} data-testid={`field-card-edit-${idx}`}>
                      <Edit3 size={13} />
                    </button>
                    <button onClick={() => deleteField(idx)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`field-card-delete-${idx}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <p className={`text-xs leading-relaxed ${field.description ? textSecondary : `italic ${textSecondary} opacity-60`}`}>
                  {field.description || "No capture rule set — the AI won't know what to save here"}
                </p>
                {opts.length > 0 && (
                  <p className={`text-[11px] ${textSecondary} opacity-70 truncate`}>
                    Options: {opts.slice(0, 5).join(' · ')}{opts.length > 5 ? ` +${opts.length - 5} more` : ''}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editorOpen && (
        <div className={`p-4 rounded-xl border-2 border-sky-400/30 ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'} space-y-3`} data-testid="field-draft-editor">
          <p className={`text-xs font-semibold ${textPrimary}`}>{editIdx === null ? 'Add Field' : 'Edit Field'}</p>
          <div>
            <label className={`block text-xs font-medium ${textSecondary} mb-1.5`}>LeadConnector Custom Field *</label>
            {loadingFields ? (
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg ${inputBg} border ${cardBorder}`}>
                <Loader2 size={14} className="animate-spin text-sky-400" />
                <span className={`text-xs ${textSecondary}`}>Loading fields...</span>
              </div>
            ) : selectable.length === 0 ? (
              <div className={`px-3 py-2.5 rounded-lg ${inputBg} border ${cardBorder}`}>
                <p className={`text-xs ${textSecondary}`}>
                  {(ghlCustomFields || []).length === 0
                    ? 'No custom fields found — click "Sync fields" to pull them from LeadConnector.'
                    : 'All synced fields are already mapped.'}
                </p>
              </div>
            ) : (
              <select
                value={draftFieldId}
                onChange={(e) => setDraftFieldId(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-sky-400/50`}
                data-testid="field-draft-select"
              >
                <option value="">Select a custom field...</option>
                {selectable.map((f) => (
                  <option key={f.id} value={f.id}>{f.name || f.key} ({dtLabel(f.dataType)})</option>
                ))}
              </select>
            )}
            {draftField && optNames(draftField).length > 0 && (
              <p className={`mt-1.5 text-[11px] ${textSecondary}`} data-testid="field-draft-options-hint">
                <span className="font-semibold text-sky-400">Locked options:</span> {optNames(draftField).join(' · ')} — the AI will always pick one of these exact values.
              </p>
            )}
          </div>
          <div>
            <label className={`block text-xs font-medium ${textSecondary} mb-1.5`}>What should the AI capture into this field?</label>
            <textarea
              value={draftDesc}
              onChange={(e) => setDraftDesc(e.target.value)}
              placeholder="e.g., The customer's monthly ad budget — save it whenever they mention how much they spend"
              rows={2}
              className={`w-full px-3 py-2.5 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-sky-400/50 resize-none`}
              data-testid="field-draft-description"
            />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveDraft} className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-black text-xs font-bold transition-colors" data-testid="field-draft-save">
              {editIdx === null ? 'Add Field' : 'Save Changes'}
            </button>
            <button onClick={() => setEditorOpen(false)} className={`px-4 py-2 rounded-lg border ${cardBorder} ${textSecondary} text-xs font-medium hover:bg-white/5 transition-colors`} data-testid="field-draft-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {fields.length === 0 && !editorOpen && (
        <p className={`text-xs ${textSecondary} text-center`} data-testid="custom-fields-empty">No fields mapped yet. Click "Add Field" to get started.</p>
      )}
    </div>
  );
};


// Default pre-tool speech phrases - used when no custom override is set
const PRE_TOOL_SPEECH_DEFAULTS = {
  check_availability: "Let me check what time slots we have available...",
  book_appointment: "Locking you in now...",
  reschedule_appointment: "Let me pull up your appointment and find a new time...",
  cancel_appointment: "Let me take care of that cancellation for you...",
  get_contact_appointments: "One sec — let me pull up your appointment details...",
  transfer_call: "Let me connect you with someone who can help...",
  schedule_callback: "Let me schedule that callback for you...",
  schedule_textback: "Let me schedule that text for you...",
  add_tag: "Just updating your profile...",
  update_contact: "Let me update your information...",
  create_contact: "Let me get you set up in our system...",
  add_note: "Making a note of that...",
  trigger_workflow: "Setting that up for you now...",
  send_sms: "Sending that over to you now...",
  end_call: "",
  remove_tag: "",
  update_opportunity_stage: "Updating your status...",
  trigger_outbound_call: "Initiating that call for you...",
  switch_to_sms: "No problem — texting you now so we can keep this going on your phone...",
};

// ElevenLabs language sets, per TTS model family.
//   • Flash v2.5 / Turbo v2.5 → 32 languages (real-time agents).
//   • Eleven v3 → 74 languages (premium, most natural).
// Codes are the ISO codes ElevenLabs expects for `language` / `language_presets`.
// Source: https://elevenlabs.io/docs/overview/models
const VOICE_LANGUAGES_V3 = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'it', label: 'Italian' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pl', label: 'Polish' },
  { code: 'ru', label: 'Russian' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'cs', label: 'Czech' },
  { code: 'sk', label: 'Slovak' },
  { code: 'hr', label: 'Croatian' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'ro', label: 'Romanian' },
  { code: 'el', label: 'Greek' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'fi', label: 'Finnish' },
  { code: 'da', label: 'Danish' },
  { code: 'no', label: 'Norwegian' },
  { code: 'sv', label: 'Swedish' },
  { code: 'tr', label: 'Turkish' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh', label: 'Chinese (Mandarin)' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'fil', label: 'Filipino' },
  { code: 'vi', label: 'Vietnamese' },
  // ─── v3-only additions (42 more) ───
  { code: 'af', label: 'Afrikaans' },
  { code: 'hy', label: 'Armenian' },
  { code: 'as', label: 'Assamese' },
  { code: 'az', label: 'Azerbaijani' },
  { code: 'be', label: 'Belarusian' },
  { code: 'bn', label: 'Bengali' },
  { code: 'bs', label: 'Bosnian' },
  { code: 'ca', label: 'Catalan' },
  { code: 'ceb', label: 'Cebuano' },
  { code: 'ny', label: 'Chichewa' },
  { code: 'et', label: 'Estonian' },
  { code: 'gl', label: 'Galician' },
  { code: 'ka', label: 'Georgian' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'ha', label: 'Hausa' },
  { code: 'he', label: 'Hebrew' },
  { code: 'is', label: 'Icelandic' },
  { code: 'ga', label: 'Irish' },
  { code: 'jv', label: 'Javanese' },
  { code: 'kn', label: 'Kannada' },
  { code: 'kk', label: 'Kazakh' },
  { code: 'ky', label: 'Kirghiz' },
  { code: 'lv', label: 'Latvian' },
  { code: 'ln', label: 'Lingala' },
  { code: 'lt', label: 'Lithuanian' },
  { code: 'lb', label: 'Luxembourgish' },
  { code: 'mk', label: 'Macedonian' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'mr', label: 'Marathi' },
  { code: 'ne', label: 'Nepali' },
  { code: 'ps', label: 'Pashto' },
  { code: 'fa', label: 'Persian' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'sr', label: 'Serbian' },
  { code: 'sd', label: 'Sindhi' },
  { code: 'sl', label: 'Slovenian' },
  { code: 'so', label: 'Somali' },
  { code: 'sw', label: 'Swahili' },
  { code: 'te', label: 'Telugu' },
  { code: 'th', label: 'Thai' },
  { code: 'ur', label: 'Urdu' },
  { code: 'cy', label: 'Welsh' },
];

// Flash/Turbo v2.5 supported subset (32 languages) — the first 32 entries above.
const FLASH_V25_CODES = new Set([
  'en', 'es', 'fr', 'de', 'pt', 'it', 'nl', 'pl', 'ru', 'uk', 'cs', 'sk',
  'hr', 'bg', 'ro', 'el', 'hu', 'fi', 'da', 'no', 'sv', 'tr', 'ar', 'hi',
  'ta', 'ja', 'ko', 'zh', 'id', 'ms', 'fil', 'vi',
]);

const VOICE_LANGUAGES_FLASH = VOICE_LANGUAGES_V3.filter(l => FLASH_V25_CODES.has(l.code));

// Return the supported language set for the selected voice model.
// Eleven v3 unlocks the full 74-language list; everything else (Flash/Turbo
// v2.5) uses the 32-language real-time set.
const renameFirstTaskVars = (t) => (t || '').replace(/\{\{(inbound|outbound)_greeting\}\}/g, '{{$1_first_task}}');

function getLanguagesForModel(model, v4Languages = []) {
  if (isV4(model)) return v4Languages;
  return model === 'eleven_v3' ? VOICE_LANGUAGES_V3 : VOICE_LANGUAGES_FLASH;
}


// Grok Voice Preview Component - plays a short sample of the selected Grok voice
const GrokVoicePreview = ({ voice, speed, isDarkMode, textSecondary, inputBg, cardBorder }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);
  const { api } = useAuth();

  const handlePreview = async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }

    setIsLoading(true);
    try {
      const resp = await api.post('/api/voice/grok-preview', { voice, speed });
      const { audio } = resp.data;

      const byteStr = atob(audio);
      const bytes = new Uint8Array(byteStr.length);
      for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);

      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setIsPlaying(false);
      a.onerror = () => { setIsPlaying(false); toast.error('Audio playback failed'); };
      await a.play();
      setIsPlaying(true);
    } catch (err) {
      console.error('Grok preview error:', err);
      toast.error('Failed to generate voice preview');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  return (
    <button
      data-testid="grok-voice-preview-btn"
      onClick={handlePreview}
      disabled={isLoading}
      className={`mt-3 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
        isPlaying
          ? 'bg-orange-400/15 border-orange-400/40 text-orange-400'
          : `${inputBg} ${cardBorder} ${textSecondary} hover:border-orange-400/30 hover:text-orange-400`
      } ${isLoading ? 'opacity-60 cursor-wait' : ''}`}
    >
      {isLoading ? (
        <><Loader2 size={14} className="animate-spin" /> Generating preview...</>
      ) : isPlaying ? (
        <><Pause size={14} /> Stop Preview</>
      ) : (
        <><Play size={14} /> Preview {voice}</>
      )}
    </button>
  );
};


// Contact Variables Panel - Clean design with LeadConnector connection state, toggleable defaults, and search
const ContactVariablesPanel = ({ api, subAccountId, isDarkMode, cardBg, cardBorder, inputBg, textPrimary, textSecondary, enabledVars, onUpdateVars, ghlConnected }) => {
  const [fields, setFields] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState('');
  const searchRef = useRef(null);

  const DEFAULT_VARS = [
    { key: 'contact.first_name', name: 'First Name' },
    { key: 'contact.last_name', name: 'Last Name' },
    { key: 'contact.email', name: 'Email' },
    { key: 'contact.phone', name: 'Phone' },
    { key: 'contact.company', name: 'Company' },
    { key: 'contact.name', name: 'Full Name' },
  ];

  useEffect(() => {
    if (ghlConnected) fetchFields();
    else setLoading(false);
  }, [subAccountId, ghlConnected]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchFields = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/api/sub-accounts/${subAccountId}/ghl/custom-fields`);
      setFields(resp.data);
    } catch (err) { console.error('Failed to fetch custom fields:', err); }
    setLoading(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post(`/api/sub-accounts/${subAccountId}/ghl/custom-fields/refresh`);
      await fetchFields();
    } catch (err) { console.error('Sync failed:', err); }
    setSyncing(false);
  };

  const copyVar = (key) => {
    navigator.clipboard.writeText(`{{${key}}}`);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const isVarEnabled = (key) => enabledVars.includes(key);

  const toggleVar = (key) => {
    if (isVarEnabled(key)) {
      onUpdateVars(enabledVars.filter(v => v !== key));
    } else {
      onUpdateVars([...enabledVars, key]);
    }
  };

  const removeVar = (key) => onUpdateVars(enabledVars.filter(v => v !== key));

  // Custom fields from LeadConnector (excluding defaults)
  const defaultKeys = DEFAULT_VARS.map(d => d.key);
  const customFields = [
    ...(fields?.standard_fields || []).filter(f => !defaultKeys.includes(f.key)).map(f => ({ ...f, type: 'standard' })),
    ...(fields?.custom_fields || []).map(f => ({ ...f, type: 'custom' }))
  ];
  const addedCustomVars = enabledVars.filter(k => !defaultKeys.includes(k));
  const addedCustomFields = addedCustomVars.map(k => customFields.find(f => f.key === k) || { key: k, name: k.replace('contact.', '').replace(/_/g, ' '), type: 'custom' });

  const searchResults = searchQuery.trim()
    ? customFields.filter(f => !enabledVars.includes(f.key) && (f.key.toLowerCase().includes(searchQuery.toLowerCase()) || f.name.toLowerCase().includes(searchQuery.toLowerCase())))
    : customFields.filter(f => !enabledVars.includes(f.key)).slice(0, 10);

  // --- LeadConnector Not Connected State ---
  if (!ghlConnected) {
    return (
      <div className={`p-5 rounded-2xl ${cardBg} border ${cardBorder}`} data-testid="contact-variables-panel">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-amber-400/10 flex items-center justify-center">
            <Link2 size={15} className="text-amber-400" />
          </div>
          <div>
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Contact Variables</h3>
            <p className={`text-xs ${textSecondary}`}>Personalize AI responses with CRM data</p>
          </div>
        </div>
        <div className={`p-4 rounded-xl border border-dashed ${isDarkMode ? 'border-amber-400/20 bg-amber-400/[0.03]' : 'border-amber-300 bg-amber-50'}`}>
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>LeadConnector not connected</p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>
                Connect your LeadConnector account in the <span className="font-medium">Settings</span> tab to access contact variables like name, email, phone, and custom fields.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Connected State ---
  return (
    <div className={`p-5 rounded-2xl ${cardBg} border ${cardBorder}`} data-testid="contact-variables-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-teal-400/10 flex items-center justify-center">
            <Users size={15} className="text-teal-400" />
          </div>
          <div>
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Contact Variables</h3>
            <p className={`text-xs ${textSecondary}`}>Select variables the AI can use in conversations</p>
          </div>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${syncing ? 'opacity-50' : ''} ${isDarkMode ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} border ${cardBorder}`}
          data-testid="sync-custom-fields-btn"
        >
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing...' : 'Sync Fields'}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <RefreshCw size={16} className={`animate-spin ${textSecondary}`} />
          <span className={`ml-2 text-xs ${textSecondary}`}>Loading variables...</span>
        </div>
      ) : (
        <>
          {/* Default Variables - Toggle Grid */}
          <div className="mb-4">
            <p className={`text-xs font-medium ${textSecondary} uppercase tracking-wider mb-2`}>Defaults</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DEFAULT_VARS.map(v => {
                const active = isVarEnabled(v.key);
                return (
                  <div
                    key={v.key}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                      active
                        ? 'bg-teal-400/10 border-teal-400/30'
                        : `${inputBg} ${cardBorder}`
                    }`}
                  >
                    <button
                      onClick={() => toggleVar(v.key)}
                      className="flex items-center gap-2 flex-1 min-w-0"
                      data-testid={`toggle-var-${v.key}`}
                    >
                      <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all ${
                        active ? 'bg-teal-400 border-teal-400' : `${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`
                      }`}>
                        {active && <Check size={10} className="text-white" />}
                      </div>
                      <span className={`text-xs font-medium ${active ? 'text-teal-400' : textSecondary}`}>{v.name}</span>
                    </button>
                    <button
                      onClick={() => copyVar(v.key)}
                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-all shrink-0 ml-2 ${
                        copied === v.key
                          ? 'bg-teal-400/20 text-teal-300'
                          : `${isDarkMode ? 'text-gray-500 hover:text-teal-400 hover:bg-teal-400/10' : 'text-gray-400 hover:text-teal-600 hover:bg-teal-50'}`
                      }`}
                      title={`Copy {{${v.key}}}`}
                      data-testid={`copy-default-var-${v.key}`}
                    >
                      {copied === v.key ? <Check size={10} /> : <Copy size={10} />}
                      <span className="hidden sm:inline">{`{{${v.key.replace('contact.', '')}}}`}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Added Custom Variables - Chips */}
          {addedCustomFields.length > 0 && (
            <div className="mb-4">
              <p className={`text-xs font-medium ${textSecondary} uppercase tracking-wider mb-2`}>Custom Fields</p>
              <div className="space-y-1.5">
                {addedCustomFields.map(f => (
                  <div key={f.key} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${inputBg} ${cardBorder} group`}>
                    <span className={`text-xs ${textPrimary}`}>{f.name}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => copyVar(f.key)}
                        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-all ${
                          copied === f.key
                            ? 'bg-violet-400/20 text-violet-300'
                            : `${isDarkMode ? 'text-gray-500 hover:text-violet-400 hover:bg-violet-400/10' : 'text-gray-400 hover:text-violet-600 hover:bg-violet-50'}`
                        }`}
                        title={`Copy {{${f.key}}}`}
                        data-testid={`copy-var-${f.key}`}
                      >
                        {copied === f.key ? <Check size={10} /> : <Copy size={10} />}
                        <span>{`{{${f.key.replace('contact.', '')}}}`}</span>
                      </button>
                      <button
                        onClick={() => removeVar(f.key)}
                        className={`p-0.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-400/10 transition-all`}
                        title="Remove"
                        data-testid={`remove-var-${f.key}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search to Add Custom Fields */}
          <div ref={searchRef} className="relative">
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${inputBg} border ${
                searchOpen ? (isDarkMode ? 'border-teal-400/30' : 'border-teal-400') : cardBorder
              } transition-all cursor-text`}
              onClick={() => setSearchOpen(true)}
            >
              <Search size={14} className={textSecondary} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Search custom fields to add..."
                className={`flex-1 bg-transparent text-xs ${textPrimary} outline-none placeholder:text-gray-500`}
                data-testid="variable-search-input"
              />
              {searchOpen && (
                <button onClick={(e) => { e.stopPropagation(); setSearchOpen(false); setSearchQuery(''); }} className={`${textSecondary} hover:${textPrimary}`}>
                  <X size={14} />
                </button>
              )}
            </div>
            {searchOpen && (
              <div className={`absolute z-20 top-full left-0 right-0 mt-1.5 rounded-xl ${cardBg} border ${cardBorder} shadow-2xl max-h-52 overflow-auto`}>
                {searchResults.length === 0 ? (
                  <p className={`px-4 py-3 text-xs ${textSecondary} text-center`}>
                    {searchQuery ? 'No matching fields found' : customFields.length === 0 ? 'No custom fields found — try syncing' : 'All available fields have been added'}
                  </p>
                ) : (
                  searchResults.map(f => (
                    <button
                      key={f.key}
                      onClick={() => { toggleVar(f.key); setSearchQuery(''); }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'} border-b ${isDarkMode ? 'border-white/[0.04]' : 'border-gray-100'} last:border-0`}
                      data-testid={`search-result-${f.key}`}
                    >
                      <div className="flex items-center gap-2">
                        <Plus size={12} className="text-teal-400" />
                        <span className={`text-xs ${textPrimary}`}>{f.name}</span>
                      </div>
                      <span className={`text-[10px] font-mono ${f.type === 'custom' ? 'text-violet-400/70' : 'text-teal-400/70'}`}>{f.key}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Tip */}
          {enabledVars.length > 0 && (
            <p className={`text-[10px] ${textSecondary} mt-3`}>
              Use <span className="font-mono text-teal-400/80">{'{{variable_key}}'}</span> in your System Prompt to inject contact data.
            </p>
          )}
        </>
      )}
    </div>
  );
};


// Voice Test Panel Component - Real-time voice conversation interface using MediaRecorder + Whisper
const VoiceTestPanel = ({ agentId, config, api, isDarkMode, cardBg, cardBorder, inputBg, textPrimary, textSecondary, chatOnly = false }) => {
  const [testMode, setTestMode] = useState('text'); // Start with 'text' always - voice mode is handled by AdvancedVoicePanel
  const [isCallActive, setIsCallActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [testSessionId, setTestSessionId] = useState('');
  const [error, setError] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Call Me state
  const [callMePhone, setCallMePhone] = useState('');
  const [callMeVariables, setCallMeVariables] = useState({});
  const [isCallingMe, setIsCallingMe] = useState(false);
  const [detectedVariables, setDetectedVariables] = useState([]);

  // Auto-detect {variables} and {{variables}} from agent prompts (supports dot notation like contact.first_name)
  useEffect(() => {
    const allPromptText = [
      config?.system_prompt || '',
      config?.chat_prompt || '',
      config?.first_message || '',
      config?.outbound_greeting || '',
      config?.personality_traits || '',
      config?.style_guardrails || '',
      config?.response_guidelines || '',
    ].join(' ');
    
    // Match both {variable} and {{variable}} patterns — support dots for LeadConnector fields
    const found = new Set();
    let match;
    const doubleRegex = /\{\{([\w.]+)\}\}/g;
    while ((match = doubleRegex.exec(allPromptText)) !== null) found.add(match[1]);
    const singleRegex = /(?<!\{)\{([\w.]+)\}(?!\})/g;
    while ((match = singleRegex.exec(allPromptText)) !== null) found.add(match[1]);
    
    const varList = Array.from(found);
    setDetectedVariables(varList);
    
    // Initialize missing variable values
    setCallMeVariables(prev => {
      const updated = { ...prev };
      varList.forEach(v => {
        if (!(v in updated)) updated[v] = '';
      });
      // Clean up removed variables
      Object.keys(updated).forEach(k => {
        if (!varList.includes(k)) delete updated[k];
      });
      return updated;
    });
  }, [config?.system_prompt, config?.chat_prompt, config?.first_message, config?.outbound_greeting, config?.personality_traits, config?.style_guardrails, config?.response_guidelines]);
  
  // VAD (Voice Activity Detection) settings - optimized for low latency
  const silenceThreshold = 0.02; // Lower threshold to detect more speech
  const silenceDuration = 800; // 800ms of silence - fast response
  const minSpeechDuration = 200; // 200ms of speech required (faster)
  const maxRecordingDuration = 12000; // Auto-send after 12 seconds max
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isCallActiveRef = useRef(false);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const silenceStartRef = useRef(null);
  const hasSpokenRef = useRef(false);
  const speechStartRef = useRef(null);
  const recordingStartRef = useRef(null); // Track recording start time

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Voice Activity Detection - auto-detect silence and trigger send
  const startAudioVisualization = (stream) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = audioContext;
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    analyser.fftSize = 512; // Higher resolution for better detection
    analyser.smoothingTimeConstant = 0.4; // Faster response
    analyserRef.current = analyser;
    
    const dataArray = new Float32Array(analyser.frequencyBinCount);
    let smoothedLevel = 0; // Running average for smoother detection
    
    const updateLevel = () => {
      if (!isCallActiveRef.current) return;
      
      // Use getFloatFrequencyData for more accurate readings
      analyser.getFloatFrequencyData(dataArray);
      
      // Calculate RMS of frequency magnitudes (better for voice detection)
      let sumSquares = 0;
      let count = 0;
      // Focus on voice frequency range (85-3000 Hz) - bins roughly 2-50 for 512 FFT at 44.1kHz
      const startBin = 2;
      const endBin = Math.min(60, dataArray.length);
      for (let i = startBin; i < endBin; i++) {
        // Convert from dB to linear and square it
        const linear = Math.pow(10, dataArray[i] / 20);
        sumSquares += linear * linear;
        count++;
      }
      const rms = Math.sqrt(sumSquares / count);
      
      // Smooth the level to avoid jitter
      smoothedLevel = smoothedLevel * 0.7 + rms * 0.3;
      const normalizedLevel = Math.min(1, smoothedLevel * 50); // Scale for 0-1 range
      setAudioLevel(normalizedLevel * 2); // Scale for visual effect
      
      // Voice Activity Detection - only when recording
      if (mediaRecorderRef.current?.state === 'recording') {
        const now = Date.now();
        const recordingDuration = recordingStartRef.current ? now - recordingStartRef.current : 0;
        
        // Auto-send after max recording duration (failsafe)
        if (recordingDuration > maxRecordingDuration) {
          console.log('[VAD] Max duration reached, auto-sending');
          triggerStopRecording();
          animationFrameRef.current = requestAnimationFrame(updateLevel);
          return;
        }
        
        // Check if audio level indicates speech (using smoothed level)
        const isSpeaking = normalizedLevel > silenceThreshold;
        
        if (isSpeaking) {
          // Speech detected
          if (!hasSpokenRef.current) {
            speechStartRef.current = now;
            hasSpokenRef.current = true;
            console.log('[VAD] Speech started, level:', normalizedLevel.toFixed(3));
          }
          silenceStartRef.current = null; // Reset silence timer
        } else if (hasSpokenRef.current) {
          // Silence after speech - check if we should send
          const speechDuration = speechStartRef.current ? now - speechStartRef.current : 0;
          
          // Only detect end of speech if user spoke for minimum duration
          if (speechDuration >= minSpeechDuration) {
            if (!silenceStartRef.current) {
              silenceStartRef.current = now;
              console.log('[VAD] Silence started, waiting', silenceDuration, 'ms');
            } else {
              const silenceTime = now - silenceStartRef.current;
              if (silenceTime >= silenceDuration) {
                console.log('[VAD] Silence detected after', speechDuration, 'ms speech, sending...');
                triggerStopRecording();
              }
            }
          }
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };
    
    // Helper to stop recording without recursion issues
    const triggerStopRecording = () => {
      hasSpokenRef.current = false;
      silenceStartRef.current = null;
      speechStartRef.current = null;
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }
    };
    
    updateLevel();
  };

  const startCall = async () => {
    setError(null);
    setConversationHistory([]);
    
    // Request microphone permission
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      startAudioVisualization(stream);
    } catch (err) {
      setError('Microphone access required. Please allow microphone permission.');
      return;
    }
    
    setIsCallActive(true);
    isCallActiveRef.current = true;
    
    // Check if AI should speak first (config.ai_speaks_first)
    const aiSpeaksFirst = config.ai_speaks_first !== false;
    
    console.log('[Voice] Starting call, AI speaks first:', aiSpeaksFirst);
    
    if (aiSpeaksFirst && config.first_message) {
      const greeting = { 
        role: 'assistant', 
        content: config.first_message,
        timestamp: new Date().toISOString()
      };
      setConversationHistory([greeting]);
      
      await playAIResponse(config.first_message);
      if (isCallActiveRef.current) {
        startRecording();
      }
    } else {
      startRecording();
    }
  };

  const endCall = () => {
    setIsCallActive(false);
    isCallActiveRef.current = false;
    setIsRecording(false);
    setIsSpeaking(false);
    setIsProcessing(false);
    setAudioLevel(0);
    hasSpokenRef.current = false;
    silenceStartRef.current = null;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const startRecording = () => {
    if (!streamRef.current || !isCallActiveRef.current) return;
    
    audioChunksRef.current = [];
    hasSpokenRef.current = false;
    silenceStartRef.current = null;
    speechStartRef.current = null;
    recordingStartRef.current = Date.now();
    
    console.log('[Voice] Starting recording...');
    
    try {
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length > 0 && isCallActiveRef.current) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await processAudio(audioBlob);
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100); // Collect data every 100ms for smoother VAD
      setIsRecording(true);
    } catch (err) {
      console.error('MediaRecorder error:', err);
      setError('Failed to start recording. Try a different browser.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      setIsRecording(false);
      mediaRecorderRef.current.stop();
    }
  };

  const processAudio = async (audioBlob) => {
    if (!isCallActiveRef.current) return;
    
    console.log('[Voice] Processing audio blob, size:', audioBlob.size);
    setIsProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      
      console.log('[Voice] Sending to transcribe...');
      const transcribeResponse = await api.post('/api/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const userText = transcribeResponse.data.text;
      console.log('[Voice] Transcription result:', userText);
      
      if (!userText || !userText.trim()) {
        console.log('[Voice] No speech detected, resuming recording');
        setIsProcessing(false);
        if (isCallActiveRef.current) {
          startRecording();
        }
        return;
      }
      
      const userMsg = { role: 'user', content: userText.trim(), timestamp: new Date().toISOString() };
      setConversationHistory(prev => [...prev, userMsg]);
      
      console.log('[Voice] Getting AI response...');
      // Pass full live config to backend for real-time testing
      const response = await api.post(`/api/agents/${agentId}/test-voice`, {
        text: userText.trim(),
        conversation_history: [...conversationHistory, userMsg],
        voice_id: config.voice_id,
        voice_stability: config.voice_stability,
        voice_model: config.voice_model,
        include_audio: true,
        // Live config overrides - test with current editor values
        system_prompt: config.system_prompt,
        first_message: config.first_message,
        personality_traits: config.personality_traits,
        style_guardrails: config.style_guardrails,
        response_guidelines: config.response_guidelines,
        ai_model: config.ai_model,
        functions: config.functions,
        template_variables: callMeVariables || {},
        function_configs: config.function_configs || {}
      });
      
      const { response: aiText, audio, audio_format, function_calls } = response.data;
      console.log('[Voice] AI response received, audio:', audio ? 'yes' : 'no', 'format:', audio_format);
      
      const shouldEndCall = function_calls?.some(fc => fc.function === 'end_call');
      
      const aiMsg = { 
        role: 'assistant', 
        content: aiText,
        functionCalls: function_calls || [],
        timestamp: new Date().toISOString()
      };
      setConversationHistory(prev => [...prev, aiMsg]);
      
      setIsProcessing(false);
      
      if (audio && isCallActiveRef.current) {
        console.log('[Voice] Playing audio response...');
        await playAudioResponse(audio, audio_format);
        console.log('[Voice] Audio playback complete');
      }
      
      if (shouldEndCall) {
        console.log('[Voice] End call triggered by AI');
        endCall();
        return;
      }
      
      // Resume recording for next user input
      if (isCallActiveRef.current) {
        console.log('[Voice] Resuming recording...');
        startRecording();
      }
      
    } catch (err) {
      console.error('[Voice] Processing error:', err);
      const errorMsg = { role: 'error', content: 'Failed to process. Retrying...' };
      setConversationHistory(prev => [...prev, errorMsg]);
      setIsProcessing(false);
      
      // Always try to recover and continue
      if (isCallActiveRef.current) {
        setTimeout(() => {
          if (isCallActiveRef.current) {
            console.log('[Voice] Retrying recording after error...');
            startRecording();
          }
        }, 1500);
      }
    }
  };

  const playAIResponse = async (text) => {
    setIsSpeaking(true);
    try {
      const response = await api.post(`/api/tts/generate`, {
        text: text,
        voice_id: config.voice_id || '21m00Tcm4TlvDq8ikWAM',
        stability: isV4(config.voice_model) ? (config.voice_v4_stability ?? .5) : config.voice_stability === 'creative' ? 0.3 : config.voice_stability === 'robust' ? 0.8 : 0.5,
        ...(isV4(config.voice_model) ? { similarity_boost: config.voice_similarity ?? .8 } : {}),
        voice_model: config.voice_model || 'eleven_flash_v2_5'
      });
      
      if (response.data.audio) {
        await playAudioResponse(response.data.audio);
      }
    } catch (e) {
      console.error('TTS error:', e);
    }
    setIsSpeaking(false);
  };

  const playAudioResponse = (audioBase64, audioFormat) => {
    return new Promise((resolve) => {
      setIsSpeaking(true);
      const mimeType = audioFormat || 'audio/mpeg';
      const audioBlob = new Blob(
        [Uint8Array.from(atob(audioBase64), c => c.charCodeAt(0))],
        { type: mimeType }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) audioRef.current.pause();
      
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        resolve();
      };
      audioRef.current.onerror = () => {
        setIsSpeaking(false);
        resolve();
      };
      audioRef.current.play().catch(() => {
        setIsSpeaking(false);
        resolve();
      });
    });
  };

  const handleTextSend = async () => {
    if (!textInput.trim() || isProcessing) return;

    const text = textInput.trim();
    setTextInput('');

    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setConversationHistory(prev => [...prev, userMsg]);

    setIsProcessing(true);

    try {
      // Chat mode: use /api/agents/{id}/test which mirrors EXACT real-channel
      // behavior (concise enforcement, KB, training entries, misspellings,
      // double-texting split, response_wait_time delays).
      const endpoint = chatOnly
        ? `/api/agents/${agentId}/test`
        : `/api/agents/${agentId}/test-voice`;

      const payload = chatOnly
        ? {
            message: text,
            mode: 'text',
            // Chat tests exercise the chat prompt only when the toggle is on
            system_prompt: config.chat_prompt_enabled && (config.chat_prompt || '').trim() ? config.chat_prompt : config.system_prompt,
            functions: Object.entries(config.functions || {}).filter(([_, e]) => e).map(([id]) => id),
            // Multi-turn memory — send full prior conversation so the AI
            // doesn't loop on the same question across turns.
            conversation_history: conversationHistory
              .filter(m => m.role === 'user' || m.role === 'assistant')
              .map(m => ({ role: m.role, content: m.content })),
            session_id: testSessionId || undefined,
          }
        : {
            text,
            conversation_history: [...conversationHistory, userMsg],
            voice_id: config.voice_id,
            voice_stability: config.voice_stability,
            voice_model: config.voice_model,
            include_audio: false,
            system_prompt: config.system_prompt,
            first_message: config.first_message,
            personality_traits: config.personality_traits,
            style_guardrails: config.style_guardrails,
            response_guidelines: config.response_guidelines,
            ai_model: config.ai_model,
            functions: config.functions,
            template_variables: callMeVariables || {},
            function_configs: config.function_configs || {},
          };

      const response = await api.post(endpoint, payload);

      if (chatOnly) {
        // New test endpoint returns: { parts[], response_delay_ms, between_parts_ms, tool_calls, session_id }
        const { parts = [], response_delay_ms = 0, between_parts_ms = 0, tool_calls = [], session_id } = response.data;
        if (session_id && !testSessionId) setTestSessionId(session_id);
        // Honor the agent's configured response delay before the first bubble
        if (response_delay_ms > 0) {
          await new Promise(r => setTimeout(r, Math.min(response_delay_ms, 8000)));
        }
        for (let i = 0; i < parts.length; i++) {
          if (i > 0 && between_parts_ms > 0) {
            // Show typing dots between parts (we re-show the indicator)
            setIsProcessing(true);
            await new Promise(r => setTimeout(r, Math.min(between_parts_ms, 6000)));
          }
          const aiMsg = {
            role: 'assistant',
            content: parts[i],
            functionCalls: i === 0 ? (tool_calls || []) : [],
            timestamp: new Date().toISOString(),
          };
          setConversationHistory(prev => [...prev, aiMsg]);
          setIsProcessing(false);
        }
      } else {
        const { response: aiText, function_calls } = response.data;
        const aiMsg = {
          role: 'assistant',
          content: aiText,
          functionCalls: function_calls || [],
          timestamp: new Date().toISOString(),
        };
        setConversationHistory(prev => [...prev, aiMsg]);
      }

    } catch (err) {
      const errorMsg = { role: 'error', content: err?.response?.data?.detail || 'Failed to get response' };
      setConversationHistory(prev => [...prev, errorMsg]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Call Me Feature - Initiate outbound call with all template variables
  const handleCallMe = async () => {
    if (!callMePhone.trim()) {
      setError('Please enter a phone number');
      return;
    }
    
    setIsCallingMe(true);
    setError(null);
    
    try {
      // Build template variables from all detected variable inputs
      const templateVars = { ...callMeVariables };
      // Ensure 'name' has a fallback
      if (detectedVariables.includes('name') && !templateVars.name?.trim()) {
        templateVars.name = 'there';
      }
      
      const response = await api.post(`/api/agents/${agentId}/call-me`, {
        phone_number: callMePhone.trim(),
        template_variables: templateVars
      });
      
      if (response.data.success) {
        toast.success('Call initiated! You should receive a call shortly.');
        const varSummary = Object.entries(templateVars)
          .filter(([, v]) => v)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        setConversationHistory([{
          role: 'system',
          content: `Call initiated to ${callMePhone}${varSummary ? ` (${varSummary})` : ''}. The AI agent will call you shortly.`,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setIsCallingMe(false);
    }
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const orbState = isSpeaking ? 'speaking' : isRecording ? 'listening' : isProcessing ? 'processing' : 'idle';
  const orbScale = isRecording ? 1 + (audioLevel * 0.2) : 1;

  // Function call component
  const FunctionCallBadge = ({ fc }) => {
    const [expanded, setExpanded] = useState(false);
    const isError = fc.error || fc.status === 'error';
    const isSuccess = fc.result || fc.status === 'success';
    const isSimulated = fc.status === 'simulated';
    const funcName = fc.function || fc.name;
    
    // Determine type: function call vs result
    const isResult = isError || isSuccess;
    
    const barBg = isError 
      ? 'bg-gradient-to-r from-red-600/30 to-red-500/10 border-red-500/20'
      : isSuccess
        ? 'bg-gradient-to-r from-emerald-600/30 to-emerald-500/10 border-emerald-500/20'
        : 'bg-gradient-to-r from-violet-600/30 to-indigo-500/10 border-violet-500/20';
    
    const iconColor = isError ? 'text-red-400' : isSuccess ? 'text-emerald-400' : 'text-violet-400';
    const textColor = isError ? 'text-red-300' : isSuccess ? 'text-emerald-300' : 'text-violet-300';
    const labelColor = isError ? 'text-red-400/70' : isSuccess ? 'text-emerald-400/70' : 'text-violet-400/70';
    
    return (
      <div className="w-full space-y-1">
        {/* Function Call bar */}
        {!isResult && (
          <button onClick={() => setExpanded(!expanded)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border ${barBg} transition-all hover:brightness-110 cursor-pointer`} data-testid={`fc-call-${funcName}`}>
            <Zap size={12} className={iconColor} />
            <span className={`text-[11px] font-medium ${labelColor}`}>Function Call:</span>
            <span className={`text-xs font-semibold font-mono ${textColor}`}>{funcName}</span>
            <ChevronDown size={12} className={`ml-auto ${labelColor} transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
        {/* Result bar */}
        {isResult && (
          <button onClick={() => setExpanded(!expanded)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border ${barBg} transition-all hover:brightness-110 cursor-pointer`} data-testid={`fc-result-${funcName}`}>
            {isError ? <XCircle size={12} className={iconColor} /> : <CheckCircle size={12} className={iconColor} />}
            <span className={`text-[11px] font-medium ${labelColor}`}>Result:</span>
            <span className={`text-xs font-semibold font-mono ${textColor}`}>{funcName}</span>
            {isError && <span className="text-[10px] text-red-400 font-bold ml-1">FAILED</span>}
            {isSuccess && <span className="text-[10px] text-emerald-400 font-bold ml-1">OK</span>}
            <ChevronDown size={12} className={`ml-auto ${labelColor} transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
        {/* Expanded details */}
        {expanded && (
          <div className={`px-3 py-2 rounded-lg border ml-2 ${isError ? 'bg-red-500/5 border-red-500/10' : isSuccess ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-violet-500/5 border-violet-500/10'}`}>
            {fc.args && Object.keys(fc.args).length > 0 && (
              <div className="space-y-0.5">
                {Object.entries(fc.args).map(([k, v]) => (
                  <p key={k} className="text-[10px] text-slate-400"><span className="text-slate-500">{k}:</span> {String(v).slice(0, 80)}</p>
                ))}
              </div>
            )}
            {fc.parameters && Object.keys(fc.parameters).length > 0 && (
              <div className="space-y-0.5">
                {Object.entries(fc.parameters).map(([k, v]) => (
                  <p key={k} className="text-[10px] text-slate-400"><span className="text-slate-500">{k}:</span> {String(v).slice(0, 80)}</p>
                ))}
              </div>
            )}
            {fc.result && typeof fc.result === 'object' && (
              <p className="text-[10px] text-emerald-300/60 mt-1">{JSON.stringify(fc.result).slice(0, 200)}</p>
            )}
            {fc.error && (
              <p className="text-[10px] text-red-300/60 mt-1">{typeof fc.error === 'string' ? fc.error : JSON.stringify(fc.error).slice(0, 200)}</p>
            )}
            {isSimulated && !fc.args && !fc.parameters && !fc.result && !fc.error && (
              <p className="text-[10px] text-slate-500">Simulated — no data returned</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`h-full flex flex-col ${isDarkMode ? 'bg-[#0a0b10]' : 'bg-gray-50'} relative`} data-testid="chat-test-panel">
      {/* Header */}
      <div className="shrink-0 flex justify-center p-2 z-20">
        <div className={`flex items-center gap-2 ${isDarkMode ? 'text-white/60' : 'text-gray-500'} text-xs`}>
          <MessageSquare size={12} />
          <span>Chat Testing</span>
        </div>
      </div>
      {/* Text Mode — iPhone mockup that mirrors real SMS / IG / FB / widget */}
      {testMode === 'text' && (
        <div className="flex-1 min-h-0">
          <IPhoneChatMockup
            messages={conversationHistory}
            isProcessing={isProcessing}
            value={textInput}
            onChange={setTextInput}
            onSend={handleTextSend}
            onReset={() => { setConversationHistory([]); setTextInput(''); setTestSessionId(''); }}
            agentName={config?.name || 'AI Agent'}
            testId="agent-test-iphone-mockup"
          />
        </div>
      )}

      {/* Call Me Mode - Hidden when chatOnly */}
      {testMode === 'callme' && !chatOnly && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-y-auto">
          <div className="w-full max-w-sm space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mx-auto mb-4">
                <PhoneOutgoing size={28} className="text-white" />
              </div>
              <h3 className={`${textPrimary} font-semibold text-lg`}>Have AI Call You</h3>
              <p className={`${textSecondary} text-sm mt-1`}>Test your agent with a real outbound call</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className={`block ${textSecondary} text-xs mb-1.5`}>Phone Number *</label>
                <input
                  type="tel"
                  value={callMePhone}
                  onChange={(e) => setCallMePhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className={`w-full px-4 py-3 rounded-xl ${isDarkMode ? 'bg-[#1a1b23] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:outline-none focus:border-teal-400/50`}
                  data-testid="callme-phone-input"
                />
              </div>
              
              {/* Dynamic Variable Fields */}
              {detectedVariables.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Zap size={14} className="text-amber-400" />
                    <span className="text-amber-400 text-xs font-medium">DETECTED PROMPT VARIABLES</span>
                  </div>
                  {detectedVariables.map((varName) => (
                    <div key={varName}>
                      <label className={`block ${textSecondary} text-xs mb-1.5`}>
                        <code className="text-teal-400">{`{${varName}}`}</code>
                      </label>
                      <input
                        type="text"
                        value={callMeVariables[varName] || ''}
                        onChange={(e) => setCallMeVariables(prev => ({ ...prev, [varName]: e.target.value }))}
                        placeholder={`Enter ${varName}...`}
                        className={`w-full px-4 py-2.5 rounded-xl ${isDarkMode ? 'bg-[#1a1b23] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:outline-none focus:border-teal-400/50 text-sm`}
                        data-testid={`callme-var-${varName}`}
                      />
                    </div>
                  ))}
                  <p className={`${isDarkMode ? 'text-gray-600' : 'text-gray-400'} text-xs`}>
                    These variables from your prompts will be replaced during the call.
                  </p>
                </div>
              )}
              
              {detectedVariables.length === 0 && (
                <div className={`${isDarkMode ? 'bg-[#1a1b23] border-white/5' : 'bg-gray-100 border-gray-200'} border rounded-xl p-3`}>
                  <p className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'} text-xs text-center`}>
                    No <code className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{'{{variables}}'}</code> found in prompts. Add them like <code className="text-teal-400">{'{{name}}'}</code> or <code className="text-teal-400">{'{{company}}'}</code> to customize outbound calls.
                  </p>
                </div>
              )}

              {/* Enabled Functions Summary */}
              {(() => {
                const enabledFuncs = Object.entries(config?.functions || {}).filter(([, v]) => v).map(([k]) => k);
                if (enabledFuncs.length === 0) return null;
                return (
                  <div className={`${isDarkMode ? 'bg-[#1a1b23] border-white/5' : 'bg-gray-100 border-gray-200'} border rounded-xl p-3`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Wrench size={12} className="text-teal-400" />
                      <span className="text-teal-400 text-xs font-medium">ENABLED FUNCTIONS ({enabledFuncs.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {enabledFuncs.slice(0, 6).map(fn => (
                        <span key={fn} className="px-2 py-0.5 rounded bg-teal-500/10 text-teal-300 text-[10px] border border-teal-500/20">
                          {fn}
                        </span>
                      ))}
                      {enabledFuncs.length > 6 && (
                        <span className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-200 text-gray-500'} text-[10px]`}>
                          +{enabledFuncs.length - 6} more
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
            
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            
            {conversationHistory.length > 0 && (
              <div className={`${isDarkMode ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border rounded-xl p-3`}>
                <p className={`${isDarkMode ? 'text-blue-300' : 'text-blue-700'} text-sm`}>{conversationHistory[0]?.content}</p>
              </div>
            )}
            
            <button
              onClick={handleCallMe}
              disabled={!callMePhone.trim() || isCallingMe}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium hover:shadow-lg hover:shadow-teal-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="callme-btn"
            >
              {isCallingMe ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Initiating Call...
                </>
              ) : (
                <>
                  <Phone size={18} />
                  Call Me Now
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// Call Me Panel - Standalone component for outbound test calls with dynamic {variables}
const CallMePanel = ({ agentId, config, api, isDarkMode, textPrimary, textSecondary }) => {
  const [phone, setPhone] = useState('');
  const [variables, setVariables] = useState({});
  const [isCalling, setIsCalling] = useState(false);
  const [error, setError] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [detectedVars, setDetectedVars] = useState([]);

  // Auto-detect {variables} and {{variables}} from all prompt fields
  useEffect(() => {
    const allText = [
      config?.system_prompt || '',
      config?.chat_prompt || '',
      config?.first_message || '',
      config?.outbound_greeting || '',
      config?.personality_traits || '',
      config?.style_guardrails || '',
      config?.response_guidelines || '',
    ].join(' ');

    const found = new Set();
    let match;
    const doubleRegex = /\{\{([\w.]+)\}\}/g;
    while ((match = doubleRegex.exec(allText)) !== null) found.add(match[1]);
    const singleRegex = /(?<!\{)\{([\w.]+)\}(?!\})/g;
    while ((match = singleRegex.exec(allText)) !== null) found.add(match[1]);
    const varList = Array.from(found);
    setDetectedVars(varList);

    setVariables(prev => {
      const updated = { ...prev };
      varList.forEach(v => { if (!(v in updated)) updated[v] = ''; });
      Object.keys(updated).forEach(k => { if (!varList.includes(k)) delete updated[k]; });
      return updated;
    });
  }, [config?.system_prompt, config?.chat_prompt, config?.first_message, config?.outbound_greeting, config?.personality_traits, config?.style_guardrails, config?.response_guidelines]);

  const handleCall = async () => {
    if (!phone.trim()) { setError('Please enter a phone number'); return; }
    setIsCalling(true);
    setError(null);
    setCallStatus(null);
    try {
      const templateVars = { ...variables };
      if (detectedVars.includes('name') && !templateVars.name?.trim()) templateVars.name = 'there';

      const res = await api.post(`/api/agents/${agentId}/call-me`, {
        phone_number: phone.trim(),
        template_variables: templateVars,
      });
      if (res.data.success) {
        const varSummary = Object.entries(templateVars).filter(([,v]) => v).map(([k,v]) => `${k}=${v}`).join(', ');
        setCallStatus({ success: true, message: res.data.message, from: res.data.from_number, vars: varSummary });
        toast.success('Call initiated! You should receive a call shortly.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setIsCalling(false);
    }
  };

  const enabledFuncs = Object.entries(config?.functions || {}).filter(([,v]) => v).map(([k]) => k);

  return (
    <div className={`h-full flex flex-col items-center justify-center p-6 overflow-y-auto ${isDarkMode ? 'bg-[#0a0b10]' : 'bg-gray-50'}`} data-testid="callme-panel">
      <div className="w-full max-w-sm space-y-5">
        {/* Header */}
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mx-auto mb-3">
            <PhoneOutgoing size={24} className="text-white" />
          </div>
          <h3 className={`${textPrimary} font-semibold text-lg`}>Make AI Call</h3>
          <p className={`${textSecondary} text-sm mt-1`}>Test your agent with a real outbound call</p>
        </div>

        {/* Phone Input */}
        <div>
          <label className={`block ${textSecondary} text-xs mb-1.5`}>Phone Number *</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 (555) 123-4567"
            className={`w-full px-4 py-3 rounded-xl ${isDarkMode ? 'bg-[#1a1b23] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:outline-none focus:border-teal-400/50`}
            data-testid="callme-phone-input"
          />
        </div>

        {/* Dynamic Variable Fields */}
        {detectedVars.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-amber-400" />
              <span className="text-amber-400 text-xs font-medium uppercase tracking-wide">Prompt Variables</span>
              <span className={`${textSecondary} text-xs`}>({detectedVars.length})</span>
            </div>
            {detectedVars.map(varName => (
              <div key={varName}>
                <label className={`block ${textSecondary} text-xs mb-1`}>
                  <code className="text-teal-400 font-mono">{`{${varName}}`}</code>
                </label>
                <input
                  type="text"
                  value={variables[varName] || ''}
                  onChange={(e) => setVariables(prev => ({ ...prev, [varName]: e.target.value }))}
                  placeholder={`Enter ${varName}...`}
                  className={`w-full px-3 py-2.5 rounded-xl ${isDarkMode ? 'bg-[#1a1b23] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border text-sm focus:outline-none focus:border-teal-400/50`}
                  data-testid={`callme-var-${varName}`}
                />
              </div>
            ))}
            <p className={`${isDarkMode ? 'text-gray-600' : 'text-gray-400'} text-[11px]`}>
              These variables from your prompts will be replaced during the call.
            </p>
          </div>
        )}

        {detectedVars.length === 0 && (
          <div className={`${isDarkMode ? 'bg-[#1a1b23] border-white/5' : 'bg-gray-100 border-gray-200'} border rounded-xl p-3`}>
            <p className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'} text-xs text-center`}>
              No <code className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{'{{variables}}'}</code> detected in prompts. Add them like <code className="text-teal-400">{'{{name}}'}</code> or <code className="text-teal-400">{'{{company}}'}</code> to personalize outbound calls.
            </p>
          </div>
        )}

        {/* Enabled Functions Summary */}
        {enabledFuncs.length > 0 && (
          <div className={`${isDarkMode ? 'bg-[#1a1b23] border-white/5' : 'bg-gray-100 border-gray-200'} border rounded-xl p-3`}>
            <div className="flex items-center gap-2 mb-2">
              <Wrench size={12} className="text-teal-400" />
              <span className="text-teal-400 text-xs font-medium">ENABLED FUNCTIONS ({enabledFuncs.length})</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {enabledFuncs.slice(0, 6).map(fn => (
                <span key={fn} className="px-2 py-0.5 rounded bg-teal-500/10 text-teal-300 text-[10px] border border-teal-500/20">
                  {fn}
                </span>
              ))}
              {enabledFuncs.length > 6 && (
                <span className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-200 text-gray-500'} text-[10px]`}>+{enabledFuncs.length - 6} more</span>
              )}
            </div>
          </div>
        )}

        {/* Error / Status */}
        {error && <p className="text-red-400 text-sm text-center" data-testid="callme-error">{error}</p>}
        {callStatus?.success && (
          <div className={`${isDarkMode ? 'bg-teal-500/10 border-teal-500/20' : 'bg-teal-50 border-teal-200'} border rounded-xl p-3`} data-testid="callme-status">
            <p className={`${isDarkMode ? 'text-teal-300' : 'text-teal-700'} text-sm`}>{callStatus.message}</p>
            {callStatus.from && <p className={`${textSecondary} text-xs mt-1`}>From: {callStatus.from}</p>}
            {callStatus.vars && <p className={`${textSecondary} text-xs mt-0.5`}>Variables: {callStatus.vars}</p>}
          </div>
        )}

        {/* Call Button */}
        <button
          onClick={handleCall}
          disabled={!phone.trim() || isCalling}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium hover:shadow-lg hover:shadow-teal-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="callme-btn"
        >
          {isCalling ? (
            <><Loader2 size={18} className="animate-spin" /> Initiating Call...</>
          ) : (
            <><Phone size={18} /> Call Me Now</>
          )}
        </button>
      </div>
    </div>
  );
};


const AgentEditorContent = () => {
  const { workspaceId, subAccountId, agentId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { catalog: v4Catalog, error: v4CatalogError, retry: retryV4Catalog } = useV4Capabilities(api);
  const { subAccount } = useSubAccount();
  const { isDarkMode } = useTheme();
  
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basics');
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [expandedPromptField, setExpandedPromptField] = useState(null); // 'system_prompt', 'first_message', etc.
  // Multilingual section is shown when toggled on, or when the agent already
  // has additional languages configured. Toggling off clears the languages.
  const [multilingualOpen, setMultilingualOpen] = useState(false);
  // Collapsible per-function pre-tool phrases & sounds (Call Settings tab)
  const [pretoolPhrasesOpen, setPretoolPhrasesOpen] = useState(false);

  
  // Agent configuration state
  const [config, setConfig] = useState({
    name: '',
    status: 'active',
    activation_tag: '',
    description: '',
    type: 'voice',
    direction: 'both', // inbound, outbound, both
    language: 'en',
    voice_id: 'jessica',
    voice_meta: null,
    voice_speed: 1.0,
    voice_provider: 'elevenlabs',
    voice_model: 'eleven_flash_v2_5',
    voice_engine: 'elevenlabs', // 'elevenlabs' or 'grok'
    grok_voice: 'Eve',
    grok_speed: 1.0,
    grok_temperature: 0.8,
    grok_vad_threshold: 0.6,
    grok_silence_duration_ms: 800,
    grok_prefix_padding_ms: 200,
    turn_taking: 'normal',
    voice_stability: 'natural',
    voice_v4_stability: 0.5,
    voice_similarity: 0.8,
    voice_sensitivity: 50, // 0-100, higher = less sensitive (filters more noise)
    ai_model: 'claude-sonnet-5.5',
    temperature: 0.6,
    max_call_duration: 30,
    additional_languages: [],
    system_prompt: '',
    chat_prompt: '',
    chat_prompt_enabled: false,
    first_message: '',
    outbound_greeting: '',
    personality_traits: '',
    style_guardrails: '',
    response_guidelines: '',
    functions: {},
    outbound_voice_agent_id: '',
    knowledge_base_ids: [],
    // New: Phone Number Assignment
    assigned_phone_number_id: '',
    // New: Call Settings
    recording_disclosure: true,
    ai_speaks_first: true,
    pre_tool_speech: true,
    pre_tool_speech_overrides: {},
    tool_call_sounds: {},
    silence_timeout: 120,
    background_noise: 'call_center',
    background_noise_volume: 0.38,
    rules_of_engagement: 'ai_initiates',
    voicemail_detection: true,
    voicemail_message: '',
    voicemail_action: 'hang_up',
    inbound_ring_count: 1,
    incoming_call_webhook: '',
    post_call_webhook: '',
    // New: Intelligence Settings
    ghl_calendars: [],
    calendar_descriptions: {},
    contact_intelligence: false,
    interaction_memory: false,
    inbound_first_task: '',
    outbound_first_task: '',
    callback_detection: false,
    callback_max_days: 3,
    callback_rejection_prompt: '',
    callback_missed_workflow_id: '',
    textback_detection: false,
    textback_workflow_id: '',
    campaign_label: '',
    timezone: 'America/New_York',
    // Chat Channel Settings
    chat_enabled: false,
    chat_channels: {
      whatsapp: false,
      sms: false,
      instagram_dm: false,
      facebook_messenger: false,
    },
    // Outbound Call Trigger (for chat assistants)
    can_trigger_outbound_call: false,
    outbound_call_description: '',
    // Switch to SMS (chat widgets only)
    can_switch_to_sms: false,
    // Optional: hand off to a different chat/SMS agent for the
    // continuation. Empty = use this same agent on SMS.
    switch_to_sms_agent_id: '',
    // Tells the LLM WHEN to switch to SMS (same UX as outbound call
    // trigger description). Empty = use built-in default heuristics.
    switch_to_sms_description: '',
    // Human-like Texting Settings
    misspelling_enabled: false,
    misspelling_percentage: 5,
    response_wait_time: 0,
    randomize_delay: false,
    double_texting_enabled: false,
    // Transfer Call Configuration
    transfer_call_config: null,
    // Contact Variables (selected LeadConnector variables)
    enabled_contact_variables: [],
    // Function Configurations (add_tag, remove_tag, trigger_workflow, send_sms)
    function_configs: {},
    // Auto-add call summary to LeadConnector contact notes
    auto_summary_to_notes: false,
  });
  
  // Transfer Call Modal State - Now supports multiple destinations (max 5)
  const [showTransferCallModal, setShowTransferCallModal] = useState(false);
  const [editingDestinationIndex, setEditingDestinationIndex] = useState(null); // null = adding new, number = editing existing
  const [transferDestinations, setTransferDestinations] = useState([]);
  const MAX_TRANSFER_DESTINATIONS = 5;

  // Functions tab — collapsed category keys (empty set = all expanded)
  const [collapsedFunctionCategories, setCollapsedFunctionCategories] = useState(() => new Set());
  const toggleFunctionCategory = (cat) => {
    setCollapsedFunctionCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };
  
  const defaultDestination = {
    phone_number: '',
    name: '',
    description: '',
    whisper_enabled: true,
    schedule_enabled: false,
    schedule: {
      same_hours_all_days: true,
      start_time: '09:00',
      end_time: '17:00',
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      custom_hours: {
        monday: { enabled: true, start: '09:00', end: '17:00' },
        tuesday: { enabled: true, start: '09:00', end: '17:00' },
        wednesday: { enabled: true, start: '09:00', end: '17:00' },
        thursday: { enabled: true, start: '09:00', end: '17:00' },
        friday: { enabled: true, start: '09:00', end: '17:00' },
        saturday: { enabled: false, start: '09:00', end: '17:00' },
        sunday: { enabled: false, start: '09:00', end: '17:00' },
      }
    },
    outside_hours_message: 'Our team is currently unavailable. Please call back during business hours.',
  };
  
  const [currentDestination, setCurrentDestination] = useState({ ...defaultDestination });
  
  // Opportunity Config State
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [opportunityMappings, setOpportunityMappings] = useState([]); // max 3
  const [currentOpportunityMapping, setCurrentOpportunityMapping] = useState({ pipeline_id: '', pipeline_name: '', stage_id: '', stage_name: '', description: '' });
  const [editingOpportunityIndex, setEditingOpportunityIndex] = useState(null);
  const [ghlPipelines, setGhlPipelines] = useState([]);
  const [loadingGhlPipelines, setLoadingGhlPipelines] = useState(false);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const MAX_OPPORTUNITY_MAPPINGS = 3;
  
  // Agents with phone numbers (for transfer_call)
  const [agentsWithPhones, setAgentsWithPhones] = useState([]);
  
  // Testing state
  const [testMode, setTestMode] = useState('voice'); // 'voice', 'chat', or 'callme'
  const [voiceEngine, setVoiceEngine] = useState('advanced'); // 'advanced' (LiveKit) or 'standard' (MediaRecorder)
  const [isTestActive, setIsTestActive] = useState(false);
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState('');
  const [toolCalls, setToolCalls] = useState([]);
  const [testError, setTestError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Available voice agents for chat-to-voice handoff
  const [availableVoiceAgents, setAvailableVoiceAgents] = useState([]);
  // Available chat agents for the Switch-to-SMS handoff (so the SMS
  // continuation can be handled by a different, SMS-optimized agent).
  const [availableChatAgents, setAvailableChatAgents] = useState([]);
  
  // ElevenLabs voices state
  const voicePlayer = useVoiceSample();
  const [resolvedVoice, setResolvedVoice] = useState(null);
  const [voiceMetadataError, setVoiceMetadataError] = useState('');
  const [showVoiceLibraryModal, setShowVoiceLibraryModal] = useState(false);
  
  // Phone Numbers state
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState([]);
  
  // LeadConnector State
  const [ghlConnected, setGhlConnected] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [promptFields, setPromptFields] = useState({ standard: [], custom: [] });
  const [promptCustomValues, setPromptCustomValues] = useState([]);
  const [promptVarsSynced, setPromptVarsSynced] = useState(null);

  const loadPromptVars = async () => {
    try {
      const [f, v] = await Promise.all([
        api.get(`/api/sub-accounts/${subAccountId}/ghl/custom-fields`),
        api.get(`/api/sub-accounts/${subAccountId}/ghl/custom-values`),
      ]);
      setPromptFields({ standard: f.data.standard_fields || [], custom: f.data.custom_fields || [] });
      setPromptCustomValues(v.data.custom_values || []);
      setPromptVarsSynced(f.data.last_synced || v.data.last_synced || null);
    } catch { /* CRM optional */ }
  };

  useEffect(() => { loadPromptVars(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subAccountId]);

  const resyncPromptVars = async () => {
    try {
      await Promise.allSettled([
        api.post(`/api/sub-accounts/${subAccountId}/ghl/custom-fields/refresh`),
        api.post(`/api/sub-accounts/${subAccountId}/ghl/custom-values/refresh`),
      ]);
      await loadPromptVars();
      toast.success('Fields & values resynced');
    } catch {
      toast.error('Resync failed — is your CRM connected?');
    }
  };
  const [ghlCalendars, setGhlCalendars] = useState([]);
  const [loadingGhlCalendars, setLoadingGhlCalendars] = useState(false);
  
  // LeadConnector Function Config State
  const [ghlTags, setGhlTags] = useState([]);
  const [ghlWorkflows, setGhlWorkflows] = useState([]);
  const [loadingGhlTags, setLoadingGhlTags] = useState(false);
  const [loadingGhlWorkflows, setLoadingGhlWorkflows] = useState(false);
  const [showFuncConfigModal, setShowFuncConfigModal] = useState(null); // which function's modal is open
  const [editingSmsOption, setEditingSmsOption] = useState(false);
  const [ghlCustomFields, setGhlCustomFields] = useState([]);
  const [loadingGhlCustomFields, setLoadingGhlCustomFields] = useState(false);
  const [syncingGhlCustomFields, setSyncingGhlCustomFields] = useState(false);
  const [tagCreateRequest, setTagCreateRequest] = useState(0); // footer "Create Tag" → opens inline editor
  const [fieldCreateRequest, setFieldCreateRequest] = useState(0); // footer "Add Field" → opens inline editor
  
  // Real-time voice testing state
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);

  // Theme classes
  const cardBg = isDarkMode ? 'bg-[#0c0d12]/90' : 'bg-white/90';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';

  useEffect(() => {
    fetchAgent();
    fetchVoiceAgents();
    fetchChatAgents();
    fetchPhoneNumbers();
    fetchGhlStatus();
  }, [agentId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [testMessages]);
  
  // Resolve old snapshots and namesake voices by ID, never by display name.
  useEffect(() => {
    voicePlayer.stop();
    setResolvedVoice(null); setVoiceMetadataError('');
    if (!config.voice_id || config.voice_engine === 'grok' || loading) return;
    const controller = new AbortController();
    api.get(`/api/sub-accounts/${subAccountId}/voice-library/voices/${encodeURIComponent(config.voice_id)}?voice_model=${encodeURIComponent(config.voice_model || '')}`, { signal: controller.signal })
      .then(res => {
        if (controller.signal.aborted) return;
        if (!res.data?.voice_id || !res.data?.name) throw new Error('Incomplete voice details');
        setResolvedVoice({ requestedId: config.voice_id, ...res.data });
      })
      .catch(err => { if (!controller.signal.aborted) setVoiceMetadataError(err?.response?.data?.detail || 'Voice details could not be verified. Reopen Change Voice to retry.'); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.voice_id, config.voice_model, config.voice_engine, subAccountId, loading]);
  
  const fetchPhoneNumbers = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/phone-numbers`);
      const numbers = Array.isArray(response.data) ? response.data : (response.data.phone_numbers || []);
      setAvailablePhoneNumbers(numbers);
      return numbers;
    } catch (error) {
      console.error('Failed to fetch phone numbers:', error);
      return [];
    }
  };

  const fetchGhlStatus = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/status`);
      setGhlConnected(response.data.connected || false);
      if (response.data.connected) {
        fetchGhlCalendars();
      }
    } catch (error) {
      setGhlConnected(false);
    }
  };

  const fetchGhlCalendars = async () => {
    setLoadingGhlCalendars(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/calendars`);
      setGhlCalendars(response.data.calendars || []);
    } catch (error) {
      console.error('Failed to fetch LeadConnector calendars:', error);
    } finally {
      setLoadingGhlCalendars(false);
    }
  };

  const fetchGhlTags = async () => {
    setLoadingGhlTags(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/tags`);
      setGhlTags(response.data.tags || []);
    } catch (error) {
      console.error('Failed to fetch LeadConnector tags:', error);
    } finally {
      setLoadingGhlTags(false);
    }
  };

  const fetchGhlPipelines = async () => {
    setLoadingGhlPipelines(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/pipelines`);
      setGhlPipelines(response.data.pipelines || response.data || []);
    } catch (error) {
      console.error('Failed to fetch LeadConnector pipelines:', error);
    } finally {
      setLoadingGhlPipelines(false);
    }
  };

  // Knowledge Bases available for this sub-account — used by the Prompt tab's
  // KB selector so users can attach KBs and auto-insert the {{knowledge_base}}
  // variable into the system prompt without digging through the KB manager.
  const [availableKBs, setAvailableKBs] = useState([]);
  const [loadingKBs, setLoadingKBs] = useState(false);
  const fetchKBs = async () => {
    if (!subAccountId) return;
    setLoadingKBs(true);
    try {
      const resp = await api.get(`/api/sub-accounts/${subAccountId}/knowledge-base`);
      setAvailableKBs(Array.isArray(resp.data) ? resp.data : (resp.data?.entries || []));
    } catch (err) {
      console.error('Failed to fetch knowledge base:', err);
    } finally {
      setLoadingKBs(false);
    }
  };
  useEffect(() => { if (subAccountId) fetchKBs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subAccountId]);
  // Auto-reveal the multilingual section if the loaded agent already has
  // additional languages (never auto-closes — the user controls that).
  useEffect(() => {
    if ((config.additional_languages?.length || 0) > 0) setMultilingualOpen(true);
  }, [config.additional_languages]);


  // Auto-fetch LeadConnector tags / workflows when add_tag/remove_tag/trigger_workflow modal opens
  useEffect(() => {
    if ((showFuncConfigModal === 'add_tag' || showFuncConfigModal === 'remove_tag') && ghlTags.length === 0 && !loadingGhlTags && subAccountId) {
      fetchGhlTags();
    }
    if (['trigger_workflow', 'schedule_callback', 'schedule_textback'].includes(showFuncConfigModal) && ghlWorkflows.length === 0 && !loadingGhlWorkflows && subAccountId) {
      fetchGhlWorkflows();
    }
    if (showFuncConfigModal === 'update_custom_field' && ghlCustomFields.length === 0 && !loadingGhlCustomFields && subAccountId) {
      fetchGhlCustomFields();
    }
    setTagCreateRequest(0);
    setFieldCreateRequest(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFuncConfigModal]);

  // Auto-fetch LeadConnector pipelines when the opportunity-stage modal opens
  useEffect(() => {
    if (showOpportunityModal && ghlPipelines.length === 0 && !loadingGhlPipelines && subAccountId) {
      fetchGhlPipelines();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOpportunityModal]);

  const fetchGhlWorkflows = async () => {
    setLoadingGhlWorkflows(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/workflows`);
      setGhlWorkflows(response.data.workflows || []);
    } catch (error) {
      console.error('Failed to fetch LeadConnector workflows:', error);
    } finally {
      setLoadingGhlWorkflows(false);
    }
  };

  const fetchGhlCustomFields = async () => {
    setLoadingGhlCustomFields(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/custom-fields`);
      setGhlCustomFields(response.data.custom_fields || []);
    } catch (error) {
      console.error('Failed to fetch LeadConnector custom fields:', error);
    } finally {
      setLoadingGhlCustomFields(false);
    }
  };

  const syncGhlCustomFields = async () => {
    setSyncingGhlCustomFields(true);
    try {
      await api.post(`/api/sub-accounts/${subAccountId}/ghl/custom-fields/refresh`);
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/custom-fields`);
      setGhlCustomFields(response.data.custom_fields || []);
      toast.success(`Synced ${(response.data.custom_fields || []).length} custom fields from LeadConnector`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed — is LeadConnector connected?');
    } finally {
      setSyncingGhlCustomFields(false);
    }
  };

  const updateFuncConfig = (funcId, key, value) => {
    const updated = { ...config.function_configs, [funcId]: { ...(config.function_configs?.[funcId] || {}), [key]: value } };
    updateConfig('function_configs', updated);
  };

  const fetchAgent = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/agents/${agentId}`);
      // Editor works on the DRAFT (draft overlays published values); the live
      // agent keeps running the published config until Publish is hit.
      const agentData = { ...response.data, ...(response.data.draft_config || {}) };
      setAgent(agentData);
      setHasUnpublished(!!response.data.has_unpublished_changes);
      setPublishedAt(response.data.published_at || null);
      
      // Initialize config from agent data
      const initialFunctions = {};
      GHL_FUNCTIONS.forEach(f => {
        initialFunctions[f.id] = agentData.functions?.includes(f.id) || f.defaultEnabled;
      });
      // Legacy Intelligence-tab detection flags now live on the function toggles
      if (agentData.callback_detection) initialFunctions.schedule_callback = true;
      if (agentData.textback_detection) initialFunctions.schedule_textback = true;
      loadedPromptLenRef.current = (agentData.system_prompt || '').length;
      setConfig({
        name: agentData.name || '',
        status: agentData.status || 'active',
        activation_tag: agentData.activation_tag || `vox_${agentData.agent_id}`,
        description: agentData.description || '',
        type: agentData.type || 'voice',        direction: agentData.direction || 'both',
        language: agentData.language || 'en',
        voice_id: agentData.voice_id || 'jessica',
        voice_meta: agentData.voice_meta || null,
        voice_speed: agentData.voice_speed ?? 1.0,
        voice_provider: agentData.voice_provider || 'elevenlabs',
        voice_model: agentData.voice_model || 'eleven_flash_v2_5',
        voice_engine: 'elevenlabs', // Grok engine removed from UI — legacy agents silently migrate
        grok_voice: agentData.grok_voice || 'Eve',
        grok_speed: agentData.grok_speed ?? 1.0,
        grok_temperature: agentData.grok_temperature ?? 0.8,
        grok_vad_threshold: agentData.grok_vad_threshold ?? 0.6,
        grok_silence_duration_ms: agentData.grok_silence_duration_ms ?? 800,
        grok_prefix_padding_ms: agentData.grok_prefix_padding_ms ?? 200,
        turn_taking: agentData.turn_taking || 'normal',
        voice_stability: agentData.voice_stability || 'natural',
        voice_v4_stability: agentData.voice_v4_stability ?? 0.5,
        voice_expression_tags: agentData.voice_expression_tags || [],
        voice_similarity: agentData.voice_similarity ?? 0.8,
        voice_sensitivity: agentData.voice_sensitivity ?? 50,
        ai_model: agentData.ai_model === 'claude-fable-5' ? 'claude-sonnet-5' : (agentData.ai_model || 'claude-sonnet-5.5'),
        chat_model: agentData.chat_model === 'claude-fable-5' ? 'claude-sonnet-5' : (agentData.chat_model || ''),        temperature: agentData.temperature ?? 0.6,
        max_call_duration: agentData.max_call_duration ?? 30,
        additional_languages: agentData.additional_languages || [],
        system_prompt: renameFirstTaskVars(agentData.system_prompt),        chat_prompt: renameFirstTaskVars(agentData.chat_prompt),        chat_prompt_enabled: !!agentData.chat_prompt_enabled,        first_message: agentData.first_message || '',
        outbound_greeting: agentData.outbound_greeting || '',
        personality_traits: Array.isArray(agentData.personality_traits) ? agentData.personality_traits.join(', ') : (agentData.personality_traits || ''),
        style_guardrails: Array.isArray(agentData.style_guardrails) ? agentData.style_guardrails.join(', ') : (agentData.style_guardrails || ''),
        response_guidelines: Array.isArray(agentData.response_guidelines) ? agentData.response_guidelines.join(', ') : (agentData.response_guidelines || ''),
        functions: initialFunctions,
        outbound_voice_agent_id: agentData.outbound_voice_agent_id || '',
        knowledge_base_ids: agentData.knowledge_base_ids || [],
        // New fields
        assigned_phone_number_id: agentData.assigned_phone_number_id || '',
        recording_disclosure: agentData.recording_disclosure ?? true,
        recording_disclosure_message: agentData.recording_disclosure_message || '',
        ai_speaks_first: agentData.direction === 'ivr' ? true : (agentData.ai_speaks_first ?? true),
        pre_tool_speech: agentData.pre_tool_speech ?? true,
        pre_tool_speech_overrides: agentData.pre_tool_speech_overrides || {},
        tool_call_sounds: agentData.tool_call_sounds || {},
        silence_timeout: agentData.silence_timeout ?? 120,
        background_noise: agentData.background_noise || 'call_center',
        background_noise_volume: agentData.background_noise_volume ?? 0.38,
        rules_of_engagement: agentData.direction === 'ivr' ? 'ai_initiates' : (agentData.rules_of_engagement || 'ai_initiates'),
        voicemail_detection: agentData.voicemail_detection ?? true,
        voicemail_message: agentData.voicemail_message || '',
        voicemail_action: agentData.voicemail_action || 'hang_up',
        inbound_ring_count: agentData.inbound_ring_count ?? 1,
        incoming_call_webhook: agentData.incoming_call_webhook || '',
        post_call_webhook: agentData.post_call_webhook || '',
        ghl_calendars: agentData.ghl_calendars || [],
        calendar_descriptions: agentData.calendar_descriptions || {},
        contact_intelligence: agentData.contact_intelligence ?? false,
        interaction_memory: agentData.interaction_memory ?? agentData.contact_intelligence ?? false,
        inbound_first_task: agentData.inbound_first_task || '',
        outbound_first_task: agentData.outbound_first_task || '',
        timezone: agentData.timezone || 'America/New_York',
        // Chat Channel Settings - Load from database
        chat_enabled: agentData.chat_enabled ?? false,
        chat_channels: agentData.chat_channels || {
          whatsapp: false,
          sms: false,
          instagram_dm: false,
          facebook_messenger: false,
        },
        can_trigger_outbound_call: !!initialFunctions.trigger_outbound_call,
        can_switch_to_sms: agentData.can_switch_to_sms ?? false,
        switch_to_sms_agent_id: agentData.switch_to_sms_agent_id || '',
        switch_to_sms_description: agentData.switch_to_sms_description || '',
        chat_response_style: agentData.chat_response_style || 'conversational',
        outbound_call_description: agentData.outbound_call_description || '',
        // Human-like Texting Settings
        misspelling_enabled: agentData.misspelling_enabled ?? false,
        misspelling_percentage: agentData.misspelling_percentage ?? 5,
        response_wait_time: agentData.response_wait_time ?? 0,
        randomize_delay: agentData.randomize_delay ?? false,
        double_texting_enabled: agentData.double_texting_enabled ?? false,
        // Transfer Call Configuration - now supports multiple destinations
        transfer_call_config: agentData.transfer_call_config || null,
        // Contact Variables
        enabled_contact_variables: agentData.enabled_contact_variables || [],
        // Function Configurations
        function_configs: (() => {
          const fc = agentData.function_configs || {};
          // Migrate old single-tag format to multi-tag format
          if (fc.add_tag && !fc.add_tag.tags) {
            if (fc.add_tag.tag_name) {
              fc.add_tag = { tags: [{ tag_name: fc.add_tag.tag_name, description: fc.add_tag.description || '' }] };
            } else {
              fc.add_tag = { tags: [] };
            }
          }
          return fc;
        })(),
        // Auto-summary to LeadConnector notes
        auto_summary_to_notes: agentData.auto_summary_to_notes ?? false,
        // Smart Callback Detection
        callback_detection: agentData.callback_detection ?? false,
        callback_max_days: agentData.callback_max_days ?? 3,
        callback_rejection_prompt: agentData.callback_rejection_prompt ?? '',
        callback_missed_workflow_id: agentData.callback_missed_workflow_id ?? '',
        // Smart Textback Detection
        textback_detection: agentData.textback_detection ?? false,
        textback_workflow_id: agentData.textback_workflow_id ?? '',
        campaign_label: agentData.campaign_label ?? '',
      });
      
      // Load transfer destinations from config
      if (agentData.transfer_call_config) {
        // Support both old single-destination format and new multi-destination format
        if (Array.isArray(agentData.transfer_call_config.destinations)) {
          setTransferDestinations(agentData.transfer_call_config.destinations);
        } else if (agentData.transfer_call_config.phone_number) {
          // Migrate old single-destination format to new array format
          setTransferDestinations([agentData.transfer_call_config]);
        }
      }

      // Load opportunity mappings from config
      if (agentData.opportunity_config?.mappings) {
        setOpportunityMappings(agentData.opportunity_config.mappings);
      }

      // Fetch agents with phone numbers for transfer destinations
      try {
        const agentsRes = await api.get(`/api/sub-accounts/${subAccountId}/agents-with-phones`);
        setAgentsWithPhones(agentsRes.data.filter(a => a.agent_id !== agentId));
      } catch (err) {
        console.error('Failed to fetch agents with phones:', err);
      }

      // Sync phone number assignment from phone_numbers collection
      const numbers = await fetchPhoneNumbers();
      const assignedPhone = numbers.find(n => n.agent_id === agentId);
      if (assignedPhone && !agentData.assigned_phone_number_id) {
        setConfig(prev => ({ ...prev, assigned_phone_number_id: assignedPhone.phone_id }));
      }
    } catch (error) {
      toast.error('Failed to load agent');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVoiceAgents = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/agents?type=voice`);
      setAvailableVoiceAgents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch voice agents:', error);
    }
  };

  const fetchChatAgents = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/agents?type=chat`);
      setAvailableChatAgents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch chat agents:', error);
    }
  };

  const getCurrentVoiceMeta = () => {
    if (resolvedVoice?.requestedId === config.voice_id) return resolvedVoice;
    if (config.voice_meta?.voice_id === config.voice_id) return config.voice_meta;
    const vid = (config.voice_id || '').toLowerCase();
    const pv = PREMIUM_VOICES.find(v => v.id === vid || v.name.toLowerCase() === vid);
    if (pv) return { voice_id: config.voice_id, name: pv.name, gender: pv.gender, accent: pv.accent, descriptive: pv.style, description: pv.description, preview_url: '' };
    return { voice_id: config.voice_id, name: config.voice_id || 'No voice selected', preview_url: '' };
  };

  const handleVoiceSelected = async (voice, signal) => {
    try {
      const selected = await api.post(`/api/sub-accounts/${subAccountId}/voice-library/select`, {
        voice_id: voice.voice_id,
        public_owner_id: voice.public_owner_id || '',
        name: voice.name,
        voice_model: config.voice_model,
      }, { signal });
      if (signal?.aborted) return;
      if (!selected.data?.voice_id) throw new Error('Voice ID missing');
      changeSeqRef.current += 1;
      setConfig(prev => ({
        ...prev,
        voice_id: selected.data.voice_id,
        voice_meta: { ...voice, ...selected.data },
      }));
      setHasChanges(true);
      setShowVoiceLibraryModal(false);
      toast.success(`Voice selected: ${selected.data.name || voice.name}`);
    } catch (error) {
      if (!signal?.aborted) toast.error(error?.response?.data?.detail || 'Failed to import this voice');
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => {
      const next = { ...prev, [key]: value };
      // Auto-set rules of engagement & ai_speaks_first based on call direction
      if (key === 'direction') {
        if (value === 'inbound') {
          next.rules_of_engagement = 'ai_initiates';
          next.ai_speaks_first = true;
        } else if (value === 'outbound') {
          next.rules_of_engagement = 'human_initiates';
          next.ai_speaks_first = false;
        } else if (value === 'both') {
          next.rules_of_engagement = 'auto_detect';
          next.ai_speaks_first = true;
        } else if (value === 'ivr') {
          next.rules_of_engagement = 'ai_initiates';
          next.ai_speaks_first = true;
          next.functions = { ...(prev.functions || {}), route_call: true };
          if (!(prev.system_prompt || '').trim()) next.system_prompt = IVR_PROMPT_TEMPLATE;
        }
      }
      return next;
    });
    setHasChanges(true);
  };

  // Switching the voice model changes which languages are supported.
  // Eleven v3 → 74 langs; Flash v2.5 → 32 langs. When narrowing the set
  // (v3 → Flash) we prune any selected language the new model can't speak,
  // and reset the primary language to English if it's no longer supported.
  const handleVoiceModelChange = (modelId) => {
    changeSeqRef.current += 1;
    setConfig(prev => {
      const supported = new Set(getLanguagesForModel(modelId, v4Catalog?.languages).map(l => l.code));
      const nextLang = supported.has(prev.language) ? prev.language : 'en';
      const nextAddl = (prev.additional_languages || []).filter(c => supported.has(c));
      return { ...prev, voice_model: modelId, language: nextLang, additional_languages: nextAddl };
    });
    setHasChanges(true);
  };


  // Two-way sync: when phone number is selected in Basics, also update phone_numbers collection
  const handlePhoneNumberAssign = async (phoneId) => {
    // Update the agent config
    updateConfig('assigned_phone_number_id', phoneId);
    
    try {
      // Unassign this agent from any previously assigned number
      const prevPhone = availablePhoneNumbers.find(n => n.agent_id === agentId);
      if (prevPhone && prevPhone.phone_id !== phoneId) {
        await api.put(`/api/sub-accounts/${subAccountId}/phone-numbers/${prevPhone.phone_id}`, { agent_id: '' });
      }
      // Assign the new phone number to this agent
      if (phoneId) {
        await api.put(`/api/sub-accounts/${subAccountId}/phone-numbers/${phoneId}`, { agent_id: agentId });
      }
      // Refresh phone numbers to keep state in sync
      await fetchPhoneNumbers();
    } catch (error) {
      console.error('Failed to sync phone number assignment:', error);
    }
  };

  const toggleFunction = (funcId) => {
    changeSeqRef.current += 1;
    setConfig(prev => {
      const nextEnabled = !prev.functions[funcId];
      const extra = {};
      if (funcId === 'schedule_callback') extra.callback_detection = nextEnabled;
      if (funcId === 'schedule_textback') extra.textback_detection = nextEnabled;
      if (funcId === 'trigger_outbound_call') extra.can_trigger_outbound_call = nextEnabled;
      return { ...prev, ...extra, functions: { ...prev.functions, [funcId]: nextEnabled } };
    });
    setHasChanges(true);
  };

  const handleSave = async (silent = false) => {
    const isSilent = silent === true;
    const saveSeq = changeSeqRef.current;
    setSaving(true);
    try {
      const enabledFunctions = Object.entries(config.functions)
        .filter(([_, enabled]) => enabled)
        .map(([id]) => id);
      
      await api.put(`/api/sub-accounts/${subAccountId}/agents/${agentId}?mode=draft`, {
        ...config,
        functions: enabledFunctions,
        callback_detection: !!config.functions?.schedule_callback,
        textback_detection: !!config.functions?.schedule_textback,
        can_trigger_outbound_call: !!config.functions?.trigger_outbound_call,
      });
      
      if (!isSilent) toast.success('Draft saved');
      setHasUnpublished(true);
      setLastSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      // Only clear the dirty flag if nothing changed while the save was in flight
      if (changeSeqRef.current === saveSeq) {
        loadedPromptLenRef.current = (config.system_prompt || '').length;
        setHasChanges(false);
      }
      return true;
    } catch (error) {
      if (!isSilent) toast.error('Failed to save agent');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ── Publish workflow: autosave/save = draft only; Publish pushes live ──
  const [hasUnpublished, setHasUnpublished] = useState(false);
  const [publishedAt, setPublishedAt] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [diffView, setDiffView] = useState(null); // {from, to, changes}
  const [loadingDiff, setLoadingDiff] = useState(null);

  const handleShowDiff = async (version, prevVersion) => {
    setLoadingDiff(version);
    try {
      const [curRes, prevRes] = await Promise.all([
        api.get(`/api/sub-accounts/${subAccountId}/agents/${agentId}/versions/${version}/snapshot`),
        api.get(`/api/sub-accounts/${subAccountId}/agents/${agentId}/versions/${prevVersion}/snapshot`),
      ]);
      const changes = computeSnapshotChanges(prevRes.data.snapshot || {}, curRes.data.snapshot || {});
      setDiffView({ from: prevVersion, to: version, changes });
    } catch {
      toast.error('Failed to load version diff');
    } finally {
      setLoadingDiff(null);
    }
  };

  const handlePublish = async () => {
    if (publishing) return false;
    setPublishing(true);
    try {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      if (hasChanges && !(await handleSaveRef.current(true))) {
        toast.error('Changes could not be saved. Publish was not started.');
        return false;
      }
      const res = await api.post(`/api/sub-accounts/${subAccountId}/agents/${agentId}/publish`);
      setHasUnpublished(!!res.data.has_unpublished_changes);
      setPublishedAt(res.data.published_at);
      toast.success(res.data.has_unpublished_changes
        ? `Published v${res.data.version} — newer changes are still unpublished`
        : `Published — live agent updated (v${res.data.version})`);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Publish failed');
      return false;
    } finally {
      setPublishing(false);
    }
  };

  const fetchVersions = async () => {
    setLoadingVersions(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/agents/${agentId}/versions`);
      setVersions(res.data || []);
    } catch {
      toast.error('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRestore = async (version) => {
    setRestoringVersion(version);
    try {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      setHasChanges(false);
      await api.post(`/api/sub-accounts/${subAccountId}/agents/${agentId}/versions/${version}/restore`);
      setShowVersions(false);
      await fetchAgent();
      toast.success(`Version ${version} loaded as draft — review and publish to go live`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoringVersion(null);
    }
  };

  const pendingExitRef = useRef(null);

  const handleBack = () => {
    pendingExitRef.current = `/workspace/${workspaceId}/sub-account/${subAccountId}/agents`;
    if (hasUnpublished || hasChanges) {
      setShowExitModal(true);
      return;
    }
    navigate(pendingExitRef.current);
  };

  // Intercept in-app navigation (sidebar links etc.) while unpublished
  useEffect(() => {
    if (!hasUnpublished) return;
    const onClickCapture = (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (!href.startsWith('/') || href.includes(`/agents/${agentId}`)) return;
      e.preventDefault();
      e.stopPropagation();
      pendingExitRef.current = href;
      setShowExitModal(true);
    };
    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [hasUnpublished, agentId]);

  // Warn on tab close / refresh while changes are unpublished
  useEffect(() => {
    if (!hasUnpublished) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [hasUnpublished]);

  // ── Autosave: any config change saves silently 2s after the last edit ──
  const autosaveTimer = useRef(null);
  const savingRef = useRef(false);
  const loadedPromptLenRef = useRef(0);
  const changeSeqRef = useRef(0);
  const handleSaveRef = useRef(handleSave);
  useEffect(() => { savingRef.current = saving; }, [saving]);
  useEffect(() => { handleSaveRef.current = handleSave; });
  useEffect(() => {
    if (!hasChanges || loading) return;
    changeSeqRef.current += 1;
    // Guard: a huge prompt deletion (>70% gone) never autosaves — manual Save required
    const newLen = (config.system_prompt || '').length;
    const loadedLen = loadedPromptLenRef.current || 0;
    if (loadedLen > 500 && newLen < loadedLen * 0.3) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    const fire = () => {
      if (savingRef.current) { autosaveTimer.current = setTimeout(fire, 1000); return; }
      handleSaveRef.current(true);
    };
    autosaveTimer.current = setTimeout(fire, 2000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, hasChanges]);

  // Testing functions
  const startTest = async () => {
    setIsTestActive(true);
    setTestMessages([]);
    setToolCalls([]);
    setTestError(null);
    
    // Add initial greeting
    if (config.first_message) {
      setTestMessages([{
        role: 'assistant',
        content: config.first_message,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const stopTest = () => {
    setIsTestActive(false);
  };

  const sendTestMessage = async () => {
    if (!testInput.trim() || isSending) return;
    
    const userMessage = {
      role: 'user',
      content: testInput,
      timestamp: new Date().toISOString()
    };
    
    setTestMessages(prev => [...prev, userMessage]);
    setTestInput('');
    setIsSending(true);
    setTestError(null);
    
    try {
      const response = await api.post(`/api/agents/${agentId}/test`, {
        message: testInput,
        mode: testMode,
        system_prompt: config.chat_prompt_enabled && (config.chat_prompt || '').trim() ? config.chat_prompt : config.system_prompt,
        functions: Object.entries(config.functions)
          .filter(([_, enabled]) => enabled)
          .map(([id]) => id),
      });
      
      // Add assistant response
      setTestMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      }]);
      
      // Add tool calls if any
      if (response.data.tool_calls?.length > 0) {
        setToolCalls(prev => [...prev, ...response.data.tool_calls.map(tc => ({
          ...tc,
          timestamp: new Date().toISOString()
        }))]);
      }
    } catch (error) {
      setTestError(error.response?.data?.detail || 'Failed to get response');
      setTestMessages(prev => [...prev, {
        role: 'error',
        content: error.response?.data?.detail || 'Failed to get AI response',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsSending(false);
    }
  };

  const tabs = [
    { id: 'basics', label: 'Basics', icon: Settings },
    { id: 'greetings', label: 'Greetings', icon: MessageSquare },
    { id: 'channels', label: 'Chat', icon: MessageSquare },
    { id: 'prompts', label: 'Knowledge', icon: FileText },
    { id: 'voice', label: 'Voice', icon: Volume2 },
    { id: 'call_settings', label: 'Call Settings', icon: Phone },
    { id: 'intelligence', label: 'Calendars', icon: Calendar },
    { id: 'functions', label: 'Functions', icon: Wrench },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  ];

  if (loading) {
    return (
      <div className={`min-h-screen ${bgMain} flex items-center justify-center`}>
        <div className="w-12 h-12 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`h-full ${bgMain} overflow-hidden flex flex-col`} data-testid="agent-editor">
      {/* Header */}
      <div className={`agent-editor-header shrink-0 z-40 ${cardBg} backdrop-blur-xl border-b ${cardBorder}`}>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10 transition-colors`}
                data-testid="editor-back-btn"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className={`text-xl font-semibold ${textPrimary}`}>
                  {config.name || 'Edit Agent'}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs ${textSecondary}`}>Agent ID:</span>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(agentId);
                      } catch {
                        const ta = document.createElement('textarea');
                        ta.value = agentId;
                        ta.style.position = 'fixed';
                        ta.style.opacity = '0';
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                      }
                      toast.success('Agent ID copied!');
                    }}
                    className={`group flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-lg ${isDarkMode ? 'bg-white/5 hover:bg-teal-400/10' : 'bg-gray-100 hover:bg-teal-50'} text-teal-400 border ${isDarkMode ? 'border-white/[0.06] hover:border-teal-400/30' : 'border-gray-200 hover:border-teal-300'} transition-all cursor-pointer`}
                    title="Click to copy Agent ID"
                    data-testid="copy-agent-id-btn"
                  >
                    {agentId}
                    <Copy size={11} className="opacity-50 group-hover:opacity-100 transition-opacity" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span data-testid="autosave-status" className={`text-[11px] ${textSecondary} flex items-center gap-1.5`}>
                {saving ? (
                  <><Loader2 size={11} className="animate-spin" /> Saving…</>
                ) : hasChanges ? (
                  <>Unsaved changes</>
                ) : lastSavedAt ? (
                  <><CheckCircle size={11} className="text-green-400" /> Saved {lastSavedAt}</>
                ) : null}
              </span>
              {hasUnpublished && !saving && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-400 border border-amber-400/30" data-testid="unpublished-pill">
                  Unpublished changes
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                  hasChanges
                    ? 'bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] hover:shadow-lg hover:shadow-teal-400/25'
                    : `${inputBg} ${textSecondary} cursor-not-allowed`
                }`}
                data-testid="save-agent-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save Changes
              </button>
              <button
                onClick={handlePublish}
                disabled={(!hasUnpublished && !hasChanges) || publishing}
                title={hasUnpublished || hasChanges ? 'Push saved changes live' : `Published${publishedAt ? ' · ' + new Date(publishedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                  hasUnpublished || hasChanges
                    ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-[#08090d] hover:shadow-lg hover:shadow-amber-400/30'
                    : `${inputBg} ${textSecondary} cursor-not-allowed opacity-70`
                }`}
                data-testid="publish-agent-btn"
              >
                {publishing ? <Loader2 size={16} className="animate-spin" /> : (hasUnpublished || hasChanges) ? <Rocket size={16} /> : <CheckCircle size={16} />}
                {(hasUnpublished || hasChanges) ? 'Publish' : 'Published'}
              </button>
              <button
                onClick={() => { setShowVersions(true); fetchVersions(); }}
                className={`p-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textSecondary} hover:text-teal-400 hover:border-teal-400/40 transition-colors`}
                title="Version history"
                data-testid="version-history-btn"
              >
                <History size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Exit guard — saved but not published */}
      {showExitModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="unpublished-exit-modal">
          <div className={`w-full max-w-md rounded-2xl ${isDarkMode ? 'bg-[#101117]' : 'bg-white'} border ${cardBorder} shadow-2xl p-6`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                <Rocket size={18} className="text-amber-400" />
              </div>
              <h3 className={`text-base font-semibold ${textPrimary}`}>Changes not published</h3>
            </div>
            <p className={`text-sm ${textSecondary} mb-5`}>
              Your changes have been <span className={textPrimary}>saved</span> — but they're only a draft. The live agent is still running the previous published version. Would you like to publish now?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowExitModal(false);
                  navigate(pendingExitRef.current || `/workspace/${workspaceId}/sub-account/${subAccountId}/agents`);
                }}
                className={`px-4 py-2 rounded-xl ${inputBg} border ${cardBorder} ${textSecondary} text-sm font-medium hover:${textPrimary}`}
                data-testid="exit-without-publish-btn"
              >
                Not right now
              </button>
              <button
                onClick={async () => {
                  const ok = await handlePublish();
                  setShowExitModal(false);
                  if (ok) navigate(pendingExitRef.current || `/workspace/${workspaceId}/sub-account/${subAccountId}/agents`);
                }}
                disabled={publishing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-[#08090d] text-sm font-semibold hover:shadow-lg hover:shadow-amber-400/30 transition-all"
                data-testid="exit-publish-btn"
              >
                {publishing ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />} Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version history */}
      {showVersions && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setShowVersions(false); setDiffView(null); }} data-testid="version-history-modal">
          <div className={`w-full ${diffView ? 'max-w-2xl' : 'max-w-lg'} max-h-[80vh] flex flex-col rounded-2xl ${isDarkMode ? 'bg-[#101117]' : 'bg-white'} border ${cardBorder} shadow-2xl`} onClick={(e) => e.stopPropagation()}>
            <div className={`px-5 py-4 border-b ${cardBorder} flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                {diffView ? (
                  <>
                    <button onClick={() => setDiffView(null)} className={`p-1 rounded ${textSecondary} hover:${textPrimary}`} data-testid="diff-back-btn">
                      <ArrowLeft size={15} />
                    </button>
                    <h3 className={`text-base font-semibold ${textPrimary}`}>Changes · v{diffView.from} → v{diffView.to}</h3>
                  </>
                ) : (
                  <>
                    <History size={16} className="text-teal-400" />
                    <h3 className={`text-base font-semibold ${textPrimary}`}>Version History</h3>
                  </>
                )}
              </div>
              <button onClick={() => { setShowVersions(false); setDiffView(null); }} className={`p-1 rounded ${textSecondary} hover:${textPrimary}`}><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {diffView ? (
                <VersionDiff changes={diffView.changes} isDarkMode={isDarkMode} textPrimary={textPrimary} textSecondary={textSecondary} cardBorder={cardBorder} />
              ) : loadingVersions ? (
                <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-teal-400" /></div>
              ) : versions.length === 0 ? (
                <p className={`text-sm ${textSecondary} text-center py-8`}>No published versions yet — hit Publish to create the first one.</p>
              ) : (
                versions.map((v, i) => (
                  <div key={v.version} className={`flex items-center gap-3 p-3 rounded-xl border ${cardBorder} ${inputBg}`} data-testid={`version-row-${v.version}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-teal-400/15 text-teal-400 border border-teal-400/30' : `${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} ${textSecondary}`}`}>
                      v{v.version}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${textPrimary} flex items-center gap-2`}>
                        {v.label || `Published ${new Date(v.published_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
                        {i === 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-teal-400/15 text-teal-400 border border-teal-400/30">LIVE</span>}
                      </div>
                      <div className={`text-[11px] ${textSecondary}`}>
                        {v.published_by_name ? `by ${v.published_by_name} · ` : ''}
                        {v.summary?.prompt_chars != null ? `${v.summary.prompt_chars.toLocaleString()} prompt chars` : ''}
                        {v.summary?.chat_model ? ` · ${v.summary.chat_model}` : ''}
                      </div>
                    </div>
                    {versions[i + 1] && (
                      <button
                        onClick={() => handleShowDiff(v.version, versions[i + 1].version)}
                        disabled={loadingDiff !== null}
                        className={`px-2.5 py-1.5 rounded-lg ${inputBg} border ${cardBorder} ${textSecondary} text-xs font-medium hover:text-teal-400 hover:border-teal-400/40 transition disabled:opacity-50`}
                        title={`Compare with v${versions[i + 1].version}`}
                        data-testid={`diff-version-${v.version}`}
                      >
                        {loadingDiff === v.version ? <Loader2 size={12} className="animate-spin" /> : 'Diff'}
                      </button>
                    )}
                    {i !== 0 && (
                      <button
                        onClick={() => handleRestore(v.version)}
                        disabled={restoringVersion !== null}
                        className="px-3 py-1.5 rounded-lg bg-teal-400/10 text-teal-400 border border-teal-400/30 text-xs font-semibold hover:bg-teal-400/20 transition disabled:opacity-50"
                        data-testid={`restore-version-${v.version}`}
                      >
                        {restoringVersion === v.version ? <Loader2 size={12} className="animate-spin" /> : 'Restore'}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className={`px-5 py-3 border-t ${cardBorder}`}>
              <p className={`text-[11px] ${textSecondary}`}>Restoring loads that version as a draft — review it, then hit Publish to make it live. Last 15 versions are kept.</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content — full-size prompt editor + right settings rail + slide-in drawer */}
      <div className="flex flex-1 min-h-0 overflow-hidden w-full relative">
        {/* Global Prompt — center stage */}
        <div className="flex-1 min-w-0 h-full p-4 lg:p-5 overflow-hidden flex flex-col" data-testid="prompt-main-area">
          <PromptEditor
            voiceModel={config.voice_engine !== 'grok' ? config.voice_model : null}
            customVoiceCues={config.voice_expression_tags || []}
            onChangeVoiceCues={(v) => updateConfig('voice_expression_tags', v)}
            firstTasks={{ inbound_first_task: config.inbound_first_task, outbound_first_task: config.outbound_first_task }}
            value={config.system_prompt}
            onChange={(v) => updateConfig('system_prompt', v)}
            chatPrompt={config.chat_prompt || ''}
            onChangeChatPrompt={(v) => updateConfig('chat_prompt', v)}
            chatPromptEnabled={!!config.chat_prompt_enabled}
            styleGuardrails={config.style_guardrails || ''}
            responseGuidelines={config.response_guidelines || ''}
            onChangeGuardrails={(v) => updateConfig('style_guardrails', v)}
            onChangeGuidelines={(v) => updateConfig('response_guidelines', v)}
            tools={GHL_FUNCTIONS.map(f => ({ id: f.id, name: f.name, description: f.description, enabled: !!(config.functions || {})[f.id] }))}
            fields={promptFields}
            customValues={promptCustomValues}
            ghlConnected={ghlConnected}
            onResync={resyncPromptVars}
            lastSynced={promptVarsSynced}
            onTest={() => { setActiveTab('test'); setDrawerOpen(true); }}
            isDarkMode={isDarkMode}
            cardBg={cardBg} cardBorder={cardBorder} inputBg={inputBg}
            textPrimary={textPrimary} textSecondary={textSecondary}
          />
        </div>

        {/* Right settings rail */}
        <div className={`w-16 shrink-0 border-l ${cardBorder} ${cardBg} flex flex-col items-center py-3 gap-1 z-30`} data-testid="settings-rail">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = drawerOpen && activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { if (active) { setDrawerOpen(false); } else { setActiveTab(tab.id); setDrawerOpen(true); } }}
                className={`w-12 py-2 rounded-xl flex flex-col items-center gap-0.5 transition-all ${
                  active ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' : `${textSecondary} hover:bg-white/5 border border-transparent`
                }`}
                title={tab.label}
                data-testid={`rail-${tab.id}`}
              >
                <Icon size={17} />
                <span className="text-[8px] font-medium leading-tight text-center">{tab.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Slide-in settings drawer */}
        <AnimatePresence>
          {drawerOpen && (
            <motion.div
              initial={{ x: '105%' }} animate={{ x: 0 }} exit={{ x: '105%' }}
              transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
              className={`${activeTab !== 'test' ? 'agent-config agent-config-drawer' : ''} absolute top-0 bottom-0 right-16 w-full max-w-[680px] z-20 ${isDarkMode ? 'bg-[#0b0c11]' : 'bg-white'} border-l ${cardBorder} shadow-2xl flex flex-col`}
              data-theme={isDarkMode ? 'dark' : 'light'}
              data-panel={activeTab}
              data-testid="settings-drawer"
            >
              <div className={`${activeTab !== 'test' ? 'agent-config-header' : ''} shrink-0 px-5 py-3 border-b ${cardBorder} flex items-center justify-between`}>
                <h2 className={`text-sm font-semibold ${textPrimary}`}>
                  {activeTab === 'test' ? 'Test Agent' : (tabs.find(t => t.id === activeTab)?.label || 'Settings')}
                </h2>
                <button onClick={() => setDrawerOpen(false)} className={`p-1.5 rounded-lg ${textSecondary} hover:bg-white/10`} data-testid="drawer-close">
                  <X size={16} />
                </button>
              </div>
              {activeTab !== 'test' && (
              <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="agent-config-body p-6" data-testid="agent-config-body">
            <AnimatePresence mode="wait">
              {/* Basics Tab */}
              {activeTab === 'basics' && (
                <motion.div
                  key="basics"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-start justify-between gap-4 mb-6">
                      <div>
                        <h3 className={`text-lg font-semibold ${textPrimary}`} data-testid="agent-identity-heading">Identity</h3>
                        <p className={`text-xs ${textSecondary} mt-1`}>How this agent appears across your dashboard and reports.</p>
                      </div>
                      <button type="button" onClick={() => updateConfig('status', config.status === 'active' ? 'paused' : 'active')}
                        data-testid="agent-status-toggle" aria-pressed={config.status === 'active'}
                        className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm font-medium transition-colors ${config.status === 'active' ? 'bg-green-400/10 border-green-400/30 text-green-400' : `${inputBg} ${cardBorder} ${textSecondary}`}`}>
                        <span className={`w-2 h-2 rounded-full ${config.status === 'active' ? 'bg-green-400' : 'bg-gray-500'}`} />
                        {config.status === 'active' ? 'Active' : 'Paused'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Agent Name</label>
                        <input type="text" value={config.name} data-testid="agent-name-input" onChange={(e) => updateConfig('name', e.target.value)}
                          className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                          placeholder="e.g., Sarah - Sales Specialist" />
                      </div>
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Campaign Label <span className="text-[10px] opacity-70">(optional)</span></label>
                        <input type="text" value={config.campaign_label || ''} onChange={(e) => updateConfig('campaign_label', e.target.value)}
                          className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                          placeholder="e.g., Spring Roofing Promo" data-testid="campaign-label-input" />
                        <p className={`text-[11px] ${textSecondary} mt-1.5`}>Used to tell campaigns apart on the dashboard and PDF reports.</p>
                      </div>
                    </div>
                  </div>

                  {/* Call Configuration */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`} data-testid="call-configuration-card">
                    <h3 className={`text-lg font-semibold ${textPrimary}`}>Call Configuration</h3>
                    <p className={`text-xs ${textSecondary} mt-1`}>Choose how this agent handles calls and which number it uses.</p>
                    <div className="space-y-6 mt-6">
                      <CallDirectionPicker value={config.direction} onChange={(v) => updateConfig('direction', v)}
                        routingHref={`/workspace/${workspaceId}/sub-account/${subAccountId}/call-routing`}
                        hasTemplate={(config.system_prompt || '').trim() === IVR_PROMPT_TEMPLATE.trim()}
                        promptEmpty={!(config.system_prompt || '').trim()}
                        onUseTemplate={() => { updateConfig('system_prompt', IVR_PROMPT_TEMPLATE); toast.success('IVR prompt template added — edit it under Prompts'); }}
                        textPrimary={textPrimary} textSecondary={textSecondary} inputBg={inputBg} cardBorder={cardBorder} />

                      {/* Phone Number Dropdown */}
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Assigned Phone Number</label>
                        {availablePhoneNumbers.length > 0 ? (
                          <select
                            data-testid="phone-number-select"
                            value={config.assigned_phone_number_id || ''}
                            onChange={(e) => handlePhoneNumberAssign(e.target.value)}
                            className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                          >
                            <option value="">Select a phone number...</option>
                            {availablePhoneNumbers.map(phone => (
                              <option key={phone.phone_id} value={phone.phone_id}>
                                {phone.friendly_name || phone.phone_number}{phone.friendly_name ? ` — ${phone.phone_number}` : ''}{phone.agent_id && phone.agent_id !== agentId ? ' (in use)' : ''}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className={`flex items-center justify-between p-3 rounded-xl ${inputBg} border ${cardBorder}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                                <Phone size={14} className={textSecondary} />
                              </div>
                              <span className={`text-sm ${textSecondary}`}>No phone numbers imported yet</span>
                            </div>
                            <Link 
                              to={`/workspace/${workspaceId}/sub-account/${subAccountId}/phone-numbers`}
                              className="text-teal-400 text-xs hover:underline flex items-center gap-1"
                              data-testid="add-phone-numbers-link"
                            >
                              Import numbers <ChevronRight size={12} />
                            </Link>
                          </div>
                        )}
                        {config.assigned_phone_number_id && (
                          <p className={`text-[11px] ${textSecondary} mt-1.5 flex items-center gap-1`}>
                            <Check size={10} className="text-teal-400" />
                            Phone number assigned and synced with Phone Numbers section.
                          </p>
                        )}
                        {!config.assigned_phone_number_id && (
                          <p data-testid="no-phone-assigned-warning" className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5 flex items-start gap-1.5">
                            <AlertTriangle size={12} className="mt-px shrink-0" />
                            No number assigned — this agent can't make or receive calls until you assign one.
                          </p>
                        )}
                      </div>

                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'greetings' && (
                <motion.div key="greetings" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6" data-testid="greetings-panel">
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <h3 className={`text-lg font-semibold ${textPrimary} mb-1`}>Greetings</h3>
                    <p className={`text-xs ${textSecondary} mb-6`}>How the AI opens each call. {config.interaction_memory ? 'Interaction Memory is on — returning contacts are greeted by name and the AI picks up where you left off.' : 'Turn on Interaction Memory to greet returning contacts by name and continue past conversations.'}</p>
                    <div className="space-y-6">
                      {/* Greeting / First Message - only for inbound or both */}
                      {['inbound', 'both', 'ivr'].includes(config.direction) && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className={`block text-sm ${textSecondary}`}>
                              {config.direction === 'both' ? 'Inbound Greeting' : 'Greeting Message'}
                            </label>
                            <button 
                              onClick={() => setExpandedPromptField('first_message')}
                            data-testid="inbound-greeting-expand"
                              className={`px-2 py-1 rounded-lg text-xs ${textSecondary} hover:bg-white/10 transition-colors`}
                            >
                              <Maximize2 size={12} />
                            </button>
                          </div>
                          <textarea
                            data-testid="greeting-message-input"
                            value={config.first_message}
                            onChange={(e) => updateConfig('first_message', e.target.value)}
                            rows={2}
                            className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50 resize-none text-sm`}
                            placeholder={config.voice_model === 'eleven_v3' 
                              ? "[cheerfully] Hey! This is Sarah from Top Gun Blades. How can I help you today?"
                              : "Hey! This is Sarah from Top Gun Blades. How can I help you today?"
                            }
                          />
                          {config.voice_model === 'eleven_v3' && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {['[cheerfully]','[slowly]','[whispers]','[laughs]','[sad]','[excited]','[serious]','[calmly]','[sighs]'].map(tag => (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => updateConfig('first_message', (config.first_message || '') + ' ' + tag + ' ')}
                                data-testid={`inbound-greeting-tag-${tag.replace(/[^a-z0-9]+/gi, '-')}`}
                                  className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
                                    isDarkMode ? 'bg-violet-400/10 text-violet-300 hover:bg-violet-400/20' : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
                                  }`}
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                          )}
                          <p className={`text-[11px] ${textSecondary} mt-1.5`}>
                            {config.direction === 'both' 
                              ? 'Used when someone calls your agent. The AI will greet them with this message.'
                              : 'The first message your agent will say when answering a call.'
                            }
                          </p>
                        </div>
                      )}

                      {['inbound', 'both', 'ivr'].includes(config.direction) && (
                        <div>
                          <label className={`block text-sm ${textSecondary} mb-2`}>Inbound First Task <span className="text-[10px] opacity-70">(optional)</span></label>
                          <textarea rows={3} value={config.inbound_first_task || ''} onChange={e => updateConfig('inbound_first_task', e.target.value)} placeholder="e.g. Ask what they need help with today and whether it's urgent." data-testid="inbound-first-task"
                            className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-teal-400/50 resize-y`} />
                          <p className={`text-[11px] ${textSecondary} mt-1.5`}>What the AI does right after greeting a caller. Replaces your prompt's opening task on inbound calls.</p>
                        </div>
                      )}

                      {/* Outbound Greeting - for outbound or both directions */}
                      {(config.direction === 'outbound' || config.direction === 'both') && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className={`block text-sm ${textSecondary}`}>
                              {config.direction === 'both' ? 'Outbound Greeting' : 'Greeting Message'}
                              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-violet-400/10 text-violet-400 font-medium">OUTBOUND</span>
                            </label>
                            <button 
                              onClick={() => setExpandedPromptField('outbound_greeting')}
                            data-testid="outbound-greeting-expand"
                              className={`px-2 py-1 rounded-lg text-xs ${textSecondary} hover:bg-white/10 transition-colors`}
                            >
                              <Maximize2 size={12} />
                            </button>
                          </div>
                          <textarea
                            data-testid="outbound-greeting-input"
                            value={config.outbound_greeting || ''}
                            onChange={(e) => updateConfig('outbound_greeting', e.target.value)}
                            rows={2}
                            className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50 resize-none text-sm`}
                            placeholder={config.voice_model === 'eleven_v3'
                              ? "[warmly] Hi {name}, this is Sarah from {company}. I'm calling regarding..."
                              : "Hi {name}, this is Sarah from {company}. I'm calling regarding..."
                            }
                          />
                          {config.voice_model === 'eleven_v3' && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {['[cheerfully]','[slowly]','[whispers]','[laughs]','[sad]','[excited]','[serious]','[calmly]','[sighs]'].map(tag => (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => updateConfig('outbound_greeting', (config.outbound_greeting || '') + ' ' + tag + ' ')}
                                data-testid={`outbound-greeting-tag-${tag.replace(/[^a-z0-9]+/gi, '-')}`}
                                  className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
                                    isDarkMode ? 'bg-violet-400/10 text-violet-300 hover:bg-violet-400/20' : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
                                  }`}
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                          )}
                          <p className={`text-[11px] ${textSecondary} mt-1.5`}>
                            Used when your AI agent makes outbound calls. Supports <code className="text-teal-400">{"{{variables}}"}</code> from the Call Me panel.
                          </p>
                        </div>
                      )}
                      {(config.direction === 'outbound' || config.direction === 'both') && (
                        <div>
                          <label className={`block text-sm ${textSecondary} mb-2`}>Outbound First Task <span className="text-[10px] opacity-70">(optional)</span></label>
                          <textarea rows={3} value={config.outbound_first_task || ''} onChange={e => updateConfig('outbound_first_task', e.target.value)} placeholder="e.g. Explain you're reaching out about their website/Facebook request and ask if they have a quick minute." data-testid="outbound-first-task"
                            className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-teal-400/50 resize-y`} />
                          <p className={`text-[11px] ${textSecondary} mt-1.5`}>What the AI does right after the contact answers. Replaces your prompt's opening task on outbound calls.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Channels Tab - Chat and SMS/WhatsApp/Social channels */}
              {activeTab === 'channels' && (
                <motion.div
                  key="channels"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  {/* Chat Assistant Toggle */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className={`text-lg font-semibold ${textPrimary}`}>Chat Assistant</h3>
                        <p className={`text-sm ${textSecondary}`}>Enable this agent to handle text-based conversations</p>
                      </div>
                      <button
                        onClick={() => {
                          const enabling = !config.chat_enabled;
                          updateConfig('chat_enabled', enabling);
                          // Auto-generate tag when enabling chat for the first time
                          if (enabling && !config.activation_tag) {
                            updateConfig('activation_tag', `vox_${config.name?.toLowerCase().replace(/[^a-z0-9]+/g, '_') || agentId}`);
                          }
                        }}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                          config.chat_enabled 
                            ? 'bg-green-400/10 text-green-400 border border-green-400/30' 
                            : `${inputBg} ${textSecondary} border ${cardBorder}`
                        }`}
                        data-testid="chat-enabled-toggle"
                      >
                        {config.chat_enabled ? 'CHAT ENABLED' : 'CHAT DISABLED'}
                      </button>
                    </div>
                    
                    {config.chat_enabled && (
                      <div className={`mt-4 space-y-4`}>
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Tag size={14} className="text-teal-400" />
                            <label className={`text-sm ${textSecondary}`}>Activation Tag</label>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              data-testid="activation-tag-input"
                              value={config.activation_tag || ''}
                              onChange={(e) => updateConfig('activation_tag', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_'))}
                              className={`flex-1 px-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} font-mono text-sm focus:outline-none focus:border-teal-400/50`}
                              placeholder="e.g., vox_sales_agent"
                            />
                            <button
                              data-testid="sync-tag-ghl-btn"
                              onClick={async () => {
                                if (!config.activation_tag) {
                                  toast.warning('Please enter an activation tag first');
                                  return;
                                }
                                const toastId = toast.loading('Syncing tag to LeadConnector...');
                                try {
                                  const resp = await api.post(`/api/sub-accounts/${subAccountId}/ghl/tags/sync`, { tag: config.activation_tag });
                                  if (resp.data.synced) {
                                    toast.success(`Tag synced to LeadConnector: ${config.activation_tag}`, { id: toastId });
                                  } else {
                                    toast.info(resp.data.message || 'Tag sync completed', { id: toastId });
                                  }
                                } catch (err) {
                                  toast.dismiss(toastId);
                                  if (err.response?.status === 400 || err.response?.status === 404) {
                                    toast.error(err.response?.data?.detail || 'LeadConnector not connected. Connect LeadConnector first.');
                                  } else {
                                    toast.error('LeadConnector sync failed. Check your connection.');
                                  }
                                }
                              }}
                              className={`px-3 py-2.5 rounded-xl text-xs font-medium transition-all bg-teal-400/10 text-teal-400 border border-teal-400/30 hover:bg-teal-400/20 whitespace-nowrap`}
                            >
                              Sync to LeadConnector
                            </button>
                          </div>
                          <p className={`text-[11px] ${textSecondary} mt-1.5`}>
                            Contacts with this tag in LeadConnector will be handled by this AI agent. Tag auto-creates in LeadConnector when synced.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Separate Chat Prompt toggle */}
                  {config.chat_enabled && (
                    <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className={`text-lg font-semibold ${textPrimary} mb-1`}>Separate Chat Prompt</h3>
                          <p className={`text-sm ${textSecondary}`}>
                            Give this agent its own prompt for texting. When off, chat uses your System Prompt.
                            Turning it off keeps your chat prompt saved — it just goes inactive.
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            const enabling = !config.chat_prompt_enabled;
                            if (enabling && !(config.chat_prompt || '').trim()) {
                              updateConfig('chat_prompt', config.system_prompt || '');
                            }
                            updateConfig('chat_prompt_enabled', enabling);
                          }}
                          className={`relative shrink-0 w-11 h-6 rounded-full transition-colors mt-1 ${config.chat_prompt_enabled ? 'bg-teal-500' : (isDarkMode ? 'bg-white/10' : 'bg-gray-300')}`}
                          data-testid="separate-chat-prompt-toggle"
                          aria-pressed={!!config.chat_prompt_enabled}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config.chat_prompt_enabled ? 'translate-x-5' : ''}`} />
                        </button>
                      </div>
                      {config.chat_prompt_enabled && (
                        <p className={`text-xs mt-3 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`} data-testid="separate-chat-prompt-hint">
                          Active — edit it under Basics via the Chat Prompt button at the top of the prompt editor.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Response Style */}
                  {config.chat_enabled && (
                    <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                      <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Response Style</h3>
                      <p className={`text-sm ${textSecondary} mb-4`}>Choose how your chat assistant responds to contacts</p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => updateConfig('chat_response_style', 'conversational')}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            (!config.chat_response_style || config.chat_response_style === 'conversational')
                              ? 'border-teal-400/50 bg-teal-400/5'
                              : `${cardBorder} ${inputBg} hover:border-white/20`
                          }`}
                          data-testid="style-conversational-btn"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare size={16} className={(!config.chat_response_style || config.chat_response_style === 'conversational') ? 'text-teal-400' : textSecondary} />
                            <span className={`text-sm font-semibold ${(!config.chat_response_style || config.chat_response_style === 'conversational') ? 'text-teal-400' : textPrimary}`}>Conversational</span>
                          </div>
                          <p className={`text-xs ${textSecondary}`}>Natural texting flow. 2-3 sentences max, friendly and straight to the point.</p>
                        </button>
                        <button
                          onClick={() => updateConfig('chat_response_style', 'concise')}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            config.chat_response_style === 'concise'
                              ? 'border-violet-400/50 bg-violet-400/5'
                              : `${cardBorder} ${inputBg} hover:border-white/20`
                          }`}
                          data-testid="style-concise-btn"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Zap size={16} className={config.chat_response_style === 'concise' ? 'text-violet-400' : textSecondary} />
                            <span className={`text-sm font-semibold ${config.chat_response_style === 'concise' ? 'text-violet-400' : textPrimary}`}>Concise</span>
                          </div>
                          <p className={`text-xs ${textSecondary}`}>Short, direct answers. 1-2 sentences max. Best for quick info and high-volume leads.</p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Chat AI Model */}
                  {config.chat_enabled && (
                    <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                      <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Chat AI Model</h3>
                      <p className={`text-sm ${textSecondary} mb-4`}>Powers chat, SMS &amp; social replies only — voice calls always use the Voice AI Model.</p>
                      <select
                        value={config.chat_model || ''}
                        onChange={(e) => updateConfig('chat_model', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                        data-testid="agent-chat-model-select"
                      >
                        <option value="">Same as voice model (default)</option>
                        <option value="claude-opus-5.5">Claude Opus 5.5 — most capable, highest cost</option>
                        <option value="claude-sonnet-5.5">Claude Sonnet 5.5 — latest, smart &amp; efficient</option>
                        <option value="claude-sonnet-5">Claude Sonnet 5 — deepest reasoning (recommended)</option>
                        <option value="claude-sonnet-4.5">Claude Sonnet 4.5 — best step-by-step discipline</option>
                        <option value="gpt-5.6-terra">GPT-5.6 Terra — latest flagship</option>
                        <option value="gpt-5.6-luna">GPT-5.6 Luna — latest, fast &amp; creative</option>
                        <option value="gpt-5.5">GPT-5.5 — advanced reasoning</option>
                        <option value="gpt-5.4">GPT-5.4 — strong all-rounder</option>
                        <option value="gpt-4o">GPT-4o — strong &amp; fast</option>
                        <option value="gpt-4o-mini">GPT-4o Mini — cheapest, simple flows only</option>
                        <option value="claude-haiku-4.5">Claude Haiku 4.5 — fast &amp; affordable</option>
                      </select>
                    </div>
                  )}

                  {/* Chat Channels */}
                  {config.chat_enabled && (
                    <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                      <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Chat Channels</h3>
                      <p className={`text-sm ${textSecondary} mb-6`}>Select which channels this agent can respond on</p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* WhatsApp */}
                        <button
                          onClick={() => setConfig(prev => ({
                            ...prev,
                            chat_channels: { ...prev.chat_channels, whatsapp: !prev.chat_channels?.whatsapp }
                          }))}
                          className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                            config.chat_channels?.whatsapp
                              ? 'bg-green-400/10 border-green-400/30'
                              : `${inputBg} ${cardBorder} hover:bg-white/5`
                          }`}
                          data-testid="channel-whatsapp"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            config.chat_channels?.whatsapp ? 'bg-green-500/20' : 'bg-green-500/10'
                          }`}>
                            <MessageSquare size={20} className="text-green-500" />
                          </div>
                          <div className="text-left">
                            <p className={`font-medium ${textPrimary}`}>WhatsApp</p>
                            <p className={`text-xs ${textSecondary}`}>
                              {config.chat_channels?.whatsapp ? 'Enabled' : 'Disabled'}
                            </p>
                          </div>
                          {config.chat_channels?.whatsapp && (
                            <CheckCircle size={16} className="text-green-400 ml-auto" />
                          )}
                        </button>

                        {/* SMS */}
                        <button
                          onClick={() => setConfig(prev => ({
                            ...prev,
                            chat_channels: { ...prev.chat_channels, sms: !prev.chat_channels?.sms }
                          }))}
                          className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                            config.chat_channels?.sms
                              ? 'bg-blue-400/10 border-blue-400/30'
                              : `${inputBg} ${cardBorder} hover:bg-white/5`
                          }`}
                          data-testid="channel-sms"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            config.chat_channels?.sms ? 'bg-blue-500/20' : 'bg-blue-500/10'
                          }`}>
                            <Phone size={20} className="text-blue-500" />
                          </div>
                          <div className="text-left">
                            <p className={`font-medium ${textPrimary}`}>SMS</p>
                            <p className={`text-xs ${textSecondary}`}>
                              {config.chat_channels?.sms ? 'Enabled' : 'Disabled'}
                            </p>
                          </div>
                          {config.chat_channels?.sms && (
                            <CheckCircle size={16} className="text-blue-400 ml-auto" />
                          )}
                        </button>

                        {/* Instagram DM */}
                        <button
                          onClick={() => setConfig(prev => ({
                            ...prev,
                            chat_channels: { ...prev.chat_channels, instagram_dm: !prev.chat_channels?.instagram_dm }
                          }))}
                          className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                            config.chat_channels?.instagram_dm
                              ? 'bg-pink-400/10 border-pink-400/30'
                              : `${inputBg} ${cardBorder} hover:bg-white/5`
                          }`}
                          data-testid="channel-instagram"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            config.chat_channels?.instagram_dm ? 'bg-pink-500/20' : 'bg-pink-500/10'
                          }`}>
                            <Camera size={20} className="text-pink-500" />
                          </div>
                          <div className="text-left">
                            <p className={`font-medium ${textPrimary}`}>Instagram DM</p>
                            <p className={`text-xs ${textSecondary}`}>
                              {config.chat_channels?.instagram_dm ? 'Enabled' : 'Disabled'}
                            </p>
                          </div>
                          {config.chat_channels?.instagram_dm && (
                            <CheckCircle size={16} className="text-pink-400 ml-auto" />
                          )}
                        </button>

                        {/* Facebook Messenger */}
                        <button
                          onClick={() => setConfig(prev => ({
                            ...prev,
                            chat_channels: { ...prev.chat_channels, facebook_messenger: !prev.chat_channels?.facebook_messenger }
                          }))}
                          className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                            config.chat_channels?.facebook_messenger
                              ? 'bg-blue-500/10 border-blue-500/30'
                              : `${inputBg} ${cardBorder} hover:bg-white/5`
                          }`}
                          data-testid="channel-facebook"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            config.chat_channels?.facebook_messenger ? 'bg-blue-600/20' : 'bg-blue-600/10'
                          }`}>
                            <MessageCircle size={20} className="text-blue-600" />
                          </div>
                          <div className="text-left">
                            <p className={`font-medium ${textPrimary}`}>FB Messenger</p>
                            <p className={`text-xs ${textSecondary}`}>
                              {config.chat_channels?.facebook_messenger ? 'Enabled' : 'Disabled'}
                            </p>
                          </div>
                          {config.chat_channels?.facebook_messenger && (
                            <CheckCircle size={16} className="text-blue-500 ml-auto" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Switch to SMS — chat-only handoff.
                      Only fires in the chat widget (not in SMS/voice), so the
                      helper text nudges the operator to reserve this for widgets. */}
                  {config.chat_enabled && (
                    <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className={`text-lg font-semibold ${textPrimary}`}>Switch to SMS</h3>
                          <p className={`text-sm ${textSecondary}`}>
                            Let the AI continue a web chat on SMS when the visitor asks to be texted.
                            <span className="text-pink-400 ml-1">Only enable if this agent powers a chat widget.</span>
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            const newValue = !config.can_switch_to_sms;
                            updateConfig('can_switch_to_sms', newValue);
                            // Auto-enable the switch_to_sms function when toggling on,
                            // and auto-disable it when toggling off.
                            if (newValue && !config.functions.switch_to_sms) {
                              toggleFunction('switch_to_sms');
                            } else if (!newValue && config.functions.switch_to_sms) {
                              toggleFunction('switch_to_sms');
                            }
                          }}
                          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                            config.can_switch_to_sms
                              ? 'bg-pink-400/10 text-pink-400 border border-pink-400/30'
                              : `${inputBg} ${textSecondary} border ${cardBorder}`
                          }`}
                          data-testid="switch-to-sms-toggle"
                        >
                          {config.can_switch_to_sms ? 'ENABLED' : 'DISABLED'}
                        </button>
                      </div>

                      {config.can_switch_to_sms && (
                        <div className={`mt-4 p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
                          <div className="flex items-start gap-3">
                            <MessageCircle size={16} className="text-pink-400 mt-0.5" />
                            <div className="flex-1">
                              <p className={`text-sm ${textPrimary} mb-1`}>Chat Agent for SMS</p>
                              <p className={`text-xs ${textSecondary} mb-3`}>
                                When the widget AI hands the conversation off to SMS, the continuation is handled by this agent.
                                Leave blank to keep the same agent (this one) on the SMS thread.
                              </p>
                              <select
                                value={config.switch_to_sms_agent_id || ''}
                                onChange={(e) => updateConfig('switch_to_sms_agent_id', e.target.value)}
                                className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-pink-400/50`}
                                data-testid="switch-to-sms-agent-select"
                              >
                                <option value="">Same agent (default)</option>
                                {availableChatAgents
                                  .filter(a => a.agent_id !== agentId)
                                  .map(a => (
                                    <option key={a.agent_id} value={a.agent_id}>
                                      {a.name}
                                    </option>
                                  ))}
                              </select>

                              <div className="mt-4">
                                <p className={`text-sm ${textPrimary} mb-1`}>Trigger Description</p>
                                <p className={`text-xs ${textSecondary} mb-2`}>
                                  Describe when the AI should hand off to SMS. Acts as instructions for the AI.
                                </p>
                                <textarea
                                  value={config.switch_to_sms_description || ''}
                                  onChange={(e) => updateConfig('switch_to_sms_description', e.target.value)}
                                  rows={3}
                                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-pink-400/50 resize-none`}
                                  placeholder="e.g., When the visitor says 'text me', 'send to my phone', 'I have to run', or seems ready to leave but hasn't finished the conversation, switch to SMS so they can continue on their phone."
                                  data-testid="switch-to-sms-description"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Human-like Texting */}
                  {config.chat_enabled && (
                    <ConfigDisclosure id="chat-advanced-texting" title="Advanced texting" summary="Response timing, purposeful typos & double texting">
                      
                      <div className="space-y-5">
                        {/* Response Wait Time */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className={`text-sm font-medium ${textPrimary}`}>Response Delay</p>
                              <p className={`text-xs ${textSecondary}`}>Wait before replying to feel human — if they text again during the wait, the timer resets and the AI sends one combined reply</p>
                            </div>
                            <span className={`text-sm font-mono ${textPrimary}`}>{(config.response_wait_time || 0) >= 60 ? `${Math.floor((config.response_wait_time || 0) / 60)}m ${(config.response_wait_time || 0) % 60}s` : `${config.response_wait_time || 0}s`}</span>
                          </div>
                          <input
                            type="range"
                            min="0" max="300" step="5"
                            value={config.response_wait_time || 0}
                            onChange={(e) => updateConfig('response_wait_time', parseInt(e.target.value))}
                            className="w-full accent-teal-400"
                            data-testid="response-wait-time-slider"
                          />
                          <div className="flex justify-between mt-1">
                            <span className={`text-[10px] ${textSecondary}`}>Instant</span>
                            <span className={`text-[10px] ${textSecondary}`}>5 minutes</span>
                          </div>
                          {(config.response_wait_time || 0) > 0 && (
                            <div className="flex items-center justify-between gap-3 mt-3">
                              <div>
                                <p className={`text-xs font-medium ${textPrimary}`}>Randomize Delay</p>
                                <p className={`text-[10px] ${textSecondary}`}>Vary the wait ±30% around the set time so replies never look mechanically punctual</p>
                              </div>
                              <button
                                onClick={() => updateConfig('randomize_delay', !config.randomize_delay)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all shrink-0 ${
                                  config.randomize_delay
                                    ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                                    : `${inputBg} ${textSecondary} border ${cardBorder}`
                                }`}
                                data-testid="randomize-delay-toggle"
                              >
                                {config.randomize_delay ? 'ON' : 'OFF'}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Misspelling Toggle */}
                        <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className={`text-sm font-medium ${textPrimary}`}>Purposeful Typos</p>
                              <p className={`text-xs ${textSecondary}`}>Add occasional misspellings to seem more human</p>
                            </div>
                            <button
                              onClick={() => updateConfig('misspelling_enabled', !config.misspelling_enabled)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                config.misspelling_enabled 
                                  ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' 
                                  : `${inputBg} ${textSecondary} border ${cardBorder}`
                              }`}
                              data-testid="misspelling-toggle"
                            >
                              {config.misspelling_enabled ? 'ON' : 'OFF'}
                            </button>
                          </div>
                          {config.misspelling_enabled && (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-xs ${textSecondary}`}>Frequency</span>
                                <span className={`text-xs font-mono ${textPrimary}`}>{config.misspelling_percentage || 5}%</span>
                              </div>
                              <input
                                type="range"
                                min="1" max="25" step="1"
                                value={config.misspelling_percentage || 5}
                                onChange={(e) => updateConfig('misspelling_percentage', parseInt(e.target.value))}
                                className="w-full accent-teal-400"
                                data-testid="misspelling-percentage-slider"
                              />
                              <div className="flex justify-between mt-1">
                                <span className={`text-[10px] ${textSecondary}`}>Subtle (1%)</span>
                                <span className={`text-[10px] ${textSecondary}`}>Noticeable (25%)</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Double Texting Toggle */}
                        <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-sm font-medium ${textPrimary}`}>Double Texting</p>
                              <p className={`text-xs ${textSecondary}`}>Split long replies into multiple messages with natural pauses</p>
                            </div>
                            <button
                              onClick={() => updateConfig('double_texting_enabled', !config.double_texting_enabled)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                config.double_texting_enabled 
                                  ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' 
                                  : `${inputBg} ${textSecondary} border ${cardBorder}`
                              }`}
                              data-testid="double-texting-toggle"
                            >
                              {config.double_texting_enabled ? 'ON' : 'OFF'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </ConfigDisclosure>
                  )}

                  {/* Info when chat is disabled */}
                  {!config.chat_enabled && (
                    <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder} text-center`}>
                      <MessageSquare size={48} className={`mx-auto mb-4 ${textSecondary}`} />
                      <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Enable Chat Assistant</h3>
                      <p className={`text-sm ${textSecondary} max-w-md mx-auto`}>
                        Turn on the chat assistant above to configure channels like WhatsApp, SMS, Instagram DM, and Facebook Messenger.
                      </p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Prompts Tab - NEW: Moved here after Basics */}
              {activeTab === 'prompts' && (
                <motion.div
                  key="prompts"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  {/* Global Prompt now lives in the main editor area —
                      this drawer keeps KBs, style & response guidelines. */}
                  <>
                      {/* Knowledge Bases — replaces the old Personality Traits
                          section. Multi-select of the sub-account's KBs, with a
                          one-click "Insert variable" helper that appends
                          `Knowledge base: {{knowledge_base}}` to the prompt so
                          the AI has live KB content at runtime. */}
                      <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                        <div className="agent-kb-heading">
                          <h3 className={`text-lg font-semibold ${textPrimary}`} data-testid="agent-kb-heading">Knowledge bases</h3>
                          <span className="agent-kb-count" data-testid="agent-kb-selected-count">{(config.knowledge_base_ids || []).length} selected</span>
                        </div>
                        <p className={`text-sm ${textSecondary} mb-4`}>
                          Select the knowledge bases available to this agent.
                        </p>

                        {loadingKBs ? (
                          <p className={`text-sm ${textSecondary}`} data-testid="agent-kb-loading">Loading…</p>
                        ) : availableKBs.length === 0 ? (
                          <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder} ${textSecondary} text-sm`} data-testid="agent-kb-empty">
                            No knowledge bases yet.{' '}
                            <Link
                              to={`/workspace/${workspaceId}/sub-account/${subAccountId}/knowledge-base`}
                              className="text-teal-400 hover:underline"
                              data-testid="agent-kb-create-link"
                            >
                              Create one →
                            </Link>
                          </div>
                        ) : (
                          <div data-testid="kb-selector">
                            <KnowledgePicker entries={availableKBs} selectedIds={config.knowledge_base_ids || []}
                              isDarkMode={isDarkMode} onToggle={(kb, e) => {
                                      const ids = new Set(config.knowledge_base_ids || []);
                                      if (e.target.checked) ids.add(kb.kb_id); else ids.delete(kb.kb_id);
                                      updateConfig('knowledge_base_ids', Array.from(ids));
                                      // Auto-insert the variable the first time a KB is attached so
                                      // users don't have to remember this step.
                                      if (e.target.checked && !(config.system_prompt || '').includes('{{knowledge_base}}')) {
                                        const current = config.system_prompt || '';
                                        const sep = current.trim() ? '\n\n' : '';
                                        updateConfig('system_prompt', `${current.trimEnd()}${sep}Knowledge base: {{knowledge_base}}`);
                                      }
                              }} />
                          </div>
                        )}

                        <div className="agent-kb-token flex items-center justify-between flex-wrap gap-3 mt-5">
                          <span className={`text-xs ${textSecondary}`} data-testid="agent-kb-prompt-status">
                            {(config.system_prompt || '').includes('{{knowledge_base}}') ? '✓ Knowledge linked in prompt' : 'Not yet referenced in prompt'}
                          </span>
                          <button type="button" data-testid="insert-kb-variable-btn" onClick={() => {
                              const marker = 'Knowledge base: {{knowledge_base}}';
                              const current = config.system_prompt || '';
                              if (current.includes('{{knowledge_base}}')) return;
                              const sep = current.trim() ? '\n\n' : '';
                              updateConfig('system_prompt', `${current.trimEnd()}${sep}${marker}`);
                            }} className="agent-config-secondary-button text-xs" title="Append Knowledge base: {{knowledge_base}} to the prompt">
                            Insert variable
                          </button>
                        </div>
                      </div>
                    </>
                </motion.div>
              )}

              {/* Voice Tab */}
              {activeTab === 'voice' && (
                <motion.div
                  key="voice"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="mb-6">
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>Voice Configuration</h3>
                      <p className={`text-sm ${textSecondary}`}>Select a voice engine and fine-tune speech settings for ultra-realistic conversations.</p>
                    </div>

                    {/* Voice Engine selector removed — platform standardised on ElevenLabs.
                        Defaults to 'elevenlabs' on new agents. Legacy agents still carrying
                        voice_engine: 'grok' continue to render their saved Grok settings below
                        (read-only fallback) until the agency re-saves them. */}

                    {/* ─── GROK VOICE SETTINGS ─── */}
                    {config.voice_engine === 'grok' && (
                      <div className="space-y-6">
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-orange-400/5 border-orange-400/20' : 'bg-orange-50 border-orange-200'}`}>
                          <p className={`text-xs ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                            <strong>Grok Realtime Engine:</strong> Native speech-to-speech via WebSocket. Ultra-low latency with automatic voice activity detection and natural barge-in.
                          </p>
                        </div>
                        
                        {/* Grok Voice Picker with Preview */}
                        <div>
                          <label className={`block text-sm ${textSecondary} mb-3`}>Grok Voice</label>
                          <div className="grid grid-cols-5 gap-3">
                            {[
                              { id: 'Eve', type: 'Female', tone: 'Energetic', color: 'text-pink-400' },
                              { id: 'Ara', type: 'Female', tone: 'Warm', color: 'text-amber-400' },
                              { id: 'Rex', type: 'Male', tone: 'Confident', color: 'text-blue-400' },
                              { id: 'Sal', type: 'Neutral', tone: 'Smooth', color: 'text-emerald-400' },
                              { id: 'Leo', type: 'Male', tone: 'Authoritative', color: 'text-violet-400' },
                            ].map(voice => (
                              <button
                                key={voice.id}
                                data-testid={`grok-voice-${voice.id.toLowerCase()}`}
                                onClick={() => updateConfig('grok_voice', voice.id)}
                                className={`p-4 rounded-xl border text-center transition-all relative group ${
                                  config.grok_voice === voice.id
                                    ? 'bg-orange-400/10 border-orange-400/30 shadow-lg shadow-orange-400/5'
                                    : `${inputBg} ${cardBorder} hover:border-white/20`
                                }`}
                              >
                                <p className={`text-sm font-semibold mb-0.5 ${config.grok_voice === voice.id ? 'text-orange-400' : textPrimary}`}>{voice.id}</p>
                                <p className={`text-[10px] ${textSecondary}`}>{voice.type}</p>
                                <p className={`text-[10px] ${voice.color}`}>{voice.tone}</p>
                              </button>
                            ))}
                          </div>
                          {/* Preview Button */}
                          <GrokVoicePreview voice={config.grok_voice} speed={config.grok_speed} isDarkMode={isDarkMode} textSecondary={textSecondary} inputBg={inputBg} cardBorder={cardBorder} />
                        </div>

                        {/* Speed & Temperature - Side by Side */}
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className={`text-sm ${textSecondary}`}>Speed</label>
                              <span className={`text-sm font-mono ${textPrimary}`}>{config.grok_speed.toFixed(1)}x</span>
                            </div>
                            <input
                              data-testid="grok-speed-slider"
                              type="range"
                              min="0.5"
                              max="2.0"
                              step="0.1"
                              value={config.grok_speed}
                              onChange={(e) => updateConfig('grok_speed', parseFloat(e.target.value))}
                              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-orange-400"
                              style={{background: `linear-gradient(to right, rgb(251 146 60) 0%, rgb(251 146 60) ${((config.grok_speed - 0.5) / 1.5) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} ${((config.grok_speed - 0.5) / 1.5) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} 100%)`}}
                            />
                            <div className="flex justify-between mt-1">
                              <span className={`text-[10px] ${textSecondary}`}>0.5x</span>
                              <span className={`text-[10px] ${textSecondary}`}>1.0x</span>
                              <span className={`text-[10px] ${textSecondary}`}>2.0x</span>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className={`text-sm ${textSecondary}`}>Temperature</label>
                              <span className={`text-sm font-mono ${textPrimary}`}>{config.grok_temperature.toFixed(1)}</span>
                            </div>
                            <input
                              data-testid="grok-temperature-slider"
                              type="range"
                              min="0.0"
                              max="1.5"
                              step="0.1"
                              value={config.grok_temperature}
                              onChange={(e) => updateConfig('grok_temperature', parseFloat(e.target.value))}
                              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-orange-400"
                              style={{background: `linear-gradient(to right, rgb(251 146 60) 0%, rgb(251 146 60) ${(config.grok_temperature / 1.5) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} ${(config.grok_temperature / 1.5) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} 100%)`}}
                            />
                            <div className="flex justify-between mt-1">
                              <span className={`text-[10px] ${textSecondary}`}>Precise</span>
                              <span className={`text-[10px] ${textSecondary}`}>Balanced</span>
                              <span className={`text-[10px] ${textSecondary}`}>Creative</span>
                            </div>
                          </div>
                        </div>

                        {/* VAD Sensitivity & Silence Duration - Side by Side */}
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className={`text-sm ${textSecondary}`}>VAD Sensitivity</label>
                              <span className={`text-sm font-mono ${textPrimary}`}>{config.grok_vad_threshold.toFixed(2)}</span>
                            </div>
                            <input
                              data-testid="grok-vad-threshold-slider"
                              type="range"
                              min="0.0"
                              max="1.0"
                              step="0.05"
                              value={config.grok_vad_threshold}
                              onChange={(e) => updateConfig('grok_vad_threshold', parseFloat(e.target.value))}
                              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-orange-400"
                              style={{background: `linear-gradient(to right, rgb(251 146 60) 0%, rgb(251 146 60) ${config.grok_vad_threshold * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} ${config.grok_vad_threshold * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} 100%)`}}
                            />
                            <p className={`text-[10px] ${textSecondary} mt-1`}>Higher = filters more noise</p>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className={`text-sm ${textSecondary}`}>Silence Duration</label>
                              <span className={`text-sm font-mono ${textPrimary}`}>{config.grok_silence_duration_ms}ms</span>
                            </div>
                            <input
                              data-testid="grok-silence-duration-slider"
                              type="range"
                              min="200"
                              max="2000"
                              step="100"
                              value={config.grok_silence_duration_ms}
                              onChange={(e) => updateConfig('grok_silence_duration_ms', parseInt(e.target.value))}
                              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-orange-400"
                              style={{background: `linear-gradient(to right, rgb(251 146 60) 0%, rgb(251 146 60) ${((config.grok_silence_duration_ms - 200) / 1800) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} ${((config.grok_silence_duration_ms - 200) / 1800) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} 100%)`}}
                            />
                            <p className={`text-[10px] ${textSecondary} mt-1`}>Wait before responding</p>
                          </div>
                        </div>

                        {/* Prefix Padding */}
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className={`text-sm ${textSecondary}`}>Prefix Padding</label>
                              <span className={`text-sm font-mono ${textPrimary}`}>{config.grok_prefix_padding_ms}ms</span>
                            </div>
                            <input
                              data-testid="grok-prefix-padding-slider"
                              type="range"
                              min="100"
                              max="500"
                              step="50"
                              value={config.grok_prefix_padding_ms}
                              onChange={(e) => updateConfig('grok_prefix_padding_ms', parseInt(e.target.value))}
                              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-orange-400"
                              style={{background: `linear-gradient(to right, rgb(251 146 60) 0%, rgb(251 146 60) ${((config.grok_prefix_padding_ms - 100) / 400) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} ${((config.grok_prefix_padding_ms - 100) / 400) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} 100%)`}}
                            />
                            <p className={`text-[10px] ${textSecondary} mt-1`}>Prevents clipping word starts</p>
                          </div>
                          <div />
                        </div>
                      </div>
                    )}
                    
                    {/* ─── ELEVENLABS VOICE SETTINGS ─── */}
                    {config.voice_engine !== 'grok' && (<>
                    {/* Voice Model Selector */}
                    <div className="mb-6">
                      <VoiceModelSelect value={config.voice_model} onChange={handleVoiceModelChange} catalog={v4Catalog} isDarkMode={isDarkMode} />
                      {v4CatalogError && <div className={`mt-3 text-xs ${textSecondary}`} role="alert" data-testid="v4-catalog-error">
                        v4 language options could not be loaded. <button type="button" data-testid="v4-catalog-retry" onClick={retryV4Catalog} className="text-teal-400 underline">Retry</button>
                      </div>}
                      {isV4(config.voice_model) && !v4Catalog?.pricing_ready?.[config.voice_model] && (
                        <p data-testid="v4-pricing-pending" role="status" className={`mt-4 text-xs leading-relaxed ${textSecondary}`}>
                          Pricing approval pending. Drafts can be saved; publishing and paid voice tests are not enabled yet.
                        </p>
                      )}
                      {config.voice_model === 'eleven_v3' && (
                        <div className={`mt-3 p-3 rounded-lg border ${isDarkMode ? 'bg-violet-400/5 border-violet-400/20' : 'bg-violet-50 border-violet-200'}`}>
                          <div className={`flex items-start gap-2 mb-2.5 pb-2.5 border-b ${isDarkMode ? 'border-violet-400/15' : 'border-violet-200'}`}>
                            <Clock size={13} className={`flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`} />
                            <p className={`text-[11px] ${isDarkMode ? 'text-amber-200/90' : 'text-amber-700'}`}>
                              <strong>Higher latency:</strong> v3 is richer and more expressive but responds a bit slower than Flash v2.5 — expect a slightly longer pause before each reply. Best when natural delivery matters more than raw speed.
                            </p>
                          </div>
                          <p className={`text-xs font-medium ${isDarkMode ? 'text-violet-200' : 'text-violet-800'}`}>
                            Add emotion tags in your greeting & prompts for a more dynamic, lifelike conversation:
                          </p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {['[cheerful]', '[laughs]', '[excited]', '[whispers]', '[sighs]', '[curious]', '[warmly]', '[sarcastic]'].map(tag => (
                              <code
                                key={tag}
                                className={`text-[11px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-violet-400/15 text-violet-300' : 'bg-violet-100 text-violet-700'}`}
                              >
                                {tag}
                              </code>
                            ))}
                          </div>
                          <p className={`text-[11px] mt-2 ${isDarkMode ? 'text-violet-300/70' : 'text-violet-600'}`}>
                            Example: <span className="italic">"[cheerful] Hi there! [laughs] I'm so glad you called."</span>
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Current Voice + Change Voice */}
                    <div className="mb-6">
                      <label className={`block text-sm ${textSecondary} mb-3`}>Voice</label>
                      <SelectedVoiceCard voice={getCurrentVoiceMeta()} model={config.voice_model} player={voicePlayer}
                        onChange={() => { voicePlayer.stop(); setShowVoiceLibraryModal(true); }}
                        isDarkMode={isDarkMode} error={voiceMetadataError} />
                    </div>


                    {/* Speed & Stability - Side by Side */}
                    {isV4(config.voice_model) ? <V4VoiceControls config={config} onChange={updateConfig} textPrimary={textPrimary} textSecondary={textSecondary} /> : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        {(() => {
                          const effSpeed = Math.min(1.2, Math.max(0.7, config.voice_speed ?? 1.0));
                          return (
                            <>
                              <div className="flex items-center justify-between mb-2">
                                <label className={`text-sm ${textSecondary}`}>Speed</label>
                                <span className={`text-sm font-mono ${textPrimary}`}>{effSpeed.toFixed(2)}x</span>
                              </div>
                              <input
                                data-testid="el-speed-slider"
                                type="range"
                                min="0.7"
                                max="1.2"
                                step="0.05"
                                value={effSpeed}
                                onChange={(e) => updateConfig('voice_speed', parseFloat(e.target.value))}
                                className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-teal-400"
                                style={{background: `linear-gradient(to right, rgb(45 212 191) 0%, rgb(45 212 191) ${((effSpeed - 0.7) / 0.5) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} ${((effSpeed - 0.7) / 0.5) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} 100%)`}}
                              />
                              <div className="flex justify-between mt-1">
                                <span className={`text-[10px] ${textSecondary}`}>0.7x</span>
                                <span className={`text-[10px] ${textSecondary}`}>1.0x</span>
                                <span className={`text-[10px] ${textSecondary}`}>1.2x</span>
                              </div>
                              <p className={`text-[10px] mt-1.5 ${textSecondary}`} data-testid="el-speed-cap-note">
                                ElevenLabs conversational voices support 0.7x–1.2x. Higher values are capped at 1.2x by ElevenLabs.
                              </p>
                            </>
                          );
                        })()}
                      </div>
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Stability</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { id: 'creative', label: 'Creative' },
                            { id: 'natural', label: 'Natural' },
                            { id: 'robust', label: 'Robust' }
                          ].map(option => (
                            <button
                              key={option.id}
                              data-testid={`el-stability-${option.id}`}
                              onClick={() => updateConfig('voice_stability', option.id)}
                              className={`py-2.5 px-2 rounded-lg border text-center text-sm font-medium transition-all ${
                                config.voice_stability === option.id
                                  ? 'bg-teal-400/10 border-teal-400/30 text-teal-400'
                                  : `${inputBg} ${cardBorder} ${textSecondary} hover:border-white/20`
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    )}
                    {/* Turn-Taking & Sensitivity */}
                    <ConfigDisclosure id="voice-advanced-tuning" title="Advanced voice tuning" summary="Turn-taking, sensitivity & temperature">
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Turn-Taking</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { id: 'low', label: 'Low' },
                            { id: 'normal', label: 'Normal' },
                            { id: 'high', label: 'High' }
                          ].map(option => (
                            <button
                              key={option.id}
                              data-testid={`el-turn-${option.id}`}
                              onClick={() => updateConfig('turn_taking', option.id)}
                              className={`py-2.5 px-2 rounded-lg border text-center text-sm font-medium transition-all ${
                                config.turn_taking === option.id
                                  ? 'bg-teal-400/10 border-teal-400/30 text-teal-400'
                                  : `${inputBg} ${cardBorder} ${textSecondary} hover:border-white/20`
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                        <p className={`text-[10px] ${textSecondary} mt-1`}>How quickly the agent responds</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className={`text-sm ${textSecondary}`}>Sensitivity</label>
                          <span className={`text-sm font-mono ${textPrimary}`}>{config.voice_sensitivity || 50}%</span>
                        </div>
                        <input
                          data-testid="el-sensitivity-slider"
                          type="range"
                          min="0"
                          max="100"
                          value={config.voice_sensitivity || 50}
                          onChange={(e) => updateConfig('voice_sensitivity', parseInt(e.target.value))}
                          className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-teal-400"
                          style={{background: `linear-gradient(to right, rgb(45 212 191) 0%, rgb(45 212 191) ${config.voice_sensitivity || 50}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} ${config.voice_sensitivity || 50}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} 100%)`}}
                        />
                        <p className={`text-[10px] ${textSecondary} mt-1`}>Higher = filters more noise</p>
                      </div>
                    </div>

                    {/* Temperature */}
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className={`text-sm ${textSecondary}`}>Temperature</label>
                          <span className={`text-sm font-mono ${textPrimary}`}>{(config.temperature ?? 0.6).toFixed(1)}</span>
                        </div>
                        <input
                          data-testid="el-temperature-slider"
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={config.temperature ?? 0.6}
                          onChange={(e) => updateConfig('temperature', parseFloat(e.target.value))}
                          className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-teal-400"
                          style={{background: `linear-gradient(to right, rgb(45 212 191) 0%, rgb(45 212 191) ${(config.temperature ?? 0.6) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} ${(config.temperature ?? 0.6) * 100}%, ${isDarkMode ? '#1e293b' : '#e2e8f0'} 100%)`}}
                        />
                        <div className="flex justify-between mt-1">
                          <span className={`text-[10px] ${textSecondary}`}>Focused</span>
                          <span className={`text-[10px] ${textSecondary}`}>Creative</span>
                        </div>
                      </div>
                      <div />
                    </div>
                    </ConfigDisclosure>
                    </>)}

                    {/* AI Model */}
                    <div className="mb-6">
                      <label className={`block text-sm ${textSecondary} mb-2`}>Voice AI Model</label>
                      <select
                        value={config.ai_model}
                        onChange={(e) => updateConfig('ai_model', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                        data-testid="agent-ai-model-select"
                      >
                        <option value="claude-opus-5.5">Claude Opus 5.5 — most capable, slower replies</option>
                        <option value="claude-sonnet-5.5">Claude Sonnet 5.5 — latest, smart &amp; quick</option>
                        <option value="claude-sonnet-4.5">Claude Sonnet 4.5</option>
                        {config.type === 'chat' && (
                          <option value="claude-sonnet-5">Claude Sonnet 5 — deep reasoning, chat only</option>
                        )}
                        <option value="gpt-4o">GPT-4o</option>
                        <option value="gpt-4o-mini">GPT-4o Mini</option>
                        <option value="claude-haiku-4.5">Claude Haiku 4.5</option>
                      </select>
                      <p className={`text-xs ${textSecondary} mt-1.5`}>Powers this agent's voice calls. Chat/SMS replies use the Chat AI Model in the Channels tab (defaults to this model). Sonnet 4.5 follows multi-step prompts most reliably.</p>
                      <p data-testid="voice-ai-pricing-disclosure" className={`text-xs ${textSecondary} mt-2 leading-relaxed`}>AI model usage is billed separately at the provider’s reported cost, with no added markup.</p>
                    </div>

                    {/* Default language and optional additional languages */}
                    <div className="mb-6">
                      <label className={`block text-sm ${textSecondary} mb-2`}>Default language</label>
                      <LanguagePicker label="Default language" testId="agent-language-select" isDarkMode={isDarkMode}
                        options={getLanguagesForModel(config.voice_model, v4Catalog?.languages)} value={config.language}
                        onChange={code => { updateConfig('language', code); updateConfig('additional_languages', (config.additional_languages || []).filter(c => c !== code)); }} />
                    </div>

                    <div className="mb-6">
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <label htmlFor="voice-multilingual-switch" className={`text-sm ${textPrimary}`}>Multilingual</label>
                        <Switch id="voice-multilingual-switch" data-testid="enable-multilingual-toggle" checked={multilingualOpen}
                          className="data-[state=checked]:bg-teal-500" onCheckedChange={enabled => {
                            setMultilingualOpen(enabled); if (!enabled) updateConfig('additional_languages', []);
                          }} />
                      </div>
                      {multilingualOpen && <LanguagePicker label="Additional languages" testId="additional-languages-select" multiple
                        isDarkMode={isDarkMode} options={getLanguagesForModel(config.voice_model, v4Catalog?.languages).filter(lang => lang.code !== config.language)}
                        value={config.additional_languages || []} onChange={codes => updateConfig('additional_languages', codes)} />}
                    </div>

                  </div>
                </motion.div>
              )}

              {/* Call Settings Tab */}
              {activeTab === 'call_settings' && (
                <motion.div
                  key="call_settings"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  {/* Interaction Memory */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Users size={18} className="text-teal-400" />
                        <h3 className={`text-lg font-semibold ${textPrimary}`}>Interaction Memory</h3>
                      </div>
                      <button
                        onClick={() => updateConfig('interaction_memory', !config.interaction_memory)}
                        className={`w-12 h-6 rounded-full transition-colors ${config.interaction_memory ? 'bg-teal-400' : 'bg-white/20'}`}
                        data-testid="interaction-memory-toggle"
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${config.interaction_memory ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    <p className={`text-sm ${textSecondary}`}>
                      When enabled, the AI will have access to the last 5 interactions (call summaries and chat messages) with this contact, using past conversations as context for more personalized responses.
                    </p>
                  </div>

                  {/* Recording & AI Behavior */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Recording & AI Behavior</h3>
                    <p className={`text-sm ${textSecondary} mb-6`}>Recording, turn-taking, and call safeguards.</p>
                    
                    <div className="space-y-5">
                      {/* Recording Disclosure */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${textPrimary}`}>Recording Disclosure</p>
                          <p className={`text-sm ${textSecondary}`}>Announce "This call may be recorded" at the start of calls.</p>
                        </div>
                        <button
                          onClick={() => updateConfig('recording_disclosure', !config.recording_disclosure)}
                        data-testid="recording-disclosure-toggle" aria-label="Recording disclosure" aria-pressed={config.recording_disclosure}
                          className={`w-12 h-6 rounded-full transition-colors ${config.recording_disclosure ? 'bg-teal-400' : 'bg-white/20'}`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${config.recording_disclosure ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      
                      {config.recording_disclosure && (
                        <div className={`p-4 rounded-xl bg-amber-500/10 border border-amber-500/30`}>
                          <p className="text-sm text-amber-400">
                            <strong>Legal Note:</strong> Two-party consent states (CA, FL, IL, MD, MA, PA, etc.) require informing callers when calls are recorded. Recommended to keep this enabled for legal compliance.
                          </p>
                        </div>
                      )}
                      {config.recording_disclosure && (
                        <div data-testid="recording-disclosure-message-field">
                          <div className="flex items-center justify-between mb-2">
                            <label className={`block text-sm ${textSecondary}`}>Disclosure message</label>
                            <button type="button" onClick={() => updateConfig('recording_disclosure_message', 'This call may be recorded for quality and training purposes.')}
                              className="text-xs text-teal-400 hover:underline" data-testid="recording-disclosure-reset">Use template</button>
                          </div>
                          <textarea rows={2} value={config.recording_disclosure_message || ''} onChange={(e) => updateConfig('recording_disclosure_message', e.target.value)}
                            placeholder="This call may be recorded for quality and training purposes." data-testid="recording-disclosure-message"
                            className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50 resize-none text-sm`} />
                          <p className={`text-[11px] ${textSecondary} mt-1.5`}>Said at the start of every call: before the greeting on inbound calls and as the first line on outbound calls. Leave empty to use the template.</p>
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Tool Execution Behavior */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>Tool Execution Behavior</h3>
                      <div className="group relative">
                        <Info size={14} className={textSecondary} />
                        <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-gray-800 text-white text-xs w-48 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10`}>
                          Control how the agent handles pauses during function calls.
                        </div>
                      </div>
                    </div>
                    <p className={`text-sm ${textSecondary} mb-6`}>Control how the agent handles pauses during function calls.</p>
                    
                    <div className="space-y-5">
                      {/* Pre-Tool Speech */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${textPrimary}`}>Pre-Tool Speech</p>
                          <p className={`text-sm ${textSecondary}`}>Announce actions before executing tools to reduce perceived latency.</p>
                        </div>
                        <button
                          data-testid="pre-tool-speech-toggle"
                          onClick={() => updateConfig('pre_tool_speech', !config.pre_tool_speech)}
                          className={`w-12 h-6 rounded-full transition-colors ${config.pre_tool_speech ? 'bg-teal-400' : 'bg-white/20'}`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${config.pre_tool_speech ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </button>
                      </div>

                      {/* Per-function phrases + sounds (collapsible, collapsed by default) */}
                      {config.pre_tool_speech && (
                        <div className={`rounded-xl ${inputBg} border ${cardBorder} overflow-hidden`}>
                          <button
                            type="button"
                            data-testid="pre-tool-phrases-toggle"
                            onClick={() => setPretoolPhrasesOpen(o => !o)}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors"
                          >
                            <div className="text-left">
                              <p className={`text-xs font-medium ${textSecondary} uppercase tracking-wide`}>Customize per-function phrases &amp; sounds</p>
                              <p className={`text-[11px] ${textSecondary} mt-0.5`}>Set what the AI says before each action and an optional typing sound while it runs.</p>
                            </div>
                            <ChevronDown size={16} className={`shrink-0 ${textSecondary} transition-transform ${pretoolPhrasesOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {pretoolPhrasesOpen && (
                            <div className="divide-y divide-white/[0.06] border-t border-white/[0.06]">
                              {GHL_FUNCTIONS.filter(f => {
                                const funcEnabled = config.functions?.[f.id];
                                return funcEnabled && f.id !== 'end_call';
                              }).map(func => {
                                const override = config.pre_tool_speech_overrides?.[func.id] ?? '';
                                const defaultPhrase = PRE_TOOL_SPEECH_DEFAULTS[func.id] || '';
                                const sound = config.tool_call_sounds?.[func.id] || 'none';
                                return (
                                  <div key={func.id} className="px-4 py-3">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className={`text-sm font-medium ${textPrimary}`}>{func.name.replace(/_/g, ' ')}</span>
                                      {!override && defaultPhrase && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-400/10 text-teal-400 font-medium">AUTO</span>
                                      )}
                                      {override && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-400/10 text-violet-400 font-medium">CUSTOM</span>
                                      )}
                                    </div>
                                    <input
                                      type="text"
                                      data-testid={`pre-tool-speech-${func.id}`}
                                      value={override}
                                      onChange={(e) => {
                                        const newOverrides = { ...config.pre_tool_speech_overrides, [func.id]: e.target.value };
                                        updateConfig('pre_tool_speech_overrides', newOverrides);
                                      }}
                                      placeholder={defaultPhrase || 'AI will auto-generate a phrase...'}
                                      className={`w-full px-3 py-2 rounded-lg bg-white/[0.03] border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-teal-400/50 placeholder:text-gray-600`}
                                    />
                                    {/* Per-function tool-call sound: None / Typing */}
                                    <div className="flex items-center gap-2 mt-2">
                                      <Volume2 size={13} className={textSecondary} />
                                      <span className={`text-[11px] ${textSecondary}`}>Sound while running:</span>
                                      <div className="flex gap-1.5">
                                        {[{ id: 'none', label: 'None' }, { id: 'typing', label: 'Typing' }].map(opt => (
                                          <button
                                            key={opt.id}
                                            type="button"
                                            data-testid={`tool-sound-${func.id}-${opt.id}`}
                                            onClick={() => {
                                              const next = { ...(config.tool_call_sounds || {}) };
                                              if (opt.id === 'none') delete next[func.id];
                                              else next[func.id] = 'typing';
                                              updateConfig('tool_call_sounds', next);
                                            }}
                                            className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                                              sound === opt.id
                                                ? 'bg-teal-400/10 border-teal-400/30 text-teal-400'
                                                : `bg-white/[0.03] ${cardBorder} ${textSecondary} hover:border-white/20`
                                            }`}
                                          >
                                            {opt.label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              {GHL_FUNCTIONS.filter(f => config.functions?.[f.id] && f.id !== 'end_call').length === 0 && (
                                <div className="px-4 py-4 text-center">
                                  <p className={`text-sm ${textSecondary}`}>Enable functions in the Functions tab to customize their speech.</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Call Safeguards */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Call Safeguards</h3>
                    <p className={`text-sm ${textSecondary} mb-6`}>Protect against wasted minutes with automatic call limits.</p>
                    
                    <div className="space-y-5">
                      {/* Max Call Duration */}
                      <div>
                        <label htmlFor="call-duration-limit" className={`block text-sm ${textSecondary} mb-2`}>Max Call Duration (minutes) <span className="text-xs opacity-60">min 1 / max 120</span></label>
                        {config.max_call_duration === 0 && <p data-testid="call-duration-default-note" className={`text-xs ${textSecondary} mb-3`}>Using the default 30-minute limit.</p>}
                        <input
                          id="call-duration-limit"
                          type="number"
                          min="1"
                          max="120"
                          value={config.max_call_duration || 30}
                          onChange={(e) => updateConfig('max_call_duration', Math.min(120, Math.max(1, parseInt(e.target.value) || 30)))}
                          data-testid="call-duration-input"
                          className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                        />
                        <p className={`text-xs ${textSecondary} mt-2`}>Automatically end calls that exceed this duration.</p>
                      </div>

                      {/* Silence Timeout */}
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Silence Timeout (seconds) <span className="text-xs opacity-60">min 10 / max 300</span></label>
                        <input
                          type="number"
                          min="10"
                          max="300"
                          value={config.silence_timeout}
                          data-testid="call-silence-timeout-input"
                          onChange={(e) => updateConfig('silence_timeout', Math.min(300, Math.max(10, parseInt(e.target.value) || 120)))}
                          className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                        />
                        <p className={`text-xs ${textSecondary} mt-2`}>End call after this many seconds of silence.</p>
                      </div>

                      {/* Inbound Ring Count */}
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Inbound Ring Count <span className="text-xs opacity-60">0 to 6</span></label>
                        <input
                          data-testid="inbound-ring-count-input"
                          type="number"
                          min="0"
                          max="6"
                          value={config.inbound_ring_count ?? 1}
                          onChange={(e) => updateConfig('inbound_ring_count', Math.min(6, Math.max(0, parseInt(e.target.value) || 0)))}
                          className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                        />
                        <p className={`text-xs ${textSecondary} mt-2`}>Number of rings before the AI answers inbound calls. Set to 0 for instant answer.</p>
                      </div>
                    </div>
                  </div>

                  {/* Background Noise & Rules */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <h3 className={`text-lg font-semibold ${textPrimary} mb-6`}>Environment Settings</h3>
                    
                    <div className="space-y-5">
                      {/* Background Noise */}
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Background Noise & Volume</label>
                        <select
                          value={config.background_noise}
                          data-testid="call-background-noise-select"
                          onChange={(e) => updateConfig('background_noise', e.target.value)}
                          className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50 mb-3`}
                        >
                          <option value="off">Off</option>
                          <option value="call_center">Call Center</option>
                          <option value="office">Office</option>
                          <option value="coffee_shop">Coffee Shop</option>
                        </select>
                        {config.background_noise !== 'off' && (
                          <div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={config.background_noise_volume}
                              data-testid="call-background-volume-slider"
                              onChange={(e) => updateConfig('background_noise_volume', parseFloat(e.target.value))}
                              className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer accent-teal-400"
                            />
                            <div className="flex justify-between text-xs mt-1">
                              <span className={textSecondary}>Quiet</span>
                              <span className={textPrimary}>{config.background_noise_volume.toFixed(2)}</span>
                              <span className={textSecondary}>Loud</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Rules of Engagement - Auto-managed by Call Direction */}
                      <div>
                        <label className={`block text-sm ${textSecondary} mb-2`}>Rules of Engagement</label>
                        <div className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} flex items-center justify-between`}>
                          <span>
                            {config.rules_of_engagement === 'ai_initiates' && 'AI initiates: AI begins the conversation'}
                            {config.rules_of_engagement === 'human_initiates' && 'Human initiates: Wait for the caller to speak first'}
                            {config.rules_of_engagement === 'auto_detect' && 'Auto-detect: AI speaks first on inbound, waits on outbound'}
                            {config.rules_of_engagement === 'greeting_only' && 'Greeting only: AI says greeting then waits'}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-teal-400/10 text-teal-400 font-medium ml-2 shrink-0">AUTO-SET</span>
                        </div>
                        <p className={`text-[11px] ${textSecondary} mt-1.5`}>
                          Automatically configured based on Call Direction. Change direction in the Basics tab to update.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Voicemail Detection */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className={`text-lg font-semibold ${textPrimary}`}>Voicemail Detection & Message</h3>
                        <p className={`text-sm ${textSecondary}`}>Enable detection and set a custom voicemail message.</p>
                      </div>
                      <button
                        data-testid="voicemail-detection-toggle"
                        aria-label="Voicemail detection"
                        aria-pressed={config.voicemail_detection}
                        onClick={() => updateConfig('voicemail_detection', !config.voicemail_detection)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                          config.voicemail_detection 
                            ? 'bg-green-400/10 text-green-400 border border-green-400/30' 
                            : `${inputBg} ${textSecondary} border ${cardBorder}`
                        }`}
                      >
                        {config.voicemail_detection ? 'On' : 'Off'}
                      </button>
                    </div>
                    
                    {config.voicemail_detection && (
                      <div className="space-y-5">
                        {/* Action Choice */}
                        <div>
                          <label className={`block text-sm ${textSecondary} mb-3`}>When voicemail is detected</label>
                          <div className="flex gap-3">
                            <button
                              data-testid="voicemail-action-hangup"
                              onClick={() => updateConfig('voicemail_action', 'hang_up')}
                              className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium border transition-all ${
                                config.voicemail_action === 'hang_up'
                                  ? 'bg-red-400/10 text-red-400 border-red-400/30'
                                  : `${inputBg} ${textSecondary} border-transparent hover:border-white/10`
                              }`}
                            >
                              Hang Up Immediately
                            </button>
                            <button
                              data-testid="voicemail-action-leave-message"
                              onClick={() => updateConfig('voicemail_action', 'leave_message')}
                              className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium border transition-all ${
                                config.voicemail_action === 'leave_message'
                                  ? 'bg-teal-400/10 text-teal-400 border-teal-400/30'
                                  : `${inputBg} ${textSecondary} border-transparent hover:border-white/10`
                              }`}
                            >
                              Leave Custom Message
                            </button>
                          </div>
                        </div>

                        {/* Custom Message - only show when action is leave_message */}
                        {config.voicemail_action === 'leave_message' && (
                          <div>
                            <label className={`block text-sm ${textSecondary} mb-2`}>Custom Voicemail Message</label>
                            <textarea
                              data-testid="voicemail-message-input"
                              value={config.voicemail_message}
                              onChange={(e) => updateConfig('voicemail_message', e.target.value)}
                              rows={3}
                              className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50 resize-none`}
                              placeholder="Hi, this is Sarah from Company Name. I'm calling to follow up on your inquiry. Please call us back at your earliest convenience."
                            />
                          </div>
                        )}

                        {/* Twilio fee transparency note */}
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-400/[0.06] border border-amber-400/20">
                          <Info size={15} className="text-amber-400 mt-0.5 shrink-0" />
                          <p className="text-xs text-amber-200/90 leading-relaxed">
                            <strong className="text-amber-200">Small Twilio fee applies.</strong> Voicemail detection uses Twilio&apos;s answering-machine detection, which adds a small per-call charge (~$0.0075). We don&apos;t charge extra for this — it&apos;s Twilio&apos;s fee for a feature we integrate directly through them, billed through your connected Twilio account.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                </motion.div>
              )}

              {activeTab === 'webhooks' && (
                <motion.div
                  key="webhooks"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                  data-testid="webhooks-panel"
                >
                  {/* Auto-Summary to LeadConnector Notes */}
                  <div className={`relative z-20 p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`text-lg font-semibold ${textPrimary}`}>Auto-add Call Summary to Notes</h3>
                        <p className={`text-sm ${textSecondary} mt-1`}>Automatically post the AI call summary — the same one shown in Contact Timeline — to the contact's notes in your CRM after each call</p>
                      </div>
                      <button
                        onClick={() => updateConfig('auto_summary_to_notes', !config.auto_summary_to_notes)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold tracking-wider border transition-all ${
                          config.auto_summary_to_notes
                            ? 'bg-teal-500/20 text-teal-400 border-teal-500/50'
                            : `${inputBg} ${textSecondary} border-transparent`
                        }`}
                        data-testid="auto-summary-to-notes-toggle"
                      >
                        {config.auto_summary_to_notes ? 'ENABLED' : 'DISABLED'}
                      </button>
                    </div>
                    {config.auto_summary_to_notes && (
                      <SummaryNoteTest api={api} agentId={agentId} subAccountId={subAccountId} s={{ cardBg, cardBorder, textPrimary, textSecondary, inputBg }} />
                    )}
                  </div>

                  {/* Webhooks */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Send size={18} className="text-teal-400" />
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>Post-Call Webhook</h3>
                    </div>
                    <p className={`text-sm ${textSecondary} mb-5`}>
                      Fires automatically after every call with full call data. Works with LeadConnector, Zapier, Make, or any webhook endpoint.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className={`block text-xs font-medium ${textSecondary} mb-1.5`}>Webhook URL</label>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            data-testid="post-call-webhook-input"
                            value={config.post_call_webhook}
                            onChange={(e) => updateConfig('post_call_webhook', e.target.value)}
                            className={`flex-1 px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                            placeholder="https://services.leadconnectorhq.com/hooks/..."
                          />
                          {config.post_call_webhook && (
                            <button
                              onClick={() => updateConfig('post_call_webhook', '')}
                              data-testid="post-call-webhook-clear" aria-label="Clear webhook"
                              className="px-3 py-3 rounded-xl text-red-400 hover:bg-red-400/10 transition-colors"
                              title="Clear webhook"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          data-testid="send-test-webhook-btn"
                          onClick={async () => {
                            if (!config.post_call_webhook) {
                              toast.error('Enter a webhook URL first');
                              return;
                            }
                            try {
                              await api.post(`/api/agents/${agentId}/test-webhook`, {
                                webhook_url: config.post_call_webhook,
                              });
                              toast.success('Test data sent! Check your webhook endpoint.');
                            } catch (error) {
                              toast.error(error?.response?.data?.detail || 'Failed to send test webhook');
                            }
                          }}
                          disabled={!config.post_call_webhook}
                          className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                            config.post_call_webhook
                              ? 'bg-teal-400/10 text-teal-400 border border-teal-400/20 hover:bg-teal-400/20'
                              : `${inputBg} border ${cardBorder} ${textSecondary} opacity-50 cursor-not-allowed`
                          }`}
                        >
                          <Send size={14} className="inline mr-2" />
                          Send Test Data
                        </button>
                        {config.post_call_webhook && (
                          <span className="flex items-center gap-1 text-xs text-green-400">
                            <Check size={12} /> Active
                          </span>
                        )}
                      </div>

                      <details className={`text-xs ${textSecondary}`}>
                        <summary className="cursor-pointer hover:text-teal-400 transition-colors" data-testid="post-call-webhook-payload-toggle">
                          View payload fields
                        </summary>
                        <PostCallPayloadTable textPrimary={textPrimary} textSecondary={textSecondary} inputBg={inputBg} cardBorder={cardBorder} />
                      </details>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Intelligence Tab */}
              {activeTab === 'intelligence' && (
                <motion.div
                  key="intelligence"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  {/* Agent Timezone */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <Globe size={18} className="text-teal-400" />
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>Agent Timezone</h3>
                    </div>
                    <p className={`text-sm ${textSecondary} mb-4`}>Set the timezone for scheduling, availability checks, and time-based responses.</p>
                    <select
                      value={config.timezone}
                      onChange={(e) => updateConfig('timezone', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                      data-testid="agent-timezone-select"
                    >
                      <optgroup label="United States">
                        <option value="America/New_York">Eastern (ET/EDT)</option>
                        <option value="America/Chicago">Central (CT/CDT)</option>
                        <option value="America/Denver">Mountain (MT/MDT)</option>
                        <option value="America/Phoenix">Arizona (MST)</option>
                        <option value="America/Los_Angeles">Pacific (PT/PDT)</option>
                        <option value="America/Anchorage">Alaska (AKST/AKDT)</option>
                        <option value="Pacific/Honolulu">Hawaii (HST)</option>
                      </optgroup>
                      <optgroup label="Canada">
                        <option value="America/Toronto">Toronto / Ottawa (ET/EDT)</option>
                        <option value="America/Winnipeg">Winnipeg (CT/CDT)</option>
                        <option value="America/Edmonton">Edmonton / Calgary (MT/MDT)</option>
                        <option value="America/Vancouver">Vancouver (PT/PDT)</option>
                        <option value="America/Halifax">Halifax (AT/ADT)</option>
                        <option value="America/St_Johns">St. John's (NT/NDT)</option>
                      </optgroup>
                      <optgroup label="Mexico & Central America">
                        <option value="America/Mexico_City">Mexico City (CST)</option>
                        <option value="America/Tijuana">Tijuana (PT/PDT)</option>
                        <option value="America/Guatemala">Guatemala (CST)</option>
                        <option value="America/Panama">Panama (EST)</option>
                      </optgroup>
                      <optgroup label="South America">
                        <option value="America/Bogota">Bogotá (COT)</option>
                        <option value="America/Lima">Lima (PET)</option>
                        <option value="America/Sao_Paulo">São Paulo (BRT)</option>
                        <option value="America/Argentina/Buenos_Aires">Buenos Aires (ART)</option>
                        <option value="America/Santiago">Santiago (CLT/CLST)</option>
                      </optgroup>
                      <optgroup label="Europe">
                        <option value="Europe/London">London (GMT/BST)</option>
                        <option value="Europe/Dublin">Dublin (GMT/IST)</option>
                        <option value="Europe/Paris">Paris (CET/CEST)</option>
                        <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                        <option value="Europe/Madrid">Madrid (CET/CEST)</option>
                        <option value="Europe/Rome">Rome (CET/CEST)</option>
                        <option value="Europe/Athens">Athens (EET/EEST)</option>
                        <option value="Europe/Moscow">Moscow (MSK)</option>
                      </optgroup>
                      <optgroup label="Africa & Middle East">
                        <option value="Africa/Lagos">Lagos (WAT)</option>
                        <option value="Africa/Cairo">Cairo (EET/EEST)</option>
                        <option value="Africa/Johannesburg">Johannesburg (SAST)</option>
                        <option value="Asia/Dubai">Dubai (GST)</option>
                        <option value="Asia/Riyadh">Riyadh (AST)</option>
                        <option value="Asia/Jerusalem">Jerusalem (IST/IDT)</option>
                      </optgroup>
                      <optgroup label="Asia">
                        <option value="Asia/Kolkata">India (IST)</option>
                        <option value="Asia/Karachi">Karachi (PKT)</option>
                        <option value="Asia/Bangkok">Bangkok (ICT)</option>
                        <option value="Asia/Singapore">Singapore (SGT)</option>
                        <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                        <option value="Asia/Taipei">Taipei (CST)</option>
                        <option value="Asia/Manila">Manila (PHT)</option>
                        <option value="Asia/Shanghai">Shanghai (CST)</option>
                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                        <option value="Asia/Seoul">Seoul (KST)</option>
                      </optgroup>
                      <optgroup label="Australia">
                        <option value="Australia/Sydney">Sydney (AEDT/AEST)</option>
                        <option value="Australia/Melbourne">Melbourne (AEDT/AEST)</option>
                        <option value="Australia/Brisbane">Brisbane (AEST)</option>
                        <option value="Australia/Adelaide">Adelaide (ACDT/ACST)</option>
                        <option value="Australia/Perth">Perth (AWST)</option>
                        <option value="Australia/Darwin">Darwin (ACST)</option>
                        <option value="Australia/Hobart">Hobart (AEDT/AEST)</option>
                      </optgroup>
                      <optgroup label="New Zealand & Pacific">
                        <option value="Pacific/Auckland">Auckland (NZDT/NZST)</option>
                        <option value="Pacific/Chatham">Chatham Islands (CHADT/CHAST)</option>
                        <option value="Pacific/Fiji">Fiji (FJT/FJST)</option>
                      </optgroup>
                      <optgroup label="Reference">
                        <option value="UTC">UTC</option>
                      </optgroup>
                    </select>

                    {/* Live clock — updates every second. Auto-handles DST via Intl.
                        Confirms to the user what "now" looks like for the agent, and
                        reassures them the AI has live access to this exact value. */}
                    <LiveTimezoneClock
                      timezone={config.timezone}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      cardBorder={cardBorder}
                    />
                  </div>

                  {/* LeadConnector Calendar Selection */}
                  <div className={`p-6 rounded-2xl ${cardBg} border ${cardBorder}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar size={18} className="text-teal-400" />
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>LeadConnector Calendars</h3>
                    </div>
                    <p className={`text-sm ${textSecondary} mb-4`}>
                      Select calendars for the AI to use when booking appointments. Multi-select for routing based on appointment type.
                    </p>
                    
                    {!ghlConnected ? (
                      <div className={`p-6 rounded-xl ${inputBg} border ${cardBorder} text-center`}>
                        <Calendar size={32} className={`mx-auto ${textSecondary} mb-3`} />
                        <p className={`${textPrimary} font-medium mb-2`}>Connect LeadConnector</p>
                        <p className={`text-sm ${textSecondary} mb-4`}>
                          Link your LeadConnector account to access calendars for scheduling.
                        </p>
                        <Link
                          to={`/workspace/${workspaceId}/sub-account/${subAccountId}/integrations`}
                        data-testid="calendar-connect-integrations-link"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] font-medium hover:shadow-lg hover:shadow-teal-400/25 transition-all"
                        >
                          Connect LeadConnector
                          <ExternalLink size={14} />
                        </Link>
                      </div>
                    ) : loadingGhlCalendars ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={24} className="text-teal-400 animate-spin" />
                      </div>
                    ) : ghlCalendars.length > 0 ? (
                      <div className="space-y-2">
                        {ghlCalendars.map(calendar => {
                          const isSelected = config.ghl_calendars?.includes(calendar.id);
                          return (
                            <div
                              key={calendar.id}
                              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                                isSelected
                                  ? 'bg-teal-400/10 border-teal-400/30'
                                  : `${inputBg} ${cardBorder} hover:border-white/20`
                              }`}
                              onClick={() => {
                                const currentCalendars = config.ghl_calendars || [];
                                const newCalendars = isSelected
                                  ? currentCalendars.filter(id => id !== calendar.id)
                                  : [...currentCalendars, calendar.id];
                                updateConfig('ghl_calendars', newCalendars);
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-teal-400/20' : 'bg-white/5'}`}>
                                    <Calendar size={18} className={isSelected ? 'text-teal-400' : textSecondary} />
                                  </div>
                                  <div>
                                    <p className={`font-medium ${isSelected ? 'text-teal-400' : textPrimary}`}>{calendar.name}</p>
                                    <p className={`text-xs ${textSecondary}`}>{calendar.id}</p>
                                  </div>
                                </div>
                                {isSelected && <Check size={20} className="text-teal-400" />}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Calendar Description - Only show when multiple calendars selected */}
                        {config.ghl_calendars?.length > 1 && (
                          <div className="mt-4">
                            <label className={`block text-sm ${textSecondary} mb-2`}>Calendar Use Case Descriptions</label>
                            <p className={`text-xs ${textSecondary} mb-2`}>
                              Describe each calendar's purpose so the AI knows which to use for different appointment types.
                            </p>
                            {config.ghl_calendars.map(calId => {
                              const cal = ghlCalendars.find(c => c.id === calId);
                              return cal ? (
                                <div key={calId} className="mb-3">
                                  <label className={`block text-xs ${textSecondary} mb-1`}>{cal.name}</label>
                                  <input
                                    data-testid={`calendar-desc-${calId}`}
                                    type="text"
                                    value={(config.calendar_descriptions || {})[calId] || ''}
                                    onChange={(e) => {
                                      const descs = { ...(config.calendar_descriptions || {}) };
                                      descs[calId] = e.target.value;
                                      updateConfig('calendar_descriptions', descs);
                                    }}
                                    placeholder={`e.g., "Use for ${cal.name.toLowerCase()} appointments"`}
                                    className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50 text-sm`}
                                  />
                                </div>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder} text-center`}>
                        <p className={textSecondary}>No calendars found in your LeadConnector account.</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Functions Tab — iter134 redesign: 2-col grid, category chips, modern toggles */}
              {activeTab === 'functions' && (
                <motion.div
                  key="functions"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-5"
                >
                  {/* Header strip with live enabled count */}
                  <div className="agent-functions-intro flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-teal-400/10 flex items-center justify-center">
                        <Wrench size={16} className="text-teal-400" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${textPrimary}`}>Tools & Capabilities</p>
                        <p className={`text-xs ${textSecondary}`}>Agent actions and configurations</p>
                      </div>
                    </div>
                    <div className={`text-xs ${textSecondary}`} data-testid="agent-functions-enabled-count">
                      <span className="text-green-400 font-semibold">
                        {Object.values(config.functions || {}).filter(Boolean).length}
                      </span>
                      {' / '}
                      {GHL_FUNCTIONS.length} enabled
                    </div>
                  </div>

                  {/* Grouped by category with collapsible sections */}
                  {(() => {
                    // Category metadata — order + display label + color token
                    const CATEGORY_META = [
                      { key: 'call',          label: 'Call Control',    style: { pill: 'bg-violet-400/10 text-violet-300 border-violet-400/20',    dot: 'bg-violet-400' } },
                      { key: 'calendar',      label: 'Calendar',        style: { pill: 'bg-sky-400/10 text-sky-300 border-sky-400/20',             dot: 'bg-sky-400' } },
                      { key: 'contact',       label: 'Contact (CRM)',   style: { pill: 'bg-teal-400/10 text-teal-300 border-teal-400/20',          dot: 'bg-teal-400' } },
                      { key: 'communication', label: 'Communication',   style: { pill: 'bg-pink-400/10 text-pink-300 border-pink-400/20',          dot: 'bg-pink-400' } },
                      { key: 'automation',    label: 'Automation',      style: { pill: 'bg-amber-400/10 text-amber-300 border-amber-400/20',       dot: 'bg-amber-400' } },
                      { key: 'sales',         label: 'Sales Pipeline',  style: { pill: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/20', dot: 'bg-emerald-400' } },
                    ];

                    const groupedFns = CATEGORY_META.map(meta => ({
                      ...meta,
                      funcs: GHL_FUNCTIONS.filter(f => f.category === meta.key),
                    })).filter(g => g.funcs.length > 0);

                    return groupedFns.map(group => {
                      const isCollapsed = collapsedFunctionCategories.has(group.key);
                      const enabledInGroup = group.funcs.filter(f => config.functions[f.id]).length;

                      return (
                        <div key={group.key} className="agent-function-group">
                          {/* Category header — click to collapse/expand */}
                          <button
                            onClick={() => toggleFunctionCategory(group.key)}
                            className="agent-function-category w-full flex items-center justify-between"
                            aria-expanded={!isCollapsed}
                            data-testid={`category-header-${group.key}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider border ${group.style.pill}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${group.style.dot}`} />
                                {group.label}
                              </span>
                              <span className={`text-xs ${textSecondary}`}>
                                <span className={enabledInGroup > 0 ? 'text-green-400 font-semibold' : ''}>{enabledInGroup}</span>
                                <span className="opacity-60"> / {group.funcs.length}</span>
                              </span>
                            </div>
                            <ChevronDown
                              size={15}
                              className={`${textSecondary} transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                            />
                          </button>

                          {/* Category grid */}
                          <AnimatePresence initial={false}>
                            {!isCollapsed && (
                              <motion.div
                                key={`${group.key}-body`}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="agent-function-list">
                                  {group.funcs.map(func => {
                                    const isEnabled = !!config.functions[func.id];
                                    const isDisabledForType = func.chatOnly && !config.chat_enabled;
                                    const isTransferCall = func.id === 'transfer_call';
                                    const hasTransferConfig = transferDestinations.length > 0;

                                    return (
                                      <motion.div
                                        key={func.id}
                                        layout
                                        className={`${(isTransferCall && isEnabled && hasTransferConfig) ? 'md:col-span-2' : ''}`}
                                      >
                                      <div key={func.id}>
                                        <div
                                          onClick={() => !isDisabledForType && toggleFunction(func.id)}
                                          className={`agent-function-row group relative rounded-xl border p-5 transition-all cursor-pointer overflow-hidden ${
                                            isEnabled && !isDisabledForType
                                              ? (isDarkMode ? 'bg-gradient-to-br from-green-400/[0.08] via-transparent to-transparent border-green-400/30' : 'bg-green-50 border-green-200')
                                              : (isDarkMode ? 'bg-white/[0.025] border-white/[0.07] hover:border-white/20 hover:bg-white/[0.04]' : 'bg-white border-gray-200 hover:border-gray-300')
                                          } ${isDisabledForType ? 'opacity-50 cursor-not-allowed' : ''}`}
                                          data-testid={`function-card-${func.id}`}
                                        >
                                          <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 flex-wrap">
                                                {func.chatOnly && !func.widgetOnly && (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider border bg-amber-400/10 text-amber-300 border-amber-400/20">
                                                    Chat only
                                                  </span>
                                                )}
                                                {func.widgetOnly && (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider border bg-teal-400/10 text-teal-300 border-teal-400/20">
                                                    Widget only
                                                  </span>
                                                )}
                                                {isTransferCall && hasTransferConfig && (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider border bg-blue-400/10 text-blue-300 border-blue-400/20">
                                                    {transferDestinations.length} dest{transferDestinations.length > 1 ? 's' : ''}
                                                  </span>
                                                )}
                                              </div>

                                              <p className={`agent-function-name ${textPrimary}`} title={func.name} data-testid={`function-name-${func.id}`}>
                                                {func.name.replace(/(^|_)([a-z])/g, (_, separator, letter) => `${separator ? ' ' : ''}${letter.toUpperCase()}`).replace(/\bSms\b/g, 'SMS').replace(/\bDnd\b/g, 'DND')}
                                              </p>

                                              <p className="agent-function-description" data-testid={`function-description-${func.id}`}>
                                                {func.description}
                                              </p>
                                              {func.id === 'route_call' && isEnabled && (
                                                <CallRoutingToolPreview api={api} subAccountId={subAccountId} agentId={agentId}
                                                  routingHref={`/workspace/${workspaceId}/sub-account/${subAccountId}/call-routing`}
                                                  textPrimary={textPrimary} textSecondary={textSecondary} />
                                              )}
                                            </div>

                                            {/* Actions — Edit (if configurable) + Toggle. Both stopPropagation so they don't fire the card-click toggle. */}
                                            <div className="agent-function-controls flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                                              {(() => {
                                                const funcConfigModals = ['add_tag', 'remove_tag', 'send_sms', 'trigger_workflow', 'update_custom_field', 'schedule_callback', 'schedule_textback', 'trigger_outbound_call'];
                                                if (funcConfigModals.includes(func.id)) {
                                                  return (
                                                    <button
                                                      onClick={() => setShowFuncConfigModal(func.id)}
                                                      aria-label={`Configure ${func.name.replace(/_/g, ' ')}`}
                                                      className={`p-1.5 rounded-lg transition-colors ${textSecondary} hover:bg-white/10 hover:text-white`}
                                                      title="Configure"
                                                      data-testid={`edit-function-${func.id}`}
                                                    >
                                                      <Settings size={14} />
                                                    </button>
                                                  );
                                                }
                                                if (func.id === 'update_opportunity_stage') {
                                                  return (
                                                    <button
                                                      onClick={() => setShowOpportunityModal(true)}
                                                      aria-label="Configure opportunity mapping"
                                                      className={`p-1.5 rounded-lg transition-colors ${textSecondary} hover:bg-white/10 hover:text-white`}
                                                      title="Configure"
                                                      data-testid={`edit-function-${func.id}`}
                                                    >
                                                      <Settings size={14} />
                                                    </button>
                                                  );
                                                }
                                                if (isTransferCall) {
                                                  return (
                                                    <button
                                                      onClick={() => {
                                                        setEditingDestinationIndex(null);
                                                        setCurrentDestination({ ...defaultDestination });
                                                        setShowTransferCallModal(true);
                                                      }}
                                                      className={`p-1.5 rounded-lg transition-colors ${textSecondary} hover:bg-white/10 hover:text-white`}
                                                      title="Configure destinations"
                                                      aria-label="Configure transfer destinations"
                                                      data-testid="transfer-call-settings-btn"
                                                    >
                                                      <Settings size={14} />
                                                    </button>
                                                  );
                                                }
                                                return null;
                                              })()}

                                              <button
                                                type="button"
                                                aria-label={`Enable ${func.name.replace(/_/g, ' ')}`}
                                                role="switch"
                                                aria-checked={isEnabled}
                                                aria-disabled={isDisabledForType}
                                                onClick={() => !isDisabledForType && toggleFunction(func.id)}
                                                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${
                                                  isEnabled && !isDisabledForType
                                                    ? 'bg-green-400'
                                                    : (isDarkMode ? 'bg-white/10' : 'bg-gray-300')
                                                } ${isDisabledForType ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                data-testid={`toggle-function-${func.id}`}
                                              >
                                                <div
                                                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform ${
                                                    isEnabled && !isDisabledForType ? 'translate-x-[22px]' : 'translate-x-0.5'
                                                  }`}
                                                />
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      </motion.div>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    });
                  })()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
              </div>
              )}

              {/* Test panel inside drawer */}
              {activeTab === 'test' && (
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className={`p-3 border-b ${cardBorder} ${cardBg}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${textPrimary}`}>Test Agent</span>
                      <div className={`flex items-center gap-1 p-0.5 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
                        <button
                          onClick={() => setTestMode('voice')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                            testMode === 'voice'
                              ? 'bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-sm'
                              : `${textSecondary} ${isDarkMode ? 'hover:text-white' : 'hover:text-gray-900'}`
                          }`}
                          data-testid="test-mode-voice"
                        >
                          <Mic size={12} />
                          Voice
                        </button>
                        <button
                          onClick={() => setTestMode('chat')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                            testMode === 'chat'
                              ? 'bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-sm'
                              : `${textSecondary} ${isDarkMode ? 'hover:text-white' : 'hover:text-gray-900'}`
                          }`}
                          data-testid="test-mode-chat"
                        >
                          <MessageSquare size={12} />
                          Chat
                        </button>
                        <button
                          onClick={() => setTestMode('callme')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                            testMode === 'callme'
                              ? 'bg-gradient-to-r from-teal-400 to-cyan-500 text-white shadow-sm'
                              : `${textSecondary} ${isDarkMode ? 'hover:text-white' : 'hover:text-gray-900'}`
                          }`}
                          data-testid="test-mode-callme"
                        >
                          <PhoneOutgoing size={12} />
                          Call Me
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-hidden">
                    {testMode === 'voice' ? (
                      <AdvancedVoicePanel 
                        agentId={agentId}
                        config={config}
                        api={api}
                        isDarkMode={isDarkMode}
                        cardBg={cardBg}
                        cardBorder={cardBorder}
                        inputBg={inputBg}
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                      />
                    ) : testMode === 'chat' ? (
                      <VoiceTestPanel 
                        agentId={agentId}
                        config={config}
                        api={api}
                        isDarkMode={isDarkMode}
                        cardBg={cardBg}
                        cardBorder={cardBorder}
                        inputBg={inputBg}
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                        chatOnly={true}
                      />
                    ) : testMode === 'callme' ? (
                      <CallMePanel
                        agentId={agentId}
                        config={config}
                        api={api}
                        isDarkMode={isDarkMode}
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                      />
                    ) : (
                      null
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Add CSS for orb animations */}
      <style>{`
        @keyframes thinkingDot {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.2); opacity: 0.4; }
          100% { transform: scale(1); opacity: 0.8; }
        }
        .animate-ping-slow {
          animation: ping-slow 2s ease-in-out infinite;
        }
        @keyframes float-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          50% { transform: translate(10px, -15px) scale(1.2); opacity: 1; }
        }
        @keyframes float-2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(-12px, 10px) scale(0.8); opacity: 0.9; }
        }
        @keyframes float-3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          50% { transform: translate(8px, 8px) scale(1.3); opacity: 0.8; }
        }
        .animate-float-1 { animation: float-1 3s ease-in-out infinite; }
        .animate-float-2 { animation: float-2 2.5s ease-in-out infinite 0.5s; }
        .animate-float-3 { animation: float-3 2s ease-in-out infinite 1s; }
      `}</style>

      {/* Import Voices Modal */}
      <VoiceLibraryModal
        open={showVoiceLibraryModal}
        onClose={() => setShowVoiceLibraryModal(false)}
        api={api}
        subAccountId={subAccountId}
        currentVoiceId={getCurrentVoiceMeta().voice_id}
        voiceModel={config.voice_model}
        player={voicePlayer}
        onSelect={handleVoiceSelected}
        isDarkMode={isDarkMode}
      />

      {/* Transfer Call Configuration Modal */}
      <AnimatePresence>
        {showTransferCallModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowTransferCallModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`agent-config agent-config-dialog w-full max-w-5xl ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl h-[88vh] flex flex-col`}
              data-theme={isDarkMode ? 'dark' : 'light'}
              role="dialog" aria-modal="true" aria-label="Transfer destinations"
              onClick={e => e.stopPropagation()}
              data-testid="transfer-call-modal"
            >
              {/* Modal Header */}
              <div className={`px-6 py-5 border-b ${cardBorder} shrink-0`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                      <Phone className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className={`text-xl font-semibold ${textPrimary}`}>
                        Transfer Destinations
                      </h3>
                      <p className={`text-sm ${textSecondary}`}>
                        Route calls to people or AI agents — with an optional whisper briefing before connecting
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowTransferCallModal(false)}
                    data-testid="transfer-config-close" aria-label="Close transfer settings"
                    className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10`}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Modal Body — destinations list + editor form */}
              <div className="flex-1 flex min-h-0">
                {/* Left: configured destinations */}
                <div className={`w-72 shrink-0 border-r ${cardBorder} flex-col min-h-0 hidden md:flex ${isDarkMode ? 'bg-white/[0.015]' : 'bg-gray-50'}`}>
                  <div className="px-4 pt-4 pb-2 flex items-center justify-between">
                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`}>Destinations</span>
                    <span className={`text-[11px] ${textSecondary}`}>{transferDestinations.length}/{MAX_TRANSFER_DESTINATIONS}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-2">
                    {transferDestinations.length === 0 && (
                      <div className={`text-xs ${textSecondary} px-2 py-6 text-center leading-relaxed`}>
                        No destinations yet.<br />Configure your first one on the right.
                      </div>
                    )}
                    {transferDestinations.map((d, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          setEditingDestinationIndex(idx);
                          setCurrentDestination({
                            ...defaultDestination,
                            ...d,
                            schedule: {
                              ...defaultDestination.schedule,
                              ...(d.schedule || {}),
                              custom_hours: {
                                ...defaultDestination.schedule.custom_hours,
                                ...((d.schedule || {}).custom_hours || {}),
                              },
                            },
                          });
                        }}
                        className={`group/dest relative p-3 rounded-xl border cursor-pointer transition-all ${
                          editingDestinationIndex === idx
                            ? 'bg-blue-400/10 border-blue-400/40'
                            : `${isDarkMode ? 'bg-white/[0.03] border-white/[0.07] hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300'}`
                        }`}
                        data-testid={`transfer-dest-item-${idx}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className={`text-sm font-medium truncate ${editingDestinationIndex === idx ? 'text-blue-400' : textPrimary}`}>
                              {d.name || 'Unnamed'}
                            </p>
                            <p className={`text-xs ${textSecondary} truncate`}>{d.phone_number}</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const nd = transferDestinations.filter((_, i) => i !== idx);
                              setTransferDestinations(nd);
                              updateConfig('transfer_call_config', { destinations: nd });
                              if (editingDestinationIndex === idx) {
                                setEditingDestinationIndex(null);
                                setCurrentDestination({ ...defaultDestination });
                              } else if (editingDestinationIndex !== null && editingDestinationIndex > idx) {
                                setEditingDestinationIndex(editingDestinationIndex - 1);
                              }
                              toast.success('Destination removed');
                            }}
                            className={`p-1 rounded-md ${textSecondary} opacity-0 group-hover/dest:opacity-100 hover:text-red-400 hover:bg-red-400/10 transition-all shrink-0`}
                            title="Remove destination"
                            data-testid={`transfer-dest-delete-${idx}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                            d.whisper_enabled !== false
                              ? 'bg-violet-400/10 text-violet-300 border-violet-400/25'
                              : `${isDarkMode ? 'bg-white/5 text-gray-400 border-white/10' : 'bg-gray-100 text-gray-500 border-gray-200'}`
                          }`}>
                            <Volume2 size={9} />
                            {d.whisper_enabled !== false ? 'Whisper' : 'Instant'}
                          </span>
                          {d.schedule_enabled && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border bg-blue-400/10 text-blue-300 border-blue-400/25`}>
                              <Clock size={9} />
                              Hours
                            </span>
                          )}
                          {d.type === 'ai_agent' && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border bg-teal-400/10 text-teal-300 border-teal-400/25">
                              <Bot size={9} />
                              AI
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 pt-1">
                    <button
                      onClick={() => {
                        setEditingDestinationIndex(null);
                        setCurrentDestination({ ...defaultDestination });
                      }}
                      disabled={transferDestinations.length >= MAX_TRANSFER_DESTINATIONS}
                      className={`w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium border border-dashed transition-colors disabled:opacity-40 ${
                        isDarkMode ? 'border-white/15 text-gray-300 hover:border-blue-400/40 hover:text-blue-400' : 'border-gray-300 text-gray-600 hover:border-blue-500/50 hover:text-blue-600'
                      }`}
                      data-testid="transfer-new-destination-btn"
                    >
                      <Plus size={15} />
                      New Destination
                    </button>
                  </div>
                </div>

                {/* Right: destination editor form */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h4 className={`font-medium ${textPrimary} flex items-center gap-2`}>
                    <User size={16} />
                    {editingDestinationIndex !== null ? 'Edit Destination' : 'New Destination'}
                  </h4>

                  {/* Quick pick from AI Agents */}
                  {agentsWithPhones.length > 0 && (
                    <div>
                      <label className={`block text-sm ${textSecondary} mb-2`}>Quick Pick — AI Agent</label>
                      <div className="grid grid-cols-1 gap-2">
                        {agentsWithPhones.map(a => (
                          <button
                            key={a.agent_id}
                            onClick={() => setCurrentDestination(prev => ({
                              ...prev,
                              phone_number: a.phone_number,
                              name: a.name,
                              description: prev.description || `Transfer to AI agent "${a.name}"`,
                              type: 'ai_agent',
                              agent_id: a.agent_id,
                            }))}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors text-left ${
                              currentDestination.agent_id === a.agent_id
                                ? 'bg-blue-400/10 border-blue-400/40 text-blue-400'
                                : `${inputBg} ${cardBorder} ${textPrimary} hover:border-blue-400/30`
                            }`}
                            data-testid={`pick-agent-${a.agent_id}`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400/20 to-purple-500/20 flex items-center justify-center shrink-0">
                              <Bot size={16} className="text-blue-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{a.name}</p>
                              <p className={`text-xs ${textSecondary}`}>{a.phone_number}</p>
                            </div>
                            {currentDestination.agent_id === a.agent_id && <Check size={16} className="text-blue-400 shrink-0" />}
                          </button>
                        ))}
                      </div>
                      <div className={`flex items-center gap-3 my-4`}>
                        <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
                        <span className={`text-xs ${textSecondary}`}>or enter manually</span>
                        <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm ${textSecondary} mb-2`}>Phone Number *</label>
                      <input
                        type="tel"
                        value={currentDestination.phone_number}
                        onChange={(e) => setCurrentDestination(prev => ({ ...prev, phone_number: e.target.value, agent_id: null, type: 'manual' }))}
                        placeholder="+1 (555) 123-4567"
                        className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-blue-400/50`}
                        data-testid="transfer-phone-input"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm ${textSecondary} mb-2`}>Name / Label *</label>
                      <input
                        type="text"
                        value={currentDestination.name}
                        onChange={(e) => setCurrentDestination(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Sales Team, Support"
                        className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-blue-400/50`}
                        data-testid="transfer-name-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm ${textSecondary} mb-2`}>Description (for AI context)</label>
                    <textarea
                      value={currentDestination.description}
                      onChange={(e) => setCurrentDestination(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="When should the AI transfer to this person? e.g., 'Transfer when customer requests to speak to a human, when the issue is complex, or when customer is upset.'"
                      rows={3}
                      className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-blue-400/50 resize-none`}
                      data-testid="transfer-description-input"
                    />
                    <p className={`text-xs ${textSecondary} mt-1`}>
                      This helps the AI understand when to transfer to this specific person/team.
                    </p>
                  </div>
                </div>

                {/* Whisper Briefing */}
                <div className={`p-4 rounded-xl border ${
                  currentDestination.whisper_enabled !== false
                    ? 'bg-violet-400/[0.06] border-violet-400/25'
                    : `${inputBg} ${cardBorder}`
                }`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        currentDestination.whisper_enabled !== false ? 'bg-violet-400/15' : (isDarkMode ? 'bg-white/5' : 'bg-gray-100')
                      }`}>
                        <Volume2 size={17} className={currentDestination.whisper_enabled !== false ? 'text-violet-400' : textSecondary} />
                      </div>
                      <div className="min-w-0">
                        <h4 className={`font-medium ${textPrimary} text-sm`}>Whisper Briefing</h4>
                        <p className={`text-xs ${textSecondary} mt-0.5 leading-relaxed`}>
                          {currentDestination.whisper_enabled !== false
                            ? 'Before connecting, the receiver (human or AI) privately hears a quick AI briefing — the caller\u2019s name and what they need — while the caller waits on hold.'
                            : 'Instant transfer: the call connects immediately with no briefing. Caller ID is preserved.'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setCurrentDestination(prev => ({ ...prev, whisper_enabled: prev.whisper_enabled === false }))}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors shrink-0 ${
                        currentDestination.whisper_enabled !== false
                          ? 'bg-violet-400/10 text-violet-400'
                          : `${inputBg} ${textSecondary}`
                      }`}
                      data-testid="whisper-toggle"
                    >
                      {currentDestination.whisper_enabled !== false ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      {currentDestination.whisper_enabled !== false ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                  {currentDestination.whisper_enabled !== false && (
                    <div className={`mt-3 px-3 py-2.5 rounded-lg text-xs leading-relaxed ${isDarkMode ? 'bg-black/30 text-gray-400' : 'bg-white text-gray-500 border border-gray-100'}`}>
                      <span className={`font-medium ${isDarkMode ? 'text-violet-300' : 'text-violet-600'}`}>Example briefing:</span>{' '}
                      "Incoming transfer: John Smith. He wants pricing on the premium plan for three locations and prefers a callback today."
                    </div>
                  )}
                </div>

                {/* Business Hours Schedule */}
                <div className={`p-4 rounded-xl border ${
                  currentDestination.schedule_enabled
                    ? 'bg-blue-400/[0.06] border-blue-400/25'
                    : `${inputBg} ${cardBorder}`
                }`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        currentDestination.schedule_enabled ? 'bg-blue-400/15' : (isDarkMode ? 'bg-white/5' : 'bg-gray-100')
                      }`}>
                        <Clock size={17} className={currentDestination.schedule_enabled ? 'text-blue-400' : textSecondary} />
                      </div>
                      <div className="min-w-0">
                        <h4 className={`font-medium ${textPrimary} text-sm`}>Business Hours Schedule</h4>
                        <p className={`text-xs ${textSecondary} mt-0.5 leading-relaxed`}>
                          {currentDestination.schedule_enabled
                            ? 'Transfers to this destination only connect during the hours below. Outside these hours the AI won\u2019t transfer — it tells the caller your fallback message instead.'
                            : 'No schedule: the AI can transfer to this destination at any time, day or night.'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setCurrentDestination(prev => ({ ...prev, schedule_enabled: !prev.schedule_enabled }))}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors shrink-0 ${
                        currentDestination.schedule_enabled
                          ? 'bg-blue-400/10 text-blue-400'
                          : `${inputBg} ${textSecondary}`
                      }`}
                      data-testid="schedule-toggle"
                    >
                      {currentDestination.schedule_enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      {currentDestination.schedule_enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                  
                  {currentDestination.schedule_enabled && (
                    <div className={`mt-4 p-4 rounded-xl ${isDarkMode ? 'bg-black/30' : 'bg-white border border-gray-100'} space-y-4`}>
                      {/* Same hours toggle */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setCurrentDestination(prev => ({
                            ...prev,
                            schedule: { ...prev.schedule, same_hours_all_days: true }
                          }))}
                          data-testid="transfer-schedule-same-hours"
                          className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                            currentDestination.schedule.same_hours_all_days
                              ? 'bg-blue-400/20 text-blue-400 border border-blue-400/30'
                              : `${inputBg} ${textSecondary} border ${cardBorder}`
                          }`}
                        >
                          Same hours all days
                        </button>
                        <button
                          onClick={() => setCurrentDestination(prev => ({
                            ...prev,
                            schedule: { ...prev.schedule, same_hours_all_days: false }
                          }))}
                          data-testid="transfer-schedule-custom-hours"
                          className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                            !currentDestination.schedule.same_hours_all_days
                              ? 'bg-blue-400/20 text-blue-400 border border-blue-400/30'
                              : `${inputBg} ${textSecondary} border ${cardBorder}`
                          }`}
                        >
                          Custom per day
                        </button>
                      </div>

                      {currentDestination.schedule.same_hours_all_days ? (
                        <>
                          {/* Unified time selection */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className={`block text-xs ${textSecondary} mb-1.5`}>Start Time</label>
                              <input
                                type="time"
                                value={currentDestination.schedule.start_time}
                                data-testid="transfer-schedule-start-time"
                                onChange={(e) => setCurrentDestination(prev => ({
                                  ...prev,
                                  schedule: { ...prev.schedule, start_time: e.target.value }
                                }))}
                                className={`w-full px-3 py-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} border ${cardBorder} ${textPrimary} focus:outline-none`}
                              />
                            </div>
                            <div>
                              <label className={`block text-xs ${textSecondary} mb-1.5`}>End Time</label>
                              <input
                                type="time"
                                value={currentDestination.schedule.end_time}
                                data-testid="transfer-schedule-end-time"
                                onChange={(e) => setCurrentDestination(prev => ({
                                  ...prev,
                                  schedule: { ...prev.schedule, end_time: e.target.value }
                                }))}
                                className={`w-full px-3 py-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} border ${cardBorder} ${textPrimary} focus:outline-none`}
                              />
                            </div>
                          </div>
                          
                          {/* Day selection */}
                          <div>
                            <label className={`block text-xs ${textSecondary} mb-2`}>Active Days</label>
                            <div className="flex flex-wrap gap-2">
                              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                <button
                                  key={day}
                                  data-testid={`transfer-schedule-day-${day}`}
                                  onClick={() => {
                                    const currentDays = currentDestination.schedule.days || [];
                                    const newDays = currentDays.includes(day)
                                      ? currentDays.filter(d => d !== day)
                                      : [...currentDays, day];
                                    setCurrentDestination(prev => ({
                                      ...prev,
                                      schedule: { ...prev.schedule, days: newDays }
                                    }));
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    currentDestination.schedule.days?.includes(day)
                                      ? 'bg-blue-400/20 text-blue-400 border border-blue-400/30'
                                      : `${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} ${textSecondary} border ${cardBorder}`
                                  }`}
                                >
                                  {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        /* Custom hours per day */
                        <div className="space-y-2">
                          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                            <div key={day} className="flex items-center gap-3">
                              <button
                                data-testid={`transfer-custom-day-${day}`}
                                onClick={() => {
                                  setCurrentDestination(prev => ({
                                    ...prev,
                                    schedule: {
                                      ...prev.schedule,
                                      custom_hours: {
                                        ...prev.schedule.custom_hours,
                                        [day]: {
                                          ...prev.schedule.custom_hours[day],
                                          enabled: !prev.schedule.custom_hours[day]?.enabled
                                        }
                                      }
                                    }
                                  }));
                                }}
                                className={`w-20 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                                  currentDestination.schedule.custom_hours[day]?.enabled
                                    ? 'bg-blue-400/20 text-blue-400'
                                    : `${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} ${textSecondary}`
                                }`}
                              >
                                {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                              </button>
                              {currentDestination.schedule.custom_hours[day]?.enabled ? (
                                <div className="flex items-center gap-2 flex-1">
                                  <input
                                    type="time"
                                    value={currentDestination.schedule.custom_hours[day]?.start || '09:00'}
                                    data-testid={`transfer-custom-start-${day}`}
                                    onChange={(e) => setCurrentDestination(prev => ({
                                      ...prev,
                                      schedule: {
                                        ...prev.schedule,
                                        custom_hours: {
                                          ...prev.schedule.custom_hours,
                                          [day]: { ...prev.schedule.custom_hours[day], start: e.target.value }
                                        }
                                      }
                                    }))}
                                    className={`flex-1 px-2 py-1.5 rounded text-xs ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} border ${cardBorder} ${textPrimary}`}
                                  />
                                  <span className={textSecondary}>to</span>
                                  <input
                                    type="time"
                                    value={currentDestination.schedule.custom_hours[day]?.end || '17:00'}
                                    data-testid={`transfer-custom-end-${day}`}
                                    onChange={(e) => setCurrentDestination(prev => ({
                                      ...prev,
                                      schedule: {
                                        ...prev.schedule,
                                        custom_hours: {
                                          ...prev.schedule.custom_hours,
                                          [day]: { ...prev.schedule.custom_hours[day], end: e.target.value }
                                        }
                                      }
                                    }))}
                                    className={`flex-1 px-2 py-1.5 rounded text-xs ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} border ${cardBorder} ${textPrimary}`}
                                  />
                                </div>
                              ) : (
                                <span className={`text-xs ${textSecondary}`}>Closed</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Outside hours message */}
                      <div>
                        <label className={`block text-xs ${textSecondary} mb-1.5`}>Outside Business Hours Message</label>
                        <textarea
                          value={currentDestination.outside_hours_message}
                          data-testid="transfer-outside-hours-message"
                          onChange={(e) => setCurrentDestination(prev => ({ ...prev, outside_hours_message: e.target.value }))}
                          placeholder="What should the AI say if someone requests a transfer outside of business hours?"
                          rows={2}
                          className={`w-full px-3 py-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} border ${cardBorder} ${textPrimary} focus:outline-none resize-none text-sm`}
                        />
                      </div>
                    </div>
                  )}
                </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className={`px-6 py-4 border-t ${cardBorder} flex items-center gap-3 shrink-0`}>
                <span className={`text-xs ${textSecondary} mr-auto hidden sm:block`}>
                  {editingDestinationIndex !== null ? `Editing "${currentDestination.name || 'destination'}"` : 'Adding new destination'}
                </span>
                <button
                  onClick={() => setShowTransferCallModal(false)}
                  className={`px-6 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition-colors`}
                  data-testid="transfer-config-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    let newDestinations;
                    if (editingDestinationIndex !== null) {
                      // Editing existing destination
                      newDestinations = [...transferDestinations];
                      newDestinations[editingDestinationIndex] = currentDestination;
                    } else {
                      // Adding new destination
                      if (transferDestinations.length >= MAX_TRANSFER_DESTINATIONS) {
                        toast.error(`Maximum ${MAX_TRANSFER_DESTINATIONS} destinations allowed`);
                        return;
                      }
                      newDestinations = [...transferDestinations, currentDestination];
                    }
                    
                    setTransferDestinations(newDestinations);
                    updateConfig('transfer_call_config', { destinations: newDestinations });
                    
                    // Also enable the function if not already
                    if (!config.functions.transfer_call) {
                      toggleFunction('transfer_call');
                    }
                    
                    // Keep the modal open so the list updates live — reset the form for the next entry
                    setEditingDestinationIndex(null);
                    setCurrentDestination({ ...defaultDestination });
                    toast.success(editingDestinationIndex !== null ? 'Destination updated' : 'Destination added');
                  }}
                  disabled={!currentDestination.phone_number || !currentDestination.name}
                  className="px-8 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-blue-400 to-blue-500 text-white font-medium hover:shadow-lg hover:shadow-blue-400/25 transition-all disabled:opacity-50"
                  data-testid="save-transfer-config-btn"
                >
                  <Check size={18} />
                  {editingDestinationIndex !== null ? 'Update Destination' : 'Add Destination'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Function Config Modals (add_tag, remove_tag, trigger_workflow, send_sms) */}
      <AnimatePresence>
        {showFuncConfigModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowFuncConfigModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`agent-config agent-config-dialog w-full ${['add_tag', 'remove_tag', 'update_custom_field', 'send_sms'].includes(showFuncConfigModal) ? 'max-w-3xl' : 'max-w-2xl'} ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl max-h-[90vh] flex flex-col`}
              data-theme={isDarkMode ? 'dark' : 'light'}
              role="dialog" aria-modal="true" aria-labelledby="function-config-dialog-title"
              onClick={e => e.stopPropagation()}
              data-testid={`${showFuncConfigModal}-config-modal`}
            >
              {/* Header */}
              <div className={`p-6 border-b ${cardBorder} shrink-0`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      showFuncConfigModal === 'add_tag' ? 'bg-gradient-to-br from-teal-400 to-emerald-600'
                        : showFuncConfigModal === 'remove_tag' ? 'bg-gradient-to-br from-red-400 to-rose-600'
                        : showFuncConfigModal === 'trigger_workflow' ? 'bg-gradient-to-br from-blue-400 to-indigo-600'
                        : showFuncConfigModal === 'update_custom_field' ? 'bg-gradient-to-br from-sky-400 to-blue-600'
                        : showFuncConfigModal === 'schedule_callback' ? 'bg-gradient-to-br from-teal-400 to-cyan-600'
                        : showFuncConfigModal === 'schedule_textback' ? 'bg-gradient-to-br from-sky-400 to-indigo-600'
                        : 'bg-gradient-to-br from-violet-400 to-purple-600'
                    }`}>
                      {showFuncConfigModal === 'add_tag' && <Tag className="text-white" size={20} />}
                      {showFuncConfigModal === 'remove_tag' && <Tag className="text-white" size={20} />}
                      {showFuncConfigModal === 'trigger_workflow' && <Zap className="text-white" size={20} />}
                      {showFuncConfigModal === 'send_sms' && <MessageSquare className="text-white" size={20} />}
                      {showFuncConfigModal === 'update_custom_field' && <Database className="text-white" size={20} />}
                      {showFuncConfigModal === 'schedule_callback' && <PhoneOutgoing className="text-white" size={20} />}
                      {showFuncConfigModal === 'schedule_textback' && <MessageSquare className="text-white" size={20} />}
                      {showFuncConfigModal === 'trigger_outbound_call' && <PhoneOutgoing className="text-white" size={20} />}
                    </div>
                    <div>
                      <h3 id="function-config-dialog-title" className={`text-lg font-semibold ${textPrimary}`} data-testid="function-config-dialog-title">
                        {showFuncConfigModal === 'add_tag' && 'Add Tag Settings'}
                        {showFuncConfigModal === 'remove_tag' && 'Remove Tag Settings'}
                        {showFuncConfigModal === 'trigger_workflow' && 'Trigger Workflow Settings'}
                        {showFuncConfigModal === 'send_sms' && 'Send SMS Settings'}
                        {showFuncConfigModal === 'update_custom_field' && 'Custom Field Mapping'}
                        {showFuncConfigModal === 'schedule_callback' && 'Callback Settings'}
                        {showFuncConfigModal === 'schedule_textback' && 'Textback Settings'}
                        {showFuncConfigModal === 'trigger_outbound_call' && 'Outbound Call Settings'}
                      </h3>
                      <p className={`text-sm ${textSecondary}`}>
                        {showFuncConfigModal === 'add_tag' && 'Create tags the AI can add — each with a rule for when to use it'}
                        {showFuncConfigModal === 'remove_tag' && 'Create tags the AI can remove — each with a rule for when to remove it'}
                        {showFuncConfigModal === 'trigger_workflow' && 'Choose which workflow the AI should trigger'}
                        {showFuncConfigModal === 'send_sms' && 'Saved messages for in-call requests'}
                        {showFuncConfigModal === 'update_custom_field' && 'Pick LeadConnector fields the AI fills with answers from the conversation'}
                        {showFuncConfigModal === 'schedule_callback' && 'Scheduling limits and missed-callback automation'}
                        {showFuncConfigModal === 'schedule_textback' && 'Automation after a scheduled textback is sent'}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setShowFuncConfigModal(null)} className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10`}
                    data-testid="function-config-close" aria-label="Close function settings">
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {showFuncConfigModal === 'trigger_outbound_call' && (
                  <OutboundCallSettings config={config} agents={availableVoiceAgents}
                    onChange={updateConfig} isDarkMode={isDarkMode} />
                )}
                {/* add_tag - card-based tag manager */}
                {showFuncConfigModal === 'add_tag' && (
                  <TagCardsEditor
                    mode="add"
                    tags={(config.function_configs?.add_tag?.tags || []).map(t => typeof t === 'string' ? { tag_name: t, description: '' } : t)}
                    onChange={(tags) => updateFuncConfig('add_tag', 'tags', tags)}
                    ghlTags={ghlTags}
                    createRequest={tagCreateRequest}
                    hideInlineCreate
                    isDarkMode={isDarkMode}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                    inputBg={inputBg}
                    cardBorder={cardBorder}
                  />
                )}

                {/* remove_tag - card-based tag manager */}
                {showFuncConfigModal === 'remove_tag' && (() => {
                  const rt = config.function_configs?.remove_tag || {};
                  const rtTags = ((rt.tags && rt.tags.length ? rt.tags : (rt.tag_name ? [rt.tag_name] : [])))
                    .map(t => typeof t === 'string' ? { tag_name: t, description: rt.description || '' } : t);
                  return (
                    <TagCardsEditor
                      mode="remove"
                      tags={rtTags}
                      onChange={(tags) => updateFuncConfig('remove_tag', 'tags', tags)}
                      ghlTags={ghlTags}
                      createRequest={tagCreateRequest}
                      hideInlineCreate
                      isDarkMode={isDarkMode}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      inputBg={inputBg}
                      cardBorder={cardBorder}
                    />
                  );
                })()}

                {/* update_custom_field - card-based field mapper */}
                {showFuncConfigModal === 'update_custom_field' && (
                  <CustomFieldsEditor
                    fields={(config.function_configs?.update_custom_field?.fields || []).filter(f => f && typeof f === 'object')}
                    onChange={(flds) => updateFuncConfig('update_custom_field', 'fields', flds)}
                    ghlCustomFields={ghlCustomFields}
                    loadingFields={loadingGhlCustomFields}
                    onSync={syncGhlCustomFields}
                    syncing={syncingGhlCustomFields}
                    createRequest={fieldCreateRequest}
                    isDarkMode={isDarkMode}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                    inputBg={inputBg}
                    cardBorder={cardBorder}
                  />
                )}

                {/* trigger_workflow */}
                {showFuncConfigModal === 'trigger_workflow' && (
                  <>
                    <div>
                      <label className={`block text-sm font-medium ${textPrimary} mb-2`}>Select Workflow *</label>
                      {loadingGhlWorkflows ? (
                        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${inputBg} border ${cardBorder}`}>
                          <Loader2 size={16} className="animate-spin text-blue-400" />
                          <span className={`text-sm ${textSecondary}`}>Loading workflows from LeadConnector...</span>
                        </div>
                      ) : ghlWorkflows.length > 0 ? (
                        <select
                          value={config.function_configs?.trigger_workflow?.workflow_id || ''}
                          onChange={(e) => {
                            const wf = ghlWorkflows.find(w => w.id === e.target.value);
                            updateFuncConfig('trigger_workflow', 'workflow_id', e.target.value);
                            if (wf) updateFuncConfig('trigger_workflow', 'workflow_name', wf.name);
                          }}
                          className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-teal-400/50`}
                          data-testid="trigger-workflow-select"
                        >
                          <option value="">Choose a workflow...</option>
                          {ghlWorkflows.map(w => (
                            <option key={w.id} value={w.id}>{w.name}{w.status === 'draft' ? ' (Draft)' : ''}</option>
                          ))}
                        </select>
                      ) : (
                        <div className={`px-4 py-3 rounded-xl ${inputBg} border ${cardBorder}`}>
                          <p className={`text-sm ${textSecondary}`}>No workflows found. Make sure LeadConnector is connected and has workflows.</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${textPrimary} mb-2`}>When should the AI trigger this workflow?</label>
                      <textarea
                        value={config.function_configs?.trigger_workflow?.description || ''}
                        onChange={(e) => updateFuncConfig('trigger_workflow', 'description', e.target.value)}
                        placeholder="e.g., Trigger this workflow when the contact books an appointment or confirms they want to proceed"
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-teal-400/50 resize-none`}
                        data-testid="trigger-workflow-description-input"
                      />
                    </div>
                  </>
                )}

                {/* send_sms */}
                {showFuncConfigModal === 'send_sms' && (
                  <SendSmsSettings config={config.function_configs?.send_sms || {}}
                    onEditingChange={setEditingSmsOption}
                    onChange={sms => { changeSeqRef.current += 1; setHasChanges(true); setConfig(prev => ({ ...prev, function_configs: { ...prev.function_configs, send_sms: sms } })); }}
                    {...{ inputBg, cardBorder, textPrimary, textSecondary }} />
                )}

                {/* schedule_callback — settings moved from the old Intelligence tab */}
                {showFuncConfigModal === 'schedule_callback' && (
                  <>
                    <div>
                      <label className={`text-sm font-medium ${textPrimary} mb-1 block`}>Max Callback Days</label>
                      <p className={`text-xs ${textSecondary} mb-2`}>Maximum number of days out a callback can be scheduled</p>
                      <input
                        type="number"
                        min={1}
                        max={14}
                        value={config.callback_max_days || 3}
                        onChange={e => updateConfig('callback_max_days', parseInt(e.target.value) || 3)}
                        className={`w-24 px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
                        data-testid="callback-max-days-input"
                      />
                    </div>
                    <div>
                      <label className={`text-sm font-medium ${textPrimary} mb-1 block`}>Beyond-Limit Response</label>
                      <p className={`text-xs ${textSecondary} mb-2`}>What the AI should say when someone requests a callback beyond the max days</p>
                      <textarea
                        value={config.callback_rejection_prompt || ''}
                        onChange={e => updateConfig('callback_rejection_prompt', e.target.value)}
                        placeholder="e.g. We can't schedule a callback that far out. Usually when people are interested in our services, they move quickly and we schedule them in with one of our specialists right away."
                        rows={3}
                        className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder:text-gray-500`}
                        data-testid="callback-rejection-prompt-input"
                      />
                    </div>
                    <div className={`p-4 rounded-xl border ${cardBorder} ${inputBg}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Workflow size={14} className="text-teal-400" />
                        <label className={`text-sm font-medium ${textPrimary}`}>LeadConnector Workflow on Missed Callback</label>
                      </div>
                      <p className={`text-xs ${textSecondary} mb-3`}>
                        When an AI-scheduled callback is marked Missed, automatically add the contact to this LeadConnector workflow so your follow-up sequence runs without extra steps.
                      </p>
                      {!ghlConnected ? (
                        <div className={`text-xs ${textSecondary} flex items-center gap-2`}>
                          <AlertCircle size={12} className="text-amber-400" /> Connect LeadConnector to enable workflow triggers.
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            value={config.callback_missed_workflow_id || ''}
                            onFocus={() => { if (ghlWorkflows.length === 0) fetchGhlWorkflows(); }}
                            onChange={e => updateConfig('callback_missed_workflow_id', e.target.value)}
                            className={`flex-1 px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
                            data-testid="callback-missed-workflow-select"
                          >
                            <option value="">— None (don't auto-trigger) —</option>
                            {ghlWorkflows.map(w => (
                              <option key={w.id} value={w.id}>{w.name || '(unnamed)'}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={fetchGhlWorkflows}
                            disabled={loadingGhlWorkflows}
                            className={`px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textSecondary} text-xs hover:${textPrimary} disabled:opacity-50`}
                            data-testid="callback-missed-workflow-refresh"
                            title="Refresh workflow list"
                          >
                            {loadingGhlWorkflows ? <Loader2 size={12} className="animate-spin" /> : 'Refresh'}
                          </button>
                        </div>
                      )}
                    </div>
                    <p className={`text-xs ${textSecondary}`}>
                      When a callback is scheduled, the contact is automatically tagged <span className="font-mono text-teal-400">callback</span> in LeadConnector.
                    </p>
                  </>
                )}

                {/* schedule_textback — settings moved from the old Intelligence tab */}
                {showFuncConfigModal === 'schedule_textback' && (
                  <>
                    <div className={`p-4 rounded-xl border ${cardBorder} ${inputBg}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Workflow size={14} className="text-sky-400" />
                        <label className={`text-sm font-medium ${textPrimary}`}>LeadConnector Workflow after Textback Sent</label>
                      </div>
                      <p className={`text-xs ${textSecondary} mb-3`}>
                        After the scheduled textback is delivered, automatically add the contact to this LeadConnector workflow so your follow-up sequence continues without extra steps.
                      </p>
                      {!ghlConnected ? (
                        <div className={`text-xs ${textSecondary} flex items-center gap-2`}>
                          <AlertCircle size={12} className="text-amber-400" /> Connect LeadConnector to enable workflow triggers.
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            value={config.textback_workflow_id || ''}
                            onFocus={() => { if (ghlWorkflows.length === 0) fetchGhlWorkflows(); }}
                            onChange={e => updateConfig('textback_workflow_id', e.target.value)}
                            className={`flex-1 px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm`}
                            data-testid="textback-workflow-select"
                          >
                            <option value="">— None (don't auto-trigger) —</option>
                            {ghlWorkflows.map(w => (
                              <option key={w.id} value={w.id}>{w.name || '(unnamed)'}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={fetchGhlWorkflows}
                            disabled={loadingGhlWorkflows}
                            className={`px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textSecondary} text-xs hover:${textPrimary} disabled:opacity-50`}
                            data-testid="textback-workflow-refresh"
                            title="Refresh workflow list"
                          >
                            {loadingGhlWorkflows ? <Loader2 size={12} className="animate-spin" /> : 'Refresh'}
                          </button>
                        </div>
                      )}
                    </div>
                    <p className={`text-xs ${textSecondary}`}>
                      When a textback is scheduled, the contact is automatically tagged <span className="font-mono text-sky-400">textback</span> in LeadConnector. When both callback and textback are on, the AI asks whether they'd rather get a call or a text.
                    </p>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className={`p-6 border-t ${cardBorder} shrink-0 ${showFuncConfigModal === 'send_sms' && editingSmsOption ? 'hidden' : ''}`}>
                {['add_tag', 'remove_tag', 'update_custom_field'].includes(showFuncConfigModal) ? (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => (showFuncConfigModal === 'update_custom_field' ? setFieldCreateRequest(n => n + 1) : setTagCreateRequest(n => n + 1))}
                      className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-dashed ${cardBorder} ${textSecondary} font-medium transition-colors ${
                        showFuncConfigModal === 'add_tag' ? 'hover:border-teal-400/50 hover:text-teal-400'
                          : showFuncConfigModal === 'remove_tag' ? 'hover:border-rose-400/50 hover:text-rose-400'
                          : 'hover:border-sky-400/50 hover:text-sky-400'
                      }`}
                      data-testid={showFuncConfigModal === 'update_custom_field' ? 'modal-add-field-btn' : 'modal-create-tag-btn'}
                    >
                      <Plus size={18} />
                      {showFuncConfigModal === 'update_custom_field' ? 'Add Field' : 'Create Tag'}
                    </button>
                    <button
                      onClick={async () => { await handleSave(); setShowFuncConfigModal(null); }}
                      disabled={saving}
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium hover:from-teal-600 hover:to-emerald-700 transition-all disabled:opacity-60"
                      data-testid="save-func-config-btn"
                    >
                      {saving ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                      {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={async () => { if (await handleSave()) setShowFuncConfigModal(null); }}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium hover:from-teal-600 hover:to-emerald-700 transition-all disabled:opacity-60"
                    data-testid="save-func-config-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Opportunity Pipeline Config Modal */}
      <AnimatePresence>
        {showOpportunityModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowOpportunityModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`agent-config agent-config-dialog w-full max-w-2xl ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl max-h-[90vh] flex flex-col`}
              data-theme={isDarkMode ? 'dark' : 'light'}
              role="dialog" aria-modal="true" aria-label="Opportunity mapping"
              onClick={e => e.stopPropagation()}
              data-testid="opportunity-config-modal"
            >
              {/* Header */}
              <div className={`p-6 border-b ${cardBorder} shrink-0`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center">
                      <Target className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className={`text-xl font-semibold ${textPrimary}`}>
                        {editingOpportunityIndex !== null ? 'Edit Opportunity Mapping' : 'Add Opportunity Mapping'}
                      </h3>
                      <p className={`text-sm ${textSecondary}`}>
                        {opportunityMappings.length}/{MAX_OPPORTUNITY_MAPPINGS} mappings configured
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setShowOpportunityModal(false)} className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10`}
                    data-testid="opportunity-config-close" aria-label="Close opportunity settings">
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Pipeline Selection */}
                <div>
                  <label className={`block text-sm font-medium ${textPrimary} mb-2`}>Pipeline *</label>
                  {loadingPipelines ? (
                    <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${inputBg} border ${cardBorder}`}>
                      <Loader2 size={16} className="animate-spin text-blue-400" />
                      <span className={`text-sm ${textSecondary}`}>Loading pipelines from LeadConnector...</span>
                    </div>
                  ) : ghlPipelines.length === 0 ? (
                    <div className={`px-4 py-3 rounded-xl ${inputBg} border ${cardBorder}`}>
                      <p className={`text-sm ${textSecondary}`}>No pipelines found. Make sure LeadConnector is connected and has pipelines configured.</p>
                    </div>
                  ) : (
                    <select
                      value={currentOpportunityMapping.pipeline_id}
                      onChange={(e) => {
                        const pipeline = ghlPipelines.find(p => p.id === e.target.value);
                        setCurrentOpportunityMapping(prev => ({
                          ...prev,
                          pipeline_id: e.target.value,
                          pipeline_name: pipeline?.name || '',
                          stage_id: '',
                          stage_name: '',
                        }));
                      }}
                      className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-emerald-400/50`}
                      data-testid="opportunity-pipeline-select"
                    >
                      <option value="">Select a pipeline...</option>
                      {ghlPipelines.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Stage Selection */}
                {currentOpportunityMapping.pipeline_id && (
                  <div>
                    <label className={`block text-sm font-medium ${textPrimary} mb-2`}>Stage *</label>
                    <div className="grid grid-cols-2 gap-2">
                      {(ghlPipelines.find(p => p.id === currentOpportunityMapping.pipeline_id)?.stages || []).map(stage => (
                        <button
                          key={stage.id}
                          onClick={() => setCurrentOpportunityMapping(prev => ({ ...prev, stage_id: stage.id, stage_name: stage.name }))}
                          className={`px-4 py-3 rounded-xl border text-sm text-left transition-colors ${
                            currentOpportunityMapping.stage_id === stage.id
                              ? 'bg-emerald-400/10 border-emerald-400/40 text-emerald-400'
                              : `${inputBg} ${cardBorder} ${textPrimary} hover:border-emerald-400/30`
                          }`}
                          data-testid={`opportunity-stage-${stage.id}`}
                        >
                          {currentOpportunityMapping.stage_id === stage.id && <Check size={14} className="inline mr-2" />}
                          {stage.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description for AI */}
                <div>
                  <label className={`block text-sm font-medium ${textPrimary} mb-2`}>AI Trigger Description *</label>
                  <textarea
                    value={currentOpportunityMapping.description}
                    onChange={(e) => setCurrentOpportunityMapping(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe when the AI should add the contact to this opportunity. e.g., 'When a customer expresses interest in the Premium package and wants a follow-up demo call.'"
                    rows={3}
                    className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-emerald-400/50 resize-none`}
                    data-testid="opportunity-description-input"
                  />
                  <p className={`text-xs ${textSecondary} mt-1`}>
                    This tells the AI when a contact should be placed in this pipeline stage.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className={`p-6 border-t ${cardBorder} flex gap-3 shrink-0`}>
                <button
                  onClick={() => setShowOpportunityModal(false)}
                  className={`flex-1 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition-colors`}
                  data-testid="opportunity-config-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    let newMappings;
                    if (editingOpportunityIndex !== null) {
                      newMappings = [...opportunityMappings];
                      newMappings[editingOpportunityIndex] = currentOpportunityMapping;
                    } else {
                      if (opportunityMappings.length >= MAX_OPPORTUNITY_MAPPINGS) {
                        toast.error(`Maximum ${MAX_OPPORTUNITY_MAPPINGS} opportunity mappings allowed`);
                        return;
                      }
                      newMappings = [...opportunityMappings, currentOpportunityMapping];
                    }
                    setOpportunityMappings(newMappings);
                    updateConfig('opportunity_config', { mappings: newMappings });
                    if (!config.functions.update_opportunity_stage) {
                      toggleFunction('update_opportunity_stage');
                    }
                    setShowOpportunityModal(false);
                    toast.success(editingOpportunityIndex !== null ? 'Mapping updated' : 'Mapping added');
                  }}
                  disabled={!currentOpportunityMapping.pipeline_id || !currentOpportunityMapping.stage_id || !currentOpportunityMapping.description}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-emerald-400 to-teal-500 text-white font-medium hover:shadow-lg hover:shadow-emerald-400/25 transition-all disabled:opacity-50"
                  data-testid="save-opportunity-config-btn"
                >
                  <Check size={18} />
                  {editingOpportunityIndex !== null ? 'Update Mapping' : 'Add Mapping'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AgentEditorPage = () => {
  return (
    <SubAccountLayout>
      <AgentEditorContent />
    </SubAccountLayout>
  );
};

export default AgentEditorPage;
